import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { useToast } from '../shared/Toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useSocket } from '../../hooks/useSocket';
import { useProductIngredients } from '../../hooks/useProductIngredients';
import { usePermissions } from '../../hooks/usePermissions';
import { API_BASE_URL } from '../config/api';
import { productsAPI } from '../../services/api';
import {
  Plus, Users, Clock, Receipt, CreditCard, X, Check,
  Utensils, Gift, FileText,
  RefreshCw, AlertCircle, ShoppingCart, Loader2,
  ChevronUp, ChevronDown, CalendarDays,
  Home, Search, MessageSquare, HandCoins, Percent, Undo2,
  Star, AlertTriangle
} from 'lucide-react';
import ClosedSessionOrders from './ClosedSessionOrders';
import type { Product, ProductSize, Category, Ingredient, SimpleIngredient, POSTerminal, PosOptionGroup, SelectedOptionEntry } from '../../types/order';
import OpenPriceModal from '../shared/OpenPriceModal';
import WeightModal from '../shared/WeightModal';
import ProductCatalog from '../shared/ProductCatalog';
import { productDisplayPrice, lineUnitPrice, defaultSize as defaultSizeOf } from '../../utils/pricing';
import { withSellableSizes, showSizeName } from '../../utils/sellableSizes';
import { dineInQuickAdd } from '../../utils/dineInQuickAdd';
import { groupMoveTargets } from '../../utils/dineInMoveTargets';
import ProductSizeModal from '../shared/ProductSizeModal';
import ProductOptionsModal from '../shared/ProductOptionsModal';
import ProductCustomizeModal, { ProductCustomizeResult } from '../shared/ProductCustomizeModal';
import { dineInCustomizedLinePrices, dineInLineEditModalProps, DineInEditedLine } from '../../utils/basketLineEdit';
import NumpadModal from '../shared/NumpadModal';
import PromotionBanner, { EligiblePromotion } from '../shared/PromotionBanner';
import CancelOrderModal from '../CancelOrderModal';
import {
  aggregatePaymentItems, buildBillSummary, unitPriceWithAddOns,
  tablePaymentState, sliceAmount, sumUnits, roundToCents, MONEY_TOLERANCE, PayableUnit, PaymentMethod
} from '../../utils/dineInMoney';
import { dineInApi, DineInApiError } from '../../services/dineInApi';
import { SIZE_UNIT_PX, zoneCanvas } from '../../utils/floorPlan';
import { athensMinutes, allergiesOf, floorReservations, upcomingList, FloorReservation } from '../../utils/dineInReservations';
import FloorCanvas from './FloorCanvas';
import ZoneRoom from './ZoneRoom';
import ReservationsScreen from './reservations/ReservationsScreen';
import { hasRoom, type ZoneLayout } from '../../utils/floorRoom';
import KitchenMessageModal from './KitchenMessageModal';
import TablePaymentsList from './TablePaymentsList';
import TableScreen from './TableScreen';
import CardDoubtNotice from './CardDoubtNotice';
import { CardDoubt, cardDoubtsOf, hasCardInFlight, CARD_DOUBT_POLL_MS } from '../../utils/dineInCardDoubt';
import { useTheme } from '../../contexts/ThemeContext';
import { catalogLookOf, useCatalogPrefsV2 } from '../../utils/catalogPrefsV2';
import { formatEuro } from '../../utils/formatEuro';
import { AttentionLevel, FloorTone, floorTone, formatElapsed, formatTableAmount, summarizeFloor, tableAttention } from '../../utils/dineInFloor';
import { kitchenMessagePresets } from '../../utils/kitchenMessages';
import { useDailyDishesToday } from '../../hooks/useDailyDishesToday';
import { applyDailyDishes } from '../../utils/dailyDishes';
import {
  AdjustableBasketLine, BasketDiscountKind, applyAmountDiscount, applyPercentDiscount, applyTreat,
  basketLinePricing, effectivePercent, formatEuroGr, formatPercentGr, hasCashierAdjustment,
  lineFullCents, lineReductionCents, removeAdjustments, toCents
} from '../../utils/dineInBasketDiscount';
import BasketActionDialog from './BasketActionDialog';
import PressableBasketLine from './PressableBasketLine';
import StaffReasonPicker, { NO_STAFF_REASON, StaffReasonChoice, useStaffReasonText } from './StaffReasonPicker';

// Όψη τραπεζιού ανά τόνο στο σκούρο θέμα (το ανοιχτό το ορίζει το CashierLight.css).
// Γεμάτο χρώμα μόνο όπου χρειάζεται ενέργεια — το ελεύθερο τραπέζι μένει ήσυχο.
const TONE_TILE: Record<FloorTone, string> = {
  free: 'bg-surface-3 border-emerald-500/70 text-t1 hover:border-emerald-400',
  occupied: 'bg-red-600 border-red-400 text-white hover:bg-red-500',
  bill: 'bg-yellow-400 border-yellow-200 text-slate-900 hover:bg-yellow-300',
  reserved: 'bg-surface-3 border-blue-500 text-t1 hover:border-blue-300'
};
const TONE_DOT: Record<FloorTone, string> = {
  free: 'bg-emerald-500', occupied: 'bg-red-500', bill: 'bg-yellow-400', reserved: 'bg-blue-500'
};
/** Κλειδί μετάφρασης ανά τόνο (dineIn:*). */
// Νέα εμφάνιση (11/10/2026, απόφαση ιδιοκτήτη): απαλό φόντο + χρωματιστό περίγραμμα αντί για
// γεμάτα κόκκινα/κίτρινα· το ελεύθερο λευκό με πράσινο όριο.
const TONE_TILE_V2: Record<FloorTone, string> = {
  free: 'bg-surface-2 border-emerald-500/60 text-t1 hover:border-emerald-500',
  occupied: 'bg-red-50 border-red-500 text-red-800 dark:bg-red-500/15 dark:border-red-400 dark:text-red-100',
  bill: 'bg-amber-50 border-amber-500 text-amber-900 dark:bg-amber-400/15 dark:border-amber-400 dark:text-amber-100',
  reserved: 'bg-brand-soft border-brand text-t1'
};
const TONE_DOT_V2: Record<FloorTone, string> = {
  free: 'bg-emerald-500', occupied: 'bg-red-500', bill: 'bg-amber-500', reserved: 'bg-brand'
};
// Χρόνος τραπεζιού στη νέα εμφάνιση: ουδέτερος ως το όριο· απαλό κεχριμπαρί = πολλή ώρα χωρίς νέο
// γύρο, γεμάτο κεχριμπαρί = απόδειξη που δεν κλείνει (utils/dineInFloor.tableAttention).
const ATTENTION_BADGE_V2: Record<AttentionLevel, string> = {
  none: 'bg-surface-0 border-line-strong text-t1',
  // αδιαφανές και στο σκούρο: το σήμα κάθεται πάνω στο περίγραμμα του τραπεζιού
  idle: 'bg-amber-50 border-amber-400 text-amber-800 dark:bg-amber-950 dark:border-amber-400/70 dark:text-amber-200',
  bill: 'bg-amber-400 border-amber-500 text-amber-950 dark:bg-amber-400 dark:border-amber-300 dark:text-amber-950'
};
const TONE_LABEL: Record<FloorTone, string> = {
  free: 'available', occupied: 'occupied', bill: 'bill', reserved: 'reserved'
};

// Dine-in specific types
interface Zone {
  id: number;
  name: string;
  display_order: number;
  /** Η περιοχή όπως τη στήνει η Sala: σχήμα, τοίχοι, πόρτες, παράθυρα, μπαρ (ZoneRoom). */
  layout?: ZoneLayout | null;
}

interface Table {
  id: number;
  zone_id: number;
  table_number: string;
  name: string | null;
  capacity: number;
  shape: 'round' | 'square' | 'rectangle';
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  // Χωρίς 'cleaning' από 12/10/2026 (migration 268)· ένα παλιό διαβάζεται ελεύθερο (utils/dineInFloor).
  status: 'available' | 'occupied' | 'receipt_printed' | 'invoice_printed' | 'reserved';
  /** Ο ανοιχτός λογαριασμός του τραπεζιού, όταν υπάρχει (GET zones/:id/tables). */
  current_session_id?: number | null;
  guest_count?: number | null;
  session_opened_at?: string | null;
  session_total?: number | string | null;
  /** Για τα όρια προσοχής της «Νέας εμφάνισης» (utils/dineInFloor.tableAttention). */
  session_last_order_at?: string | null;
  session_receipt_printed_at?: string | null;
  session_payment_requested_at?: string | null;
}

interface Session {
  id: number;
  table_id: number;
  status: 'open' | 'receipt_printed' | 'invoice_printed' | 'paid' | 'closed';
  guest_count: number;
  opened_at: string;
  opened_by_name: string;
  subtotal: number;
  discount_type?: 'percentage' | 'amount' | null;
  discount_value?: number;
  discount_amount?: number;
  total_amount: number;
  fiscal_document_id?: number;
  /** GET …/details: επιτρέπεται είσπραξη («πρώτα απόδειξη») — utils/dineInHelpers.receiptGate */
  payment_unlocked?: boolean;
  /** GET …/details: κάρτα/IRIS χωρίς πληρωμή που τρέχει ή ίσως χρεώθηκε (utils/dineInCardDoubt) — μόνο ένδειξη */
  card_doubt?: CardDoubt[];
  /** GET …/details: Σ μη ακυρωμένων πληρωμών. */
  paid_amount?: number | string;
  /** GET …/details: η ενεργή απόδειξη/το τιμολόγιο (utils/dineInCancelAfterReceipt, 25/10/2026). */
  final_document?: TableFinalDocument | null;
}

interface TableFinalDocument {
  id: number;
  invoice_type_code: string;
  series: string;
  document_number: number;
  total_gross_amount: number | string;
  status: string;
  has_mark: boolean;
}

interface Order {
  id: number;
  session_id: number;
  order_number: number;
  status: 'pending' | 'sent_to_kitchen' | 'preparing' | 'ready' | 'served' | 'cancelled';
  created_at: string;
  created_by_name: string;
  total_amount: number;
  items: OrderItem[];
}

interface OrderItem {
  id: number;
  product_id: number;
  product_name: string;
  /** Ο server στέλνει ολόκληρη τη γραμμή (to_jsonb(oi.*)) — το id ταιριάζει το μέγεθος στον κατάλογο */
  size_id?: number | null;
  size_name: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
  status: string;
  is_treat: boolean;
  original_price?: number;
  notes: string | null;
  course_number?: number;
}

/**
 * Γραμμή του καλαθιού «Νέα παραγγελία». Έκπτωση/κέρασμα ταμία (6/10/2026): full_unit_price,
 * is_treat, discount_kind, discount_percent, adjust_reason — κανόνες στο utils/dineInBasketDiscount.
 */
type DineInBasketLine = AdjustableBasketLine & {
  product_id: number;
  size_id: number | null;
  product_name: string;
  size_name: string | null;
  quantity: number;
  unit_price: number;
  removedIngredients?: number[];
  removedIngredientNames?: string[];
  addedExtras?: { name: string; quantity: number; price: number }[];
  selected_options?: SelectedOptionEntry[];
  course_number: number;
  notes?: string;            // special kitchen instructions
  // Promotion fields
  applied_promotion_id?: number | null;
  applied_promotion_name?: string | null;
  original_unit_price?: number | null;
  // Weight-variable fields
  weight?: number | null;
  weight_unit?: string | null;
  is_open_price?: boolean;
};

const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: 'Αναμονή',
  sent_to_kitchen: 'Στην κουζίνα',
  preparing: 'Ετοιμάζεται',
  ready: 'Έτοιμη',
  served: 'Σερβιρίστηκε',
  cancelled: 'Ακυρωμένη'
};

/**
 * Κονσόλα ταμία για το dine-in (κάτοψη, λογαριασμοί, παραστατικά, πληρωμές).
 * Οι σερβιτόροι έχουν ξεχωριστό app κινητού — components/waiter/app/WaiterApp.
 * Κοινά: services/dineInApi και utils/dineInMoney.
 */
const DineInOperations: React.FC = () => {
  const { showToast } = useToast();
  const { t } = useTranslation(['dineIn', 'common', 'products', 'delivery', 'settings']);
  // «Νέα εμφάνιση» (ανά συσκευή — ThemeContext): κάτοψη, μπάρα ζωνών, νέα παραγγελία τραπεζιού.
  const { look } = useTheme();
  const v2 = look === 'v2';
  // Διάταξη καταλόγου της συσκευής («μία οθόνη» / «σε βήματα») — ίδια ρύθμιση με το Take Away.
  const catalogPrefs = useCatalogPrefsV2();
  const { store, employee } = useAuth();
  const { socket } = useSocket();
  
  // State
  const [zones, setZones] = useState<Zone[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [selectedZone, setSelectedZone] = useState<Zone | null>(null);
  const [selectedTable, setSelectedTable] = useState<Table | null>(null);
  const [tableDetails, setTableDetails] = useState<{
    table: Table;
    session: Session | null;
    orders: Order[];
    paidItems: Array<{ order_item_id: number; quantity_paid: number; payment_type: string }>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Order creation state
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  // Απόφαση 15 (FE-10b) / F1-S1: ίδιος κανόνας με ταμείο/delivery/KDS, χωρίς διακόπτη.
  // Οι γραμμές τραπεζιού δεν κουβαλούν το προϊόν — το μέγεθος ταιριάζεται στον φορτωμένο
  // κατάλογο· προϊόν εκτός καταλόγου = το όνομα φαίνεται, όπως πριν. Μόνο εμφάνιση.
  const sizeNameVisible = (item: { product_id: number; size_id?: number | null; size_name: string | null }) =>
    showSizeName(products, item.product_id, { id: item.size_id, name: item.size_name });
  // Το τεμάχιο (PayableUnit) του modal πληρωμής κρατά μόνο το ΟΝΟΜΑ μεγέθους (και το κλειδί ομαδοποίησης
  // το χρειάζεται όπως είναι)· προϊόν και size_id τα βρίσκουμε από τη γραμμή του server με το
  // itemId, όπως το unitSize του PaymentScreen του σερβιτόρου — αλλιώς η ίδια κονσόλα θα έκρυβε
  // το «Κανονικό» στον λογαριασμό και θα το έδειχνε στην πληρωμή. Γραμμή που δεν βρέθηκε
  // (π.χ. μεταφέρθηκε) = το όνομα φαίνεται, όπως πριν. Μόνο εμφάνιση.
  const lineById = useMemo(() => {
    const m = new Map<number, OrderItem>();
    for (const o of tableDetails?.orders || []) for (const it of o.items || []) m.set(Number(it.id), it);
    return m;
  }, [tableDetails]);
  const instanceSize = (it: { itemId: number; size: string }): string => {
    if (!it.size) return '';
    const line = lineById.get(Number(it.itemId));
    return !line || sizeNameVisible({ product_id: line.product_id, size_id: line.size_id, size_name: it.size }) ? it.size : '';
  };
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | null>(null);
  const [orderItems, setOrderItems] = useState<DineInBasketLine[]>([]);
  // «Αποστολή στην Κουζίνα» μία φορά ανά πάτημα. Η αποστολή κρατά 3–4″ (πρώτα βγαίνει το 8.6) και ένα δεύτερο
  // πάτημα στο μεταξύ έφτιαχνε ΔΕΥΤΕΡΗ παραγγελία (GENERIS τραπέζι 52, 8/10/2026: TIRAMISU δύο φορές, 4″ διαφορά).
  const [creatingOrder, setCreatingOrder] = useState(false);
  const creatingOrderRef = useRef(false);
  // Έκπτωση / κέρασμα ΓΡΑΜΜΩΝ (6/10/2026): ο ταμίας επιλέγει γραμμές (θέσεις στο καλάθι) και πατά
  // «Έκπτωση» ή «Κέρασμα». Αντικατέστησε τις σταθερές εκπτώσεις όλης της παραγγελίας (0/10/20/30/50)
  // — η παραγγελία δεν στέλνει πια order_discount_*. Η επιλογή καθαρίζει μετά από αποστολή,
  // αφαίρεση γραμμής, ενέργεια και κλείσιμο του καλαθιού.
  const [selectedLines, setSelectedLines] = useState<Set<number>>(() => new Set());
  const [basketAction, setBasketAction] = useState<'discount' | 'treat' | null>(null);
  // Numpad for session-level discount (replaces soft-keyboard input on touchscreen POS).
  const [sessionDiscountNumpadOpen, setSessionDiscountNumpadOpen] = useState(false);
  const [currentCourse, setCurrentCourse] = useState(1); // Track current course for new items
  const [dineInSettings, setDineInSettings] = useState<{
    course_management_enabled: boolean;
    /** Xentrix Sala (stores.reservations): κρατήσεις στην κάτοψη, το κουμπί ανοίγει τη Sala. */
    sala_enabled?: boolean;
    /** «Άτομα & θέσεις»: κλειστό = δεν δείχνουμε ούτε ζητάμε άτομα/θέσεις. */
    guest_count_enabled?: boolean;
    /** Κουμπί «Λογαριασμός» (μη φορολογικό χαρτί για το τραπέζι). */
    bill_print_enabled?: boolean;
    /** «Μήνυμα κουζίνας» (ΠΑΣΟ ΚΥΡΙΩΣ, ΟΛΑ ΜΑΖΙ …) και τα έτοιμα μηνύματα του καταστήματος. */
    kitchen_messages_enabled?: boolean;
    kitchen_messages?: string[];
    // «Επί πιστώσει» και «Ακύρωση λάθος πληρωμής»: μόνιμα από 6/10/2026, χωρίς διακόπτη — τα
    // credit_payment_enabled / payment_void_enabled που στέλνει ακόμη ο server αγνοούνται.
  } | null>(null);
  // Κρατήσεις στην κάτοψη μόνο με τη Sala (stores.reservations)· ο παλιός διαχειριστής καταργήθηκε 9/10/2026.
  const reservationsOn = dineInSettings?.sala_enabled === true;
  const [guestCount, setGuestCount] = useState(2);
  const [printingBill, setPrintingBill] = useState(false);
  // Μηνύματα κουζίνας: 'send' = μόνο του από το τραπέζι, 'attach' = μαζί με τον γύρο που ετοιμάζεται.
  const [kitchenMessageMode, setKitchenMessageMode] = useState<'send' | 'attach' | null>(null);
  const [orderKitchenMessages, setOrderKitchenMessages] = useState<string[]>([]);
  const [sendingKitchenMessage, setSendingKitchenMessage] = useState(false);
  const [closingZeroTable, setClosingZeroTable] = useState(false);
  // Ρολόι σάλας: ο χρόνος πάνω στα τραπέζια προχωρά και χωρίς νέο αίτημα.
  const [floorNow, setFloorNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setFloorNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  // Σε κινητό το καλάθι της παραγγελίας γίνεται overlay αντί για sidebar
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  // Viewport ≤767px → modals full-screen + κατάλογος σε 2 στήλες. Το sizing
  // γίνεται με inline styles ώστε να μην εξαρτάται από generated CSS classes.
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    // Safari < 14
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);
  // Όλες οι σημερινές· η κάτοψη και η κεφαλίδα κρατούν όσες τις αφορούν (utils/dineInReservations).
  const [todayReservations, setTodayReservations] = useState<FloorReservation[]>([]);
  
  // Employee table assignments state
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [waiters, setWaiters] = useState<{ id: number; name: string }[]>([]);
  const [selectedWaiterId, setSelectedWaiterId] = useState<number | null>(null);
  const [assignedTableIds, setAssignedTableIds] = useState<number[]>([]);
  const [savingAssignments, setSavingAssignments] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Session history state
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [sessionHistory, setSessionHistory] = useState<{
    id: number;
    session_number: string;
    status: string;
    guest_count: number;
    opened_at: string;
    closed_at: string;
    subtotal: number;
    discount_amount: number;
    total_amount: number;
    table_number: string;
    zone_name: string;
    opened_by_name: string;
    closed_by_name: string;
    order_count: number;
    total_paid: number;
  }[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedHistorySession, setSelectedHistorySession] = useState<any>(null);
  
  // Product selection modals (like TakeAway)
  const [showSizeModal, setShowSizeModal] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  // When set, the ProductCustomizeModal is editing an EXISTING basket item at
  // this index instead of adding a new one. Null = add-new flow.
  const [editingBasketIndex, setEditingBasketIndex] = useState<number | null>(null);
  const [showOpenPriceModal, setShowOpenPriceModal] = useState(false);
  const [openPriceInput, setOpenPriceInput] = useState<string>('');
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedSize, setSelectedSize] = useState<ProductSize | null>(null);
  const [removedIngredients, setRemovedIngredients] = useState<number[]>([]);
  const [addedExtras, setAddedExtras] = useState<{ name: string; quantity: number; price: number }[]>([]);
  const { ingredients: productIngredients, loading: loadingMaterials, fetchBySize: fetchIngredientsBySize, clear: clearIngredients } = useProductIngredients();
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);

  // Catalog navigation (parity with TakeAway/Delivery)
  const [currentView, setCurrentView] = useState<'categories' | 'subcategories' | 'products'>('categories');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [categoryColumns, setCategoryColumns] = useState<number>(() => {
    const saved = localStorage.getItem('dinein_category_columns');
    return saved ? parseInt(saved) : 4;
  });
  // 4 στήλες: με την παραγγελία σε πλήρη οθόνη τα πλακίδια μεγαλώνουν και διαβάζονται.
  // Νέο κλειδί (_v2) ώστε να μη μείνει το παλιό «5», που γραφόταν αυτόματα σε κάθε
  // ταμείο χωρίς να το έχει διαλέξει κανείς (δεν υπάρχει ρύθμιση στην οθόνη).
  const [productColumns, setProductColumns] = useState<number>(() => {
    const saved = localStorage.getItem('dinein_product_columns_v2');
    return saved ? parseInt(saved) : 4;
  });
  useEffect(() => { localStorage.setItem('dinein_category_columns', String(categoryColumns)); }, [categoryColumns]);
  useEffect(() => { localStorage.setItem('dinein_product_columns_v2', String(productColumns)); }, [productColumns]);

  // Option groups (required-only quick-flow + full customization flow)
  const [productOptionGroups, setProductOptionGroups] = useState<PosOptionGroup[]>([]);
  const [pendingSelectedOptions, setPendingSelectedOptions] = useState<SelectedOptionEntry[]>([]);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [optionModalPendingSize, setOptionModalPendingSize] = useState<ProductSize | null>(null);

  // Promotions
  const [appliedPromotionId, setAppliedPromotionId] = useState<number | null>(null);
  
  // Action modals
  const [showTableModal, setShowTableModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCorrectionModal, setShowCorrectionModal] = useState(false);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveMode, setMoveMode] = useState<'session' | 'items'>('session');
  const [selectedItemsToMove, setSelectedItemsToMove] = useState<OrderItem[]>([]);
  const [moveLoading, setMoveLoading] = useState(false);
  // Τα τραπέζια ΟΛΩΝ των ζωνών για τη «Μετακίνηση» (το `tables` είναι μόνο η ζώνη της οθόνης)· null = φορτώνει.
  const [moveTargets, setMoveTargets] = useState<Array<Table & { zone_name?: string | null }> | null>(null);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountType, setDiscountType] = useState<'percentage' | 'amount'>('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [discountLoading, setDiscountLoading] = useState(false);
  const [showTreatModal, setShowTreatModal] = useState(false);
  const [treatLoading, setTreatLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  // Προαιρετικός λόγος (6/10/2026) στα παράθυρα του τραπεζιού: κέρασμα, έκπτωση λογαριασμού,
  // διόρθωση. Ποιος = ο συνδεδεμένος χρήστης (ο server). Μηδενίζεται σε κάθε άνοιγμα.
  const [treatReason, setTreatReason] = useState<StaffReasonChoice>(NO_STAFF_REASON);
  const [sessionDiscountReason, setSessionDiscountReason] = useState<StaffReasonChoice>(NO_STAFF_REASON);
  const [correctionReason, setCorrectionReason] = useState<StaffReasonChoice>(NO_STAFF_REASON);
  const staffReasonText = useStaffReasonText();

  // Receipt/Invoice modal state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptType, setReceiptType] = useState<'receipt' | 'invoice'>('receipt');
  const [generatingReceipt, setGeneratingReceipt] = useState(false);
  const [customerVat, setCustomerVat] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerStreet, setCustomerStreet] = useState('');
  const [customerNumber, setCustomerNumber] = useState('');
  const [customerPostalCode, setCustomerPostalCode] = useState('');
  const [customerCity, setCustomerCity] = useState('');
  const [customerBranch, setCustomerBranch] = useState(0);
  const [lookingUpVat, setLookingUpVat] = useState(false);
  
  // Payment modal state
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentGroups, setPaymentGroups] = useState<Array<{
    id: string;
    method: PaymentMethod;
    items: PayableUnit[];
    /** Ό,τι χρεώθηκε από αυτή την οθόνη (η τελευταία δόση = υπόλοιπο). Οι ομάδες του server δεν το ξέρουν. */
    amount?: number;
  }>>([]);
  const [unassignedItems, setUnassignedItems] = useState<PayableUnit[]>([]);
  // Σύνολο τραπεζιού και εισπραγμένα όπως ήταν όταν άνοιξε η οθόνη πληρωμής, συν ό,τι εισπράχθηκε
  // από εδώ μετά: ΜΑΖΙ με τα τεμάχια της ίδιας στιγμής δίνουν το ποσό κάθε δόσης (sliceAmount).
  // Όχι τα ζωντανά tableDetails: ανανεώνονται ασύγχρονα μετά από κάθε πληρωμή και, μέχρι να
  // έρθουν, η τελευταία δόση θα ξαναχρέωνε ό,τι μόλις πληρώθηκε.
  const [paymentLedger, setPaymentLedger] = useState<{ total: number; paid: number }>({ total: 0, paid: 0 });
  const [selectedItems, setSelectedItems] = useState<Set<string>>(() => new Set());
  const [splitMode, setSplitMode] = useState(false);

  // POS Terminal state
  const [posTerminals, setPosTerminals] = useState<POSTerminal[]>([]);
  const [selectedTerminal, setSelectedTerminal] = useState<POSTerminal | null>(null);
  
  // Card payment UI state
  const [cardPaymentStatus, setCardPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');
  const [cardPaymentMessage, setCardPaymentMessage] = useState('');
  
  const { hasPermission } = usePermissions();

  // Fetch zones
  const fetchZones = useCallback(async () => {
    if (!store?.id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/zones`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setZones(data);
        // Μετά από αλλαγή κάτοψης η επιλεγμένη ζώνη μπορεί να άλλαξε σχήμα ή να μην υπάρχει πια.
        setSelectedZone(prev => (prev && data.find((z: Zone) => z.id === prev.id)) || data[0] || null);
      }
    } catch (error) {
      console.error('Failed to fetch zones:', error);
    }
  }, [store?.id]);

  // Fetch POS terminals
  const fetchPosTerminals = useCallback(async () => {
    if (!store?.id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store.id}/pos/terminals`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const activeTerminals = data.filter((t: POSTerminal) => t.is_active);
        setPosTerminals(activeTerminals);
        
        // Check if this cashier has a saved terminal preference
        const savedTerminalId = localStorage.getItem(`pos_terminal_${employee?.id}_${store.id}`);
        if (savedTerminalId) {
          const savedTerminal = activeTerminals.find((t: POSTerminal) => t.id === parseInt(savedTerminalId));
          if (savedTerminal) {
            setSelectedTerminal(savedTerminal);
            return;
          }
        }
        
        // Otherwise, set default terminal if available
        const defaultTerminal = activeTerminals.find((t: POSTerminal) => t.is_default);
        if (defaultTerminal) {
          setSelectedTerminal(defaultTerminal);
        } else if (activeTerminals.length > 0) {
          setSelectedTerminal(activeTerminals[0]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch POS terminals:', error);
    }
  }, [store?.id, employee?.id]);

  // Fetch tables for selected zone
  const fetchTables = useCallback(async () => {
    if (!store?.id || !selectedZone) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/zones/${selectedZone.id}/tables`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setTables(data);
      }
    } catch (error) {
      console.error('Failed to fetch tables:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [store?.id, selectedZone]);

  // Ready → served
  const markOrderServed = async (orderId: number) => {
    if (!store?.id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/orders/${orderId}/serve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        if (selectedTable) fetchTableDetails(selectedTable.id);
        fetchTables();
      } else {
        const error = await response.json().catch(() => ({} as any));
        showToast('error', error.message || error.error || 'Αποτυχία ενημέρωσης παραγγελίας');
      }
    } catch (error) {
      console.error('Failed to mark order served:', error);
    }
  };

  // Fetch today's reservations
  const fetchUpcomingReservations = useCallback(async () => {
    if (!store?.id || !reservationsOn) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/reservations/today`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTodayReservations(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
    }
  }, [store?.id, reservationsOn]);

  // Κρατήσεις ΜΕΣΑ στο ταμείο (reservations/): η καθημερινή δουλειά χωρίς άλλη εφαρμογή.
  const [showReservations, setShowReservations] = useState(false);

  // Fetch table details
  const fetchTableDetails = useCallback(async (tableId: number) => {
    if (!store?.id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/tables/${tableId}/details`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTableDetails(data);
      }
    } catch (error) {
      console.error('Failed to fetch table details:', error);
    }
  }, [store?.id]);

  // Fetch waiters for employee assignments
  const fetchWaiters = useCallback(async () => {
    if (!store?.id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/waiters`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        const waiterList = (data || []).map((e: any) => ({
          id: e.id,
          name: `${e.first_name || ''} ${e.last_name || ''}`.trim() || `#${e.id}`
        }));
        setWaiters(waiterList);
      }
    } catch (err) {
      console.error('Failed to fetch waiters:', err);
    }
  }, [store?.id]);

  // Fetch assignments for a specific waiter
  const fetchWaiterAssignments = useCallback(async (employeeId: number) => {
    if (!store?.id) return;
    setLoadingAssignments(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/employee-assignments/${employeeId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignedTableIds((data.tableAssignments || []).map((a: any) => a.table_id));
      }
    } catch (err) {
      console.error('Failed to fetch assignments:', err);
    } finally {
      setLoadingAssignments(false);
    }
  }, [store?.id]);

  // Save table assignments for selected waiter
  const saveAssignments = async () => {
    if (!store?.id || !selectedWaiterId) return;
    setSavingAssignments(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/employee-assignments/${selectedWaiterId}/tables`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tableIds: assignedTableIds })
      });
      if (response.ok) {
        setShowAssignmentsModal(false);
        setSelectedWaiterId(null);
        setAssignedTableIds([]);
      }
    } catch (err) {
      console.error('Failed to save assignments:', err);
    } finally {
      setSavingAssignments(false);
    }
  };

  // Fetch session history (today's closed sessions based on EOD)
  const fetchSessionHistory = useCallback(async () => {
    if (!store?.id) return;
    
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions/history/today`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSessionHistory(data);
      }
    } catch (error) {
      console.error('Failed to fetch session history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [store?.id]);

  // Fetch products and categories
  const fetchProducts = useCallback(async () => {
    if (!store?.id) return;
    
    try {
      const [cats, prods] = await Promise.all([
        productsAPI.getCategories(store.id),
        productsAPI.getProducts(store.id)
      ]);
      
      const activeCats = cats.filter((cat: Category) => cat.is_active !== false);
      setCategories(activeCats);
      if (activeCats.length > 0) {
        setSelectedCategory(activeCats[0].id);
        if (activeCats[0].subcategories && activeCats[0].subcategories.length > 0) {
          setSelectedSubcategory(activeCats[0].subcategories[0].id);
        }
      }
      
      setProducts(withSellableSizes(prods)); // χωρίς τα κρυμμένα μεγέθη
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  }, [store?.id]);

  // Μόνο οι τιμές/τα προϊόντα, χωρίς να πειράξει την κατηγορία που βλέπει ο ταμίας: όταν ο server
  // κόβει γύρο ως «κάτω από τον κατάλογο», ο κατάλογος της κονσόλας μπορεί να είναι παλιός.
  const refreshProductsOnly = useCallback(async () => {
    if (!store?.id) return;
    try {
      setProducts(withSellableSizes(await productsAPI.getProducts(store.id)));
    } catch (error) {
      console.error('Failed to refresh products:', error);
    }
  }, [store?.id]);

  // «Πιάτα ημέρας» (migration 278): στον κατάλογο της παραγγελίας η κατηγορία πρώτη και ΜΟΝΟ τα σημερινά. Η
  // πλήρης λίστα (products) μένει για ό,τι είναι ήδη στα τραπέζια — ένα πιάτο που βγήκε από τα σημερινά δεν
  // «χάνεται» από λογαριασμό. Κάθε αλλαγή ξαναφέρνει τα προϊόντα (νέο πιάτο, νέα τιμή).
  const { today: dailyToday } = useDailyDishesToday(store?.id, store?.daily_dishes_enabled === true, refreshProductsOnly);
  const dailyCatalog = useMemo(() => applyDailyDishes(categories, products, dailyToday), [categories, products, dailyToday]);

  /**
   * Σφάλμα γύρου: πρώτα το ελληνικό `message` του server (π.χ. 403 permission_denied στο
   * κέρασμα/έκπτωση γραμμής), μετά το `error`. Στο «κάτω από τον κατάλογο» ανανεώνεται ο κατάλογος
   * και το μήνυμα το λέει. Το καλάθι μένει όπως ήταν.
   */
  const showOrderError = useCallback((body: any, fallback: string) => {
    const message = body?.message || body?.error || fallback;
    if (body?.code === 'discount_forbidden') {
      refreshProductsOnly();
      showToast('error', `${message} ${t('dineIn:catalogRefreshedHint')}`);
      return;
    }
    showToast('error', message);
  }, [refreshProductsOnly, showToast, t]);

  // Fetch dine-in settings
  const fetchDineInSettings = useCallback(async () => {
    if (!store?.id) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/settings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDineInSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch dine-in settings:', error);
    }
  }, [store?.id]);

  useEffect(() => {
    fetchZones();
    fetchProducts();
    fetchPosTerminals();
    fetchDineInSettings();
  }, [fetchZones, fetchProducts, fetchPosTerminals, fetchDineInSettings]);

  useEffect(() => {
    if (!showOrderModal) {
      setMobileCartOpen(false);
      // Τα μηνύματα κουζίνας ανήκουν στον γύρο που ετοιμαζόταν — όχι στον επόμενο.
      setOrderKitchenMessages([]);
      // Ούτε η επιλογή γραμμών του καλαθιού.
      setSelectedLines(new Set());
      setBasketAction(null);
    }
  }, [showOrderModal]);

  useEffect(() => {
    if (selectedZone) {
      setLoading(true);
      fetchTables();
    }
  }, [selectedZone, fetchTables]);

  // «Μετακίνηση»: σε κάθε άνοιγμα φρέσκα τα τραπέζια όλου του καταστήματος (κατάσταση την ώρα της επιλογής).
  useEffect(() => {
    if (!showMoveModal || !store?.id) return;
    let alive = true;
    setMoveTargets(null);
    fetch(`${API_BASE_URL}/api/dine-in/${store.id}/tables`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => { if (alive && Array.isArray(data)) setMoveTargets(data); })
      .catch(() => { /* μένουν τα τραπέζια της ζώνης, όπως πριν */ });
    return () => { alive = false; };
  }, [showMoveModal, store?.id]);

  // Fetch reservations when settings are loaded
  useEffect(() => {
    if (reservationsOn) {
      fetchUpcomingReservations();
    }
  }, [reservationsOn, fetchUpcomingReservations]);

  // Auto-refresh tables and reservations every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedZone) {
        setRefreshing(true);
        fetchTables();
      }
      if (reservationsOn) {
        fetchUpcomingReservations();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [selectedZone, fetchTables, reservationsOn, fetchUpcomingReservations]);

  // Real-time socket updates — prefer surgical updates over full refetch
  // The backend emits events with shape:
  //   { type: 'table_opened' | 'table_ready' | 'session_closed' | 'new_order' | ...,
  //     table_id, table_status?, session_id?, order_id? }
  useEffect(() => {
    if (!socket) return;

    const handleTableUpdate = (data: any) => {
      // 0) Αποθηκεύτηκε νέα κάτοψη στη Sala: ζώνες και τραπέζια από την αρχή.
      if (data.type === 'layout_changed') {
        fetchZones();
        fetchTables();
        return;
      }
      // Το «κάλεσμα σερβιτόρου» (από KDS) δεν έχει καν table_id — χωρίς αυτό το branch ο ταμίας
      // δεν μάθαινε τίποτα. (Το «ζητάει λογαριασμό» καταργήθηκε 12/10/2026.)
      if (data.type === 'waiter_call') {
        showToast('info', `Κάλεσμα από την κουζίνα${data.table_number ? ` — τραπέζι ${data.table_number}` : ''}`);
        return;
      }

      // 1) Surgical update of the table list when we know the new status
      if (data.table_id && data.table_status) {
        setTables(prev => prev.map(tbl =>
          tbl.id === data.table_id ? { ...tbl, status: data.table_status } : tbl
        ));
      } else if (selectedZone && (data.type === 'new_order' || !data.type)) {
        // Unknown shape or events that may shift counts — fall back to a refetch.
        fetchTables();
      }

      // 2) If we're viewing the affected table, refresh its details (cheap, single call)
      if (selectedTable && data.table_id === selectedTable.id) {
        fetchTableDetails(selectedTable.id);
      }
    };

    socket.on('table-update', handleTableUpdate);
    return () => { socket.off('table-update', handleTableUpdate); };
  }, [socket, selectedZone, selectedTable, fetchZones, fetchTables, fetchTableDetails, showToast]);

  // Xentrix Sala: κάθε αλλαγή κράτησης (νέα, «κάθισε», «έφυγαν») φτάνει ζωντανά.
  useEffect(() => {
    if (!socket || !reservationsOn) return;
    const onReservation = () => { fetchUpcomingReservations(); };
    socket.on('reservation:changed', onReservation);
    return () => { socket.off('reservation:changed', onReservation); };
  }, [socket, reservationsOn, fetchUpcomingReservations]);

  // Handle table click
  const handleTableClick = async (table: Table) => {
    setSelectedTable(table);
    await fetchTableDetails(table.id);

    if (table.status === 'available') {
      // Open new order modal directly
      setOrderItems([]);
      setGuestCount(2);
      setShowOrderModal(true);
    } else {
      // Show table details modal
      setShowTableModal(true);
    }
  };

  // Από τις Κρατήσεις («Άνοιγμα τραπεζιού»): η περιοχή του τραπεζιού και ο λογαριασμός του.
  const openTableById = async (tableId: number) => {
    if (!store?.id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/tables`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('xentrix_token')}` },
      });
      if (!res.ok) return;
      const list: Table[] = await res.json();
      const table = list.find(x => x.id === tableId);
      if (!table) return;
      const zone = zones.find(z => z.id === table.zone_id);
      if (zone) setSelectedZone(zone);
      setShowReservations(false);
      await handleTableClick(table);
    } catch (error) {
      console.error('Failed to open table from reservations:', error);
    }
  };

  // Create new session and order
  const handleCreateOrder = async () => {
    if (!store?.id || !selectedTable || orderItems.length === 0) return;
    // Με ref και όχι μόνο με state: δύο γρήγορα πατήματα προλαβαίνουν το re-render που απενεργοποιεί το κουμπί.
    if (creatingOrderRef.current) return;
    creatingOrderRef.current = true;
    setCreatingOrder(true);
    
    try {
      // Ο γύρος όπως φεύγει — ΙΔΙΕΣ γραμμές στο άνοιγμα συνεδρίας (έλεγχος κατώτατου) και στο POST …/orders.
      const items = orderItems.map(item => ({
        product_id: item.product_id,
        size_id: item.size_id,
        quantity: item.quantity,
        product_name: item.product_name,
        size_name: item.size_name,
        course_number: item.course_number,
        // Customizations (Phase 1.1 fix — backend reads these names)
        removedIngredients: item.removedIngredients || [],
        // Ονόματα στη σειρά των ids: ο server τα κρατά για ό,τι δεν είναι υλικό του καταστήματος
        // (utils/dineInCustomization) — αλλιώς το «χωρίς» χάνεται από το δελτίο κουζίνας.
        removedIngredientNames: item.removedIngredientNames || [],
        addedExtras: item.addedExtras || [],
        selected_options: item.selected_options || [],
        notes: item.notes ?? null,
        // Weight-variable
        weight: item.weight ?? null,
        weight_unit: item.weight_unit ?? null,
        // Open-price
        is_open_price: item.is_open_price === true,
        // Τιμή, προσφορά, έκπτωση/κέρασμα γραμμής (+ προαιρετικός λόγος): κανονική γραμμή ακριβώς
        // όπως πριν· κέρασμα = is_treat + τιμή 0 + πλήρης τιμή· έκπτωση = τιμή μετά + πλήρης + % + €
        // ΟΛΗΣ της γραμμής.
        ...basketLinePricing(item)
      }));

      // Create session first (if table is available)
      let sessionId = tableDetails?.session?.id;

      if (!sessionId) {
        const sessionResponse = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            table_id: selectedTable.id,
            guest_count: guestCount,
            // Ο πρώτος γύρος: αν θα κοβόταν από το κατώτατο, ο server δεν ανοίγει καν το τραπέζι.
            items
          })
        });

        if (!sessionResponse.ok) {
          const error = await sessionResponse.json().catch(() => ({}));
          showOrderError(error, t('dineIn:sessionCreateFailed'));
          return;
        }

        const session = await sessionResponse.json();
        sessionId = session.id;
      }

      // Create order
      const orderResponse = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions/${sessionId}/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          // Χωρίς έκπτωση ΟΛΗΣ της παραγγελίας (order_discount_*) από 6/10/2026: έκπτωση και κέρασμα
          // μπαίνουν σε ΓΡΑΜΜΕΣ, με το δικαίωμα και το όνομα του χρήστη (utils/dineInBasketDiscount).
          // «ΟΛΑ ΜΑΖΙ» κ.λπ. στην κορυφή του δελτίου κουζίνας — μόνο όταν υπάρχουν.
          ...(orderKitchenMessages.length > 0 ? { kitchen_messages: orderKitchenMessages } : {}),
          items
        })
      });

      if (orderResponse.ok) {
        setShowOrderModal(false);
        setShowTableModal(false);
        setOrderItems([]);
        setSelectedLines(new Set());
        setCurrentCourse(1); // Reset course for next order
        setOrderKitchenMessages([]);
        fetchTables();
        if (selectedTable) {
          fetchTableDetails(selectedTable.id);
        }
      } else {
        // Πρώτα το ελληνικό message (π.χ. 403 permission_denied στο κέρασμα/έκπτωση)· το καλάθι μένει.
        const error = await orderResponse.json().catch(() => ({} as any));
        showOrderError(error, t('dineIn:orderCreateFailed'));
      }
    } catch (error) {
      console.error('Failed to create order:', error);
      showToast('error', t('dineIn:orderCreateFailed'));
    } finally {
      creatingOrderRef.current = false;
      setCreatingOrder(false);
    }
  };

  // Fetch ingredients for a product size (delegated to shared hook)
  const fetchSizeMaterials = async (sizeId: number) => {
    await fetchIngredientsBySize(sizeId);
  };

  // Catalog navigation handlers (parity with TakeAway)
  const getSelectedCategoryObj = () => categories.find(c => c.id === selectedCategory);

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category.id);
    setSearchTerm('');
    if (category.subcategories && category.subcategories.length > 0) {
      setCurrentView('subcategories');
    } else {
      setSelectedSubcategory(null);
      setCurrentView('products');
    }
  };

  const handleSubcategorySelect = (subcategory: Category | null) => {
    setSelectedSubcategory(subcategory?.id || null);
    setSearchTerm('');
    setCurrentView('products');
  };

  const handleCatalogBack = () => {
    if (currentView === 'products') {
      const cat = getSelectedCategoryObj();
      if (cat?.subcategories && cat.subcategories.length > 0) {
        setCurrentView('subcategories');
      } else {
        setSelectedCategory(null);
        setCurrentView('categories');
      }
    } else if (currentView === 'subcategories') {
      setSelectedCategory(null);
      setSelectedSubcategory(null);
      setCurrentView('categories');
    }
  };

  // Νέα εμφάνιση: «Όλα» φαίνεται και με επιλεγμένη κατηγορία — καθαρίζει και αυτήν.
  const handleCatalogAll = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchTerm('');
    setCurrentView('products');
  };

  // Home — jump straight back to the catalog root from any depth.
  const handleCatalogHome = () => {
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSearchTerm('');
    setCurrentView('categories');
  };

  // Fetch POS option groups for a product (mirrors TakeAway/Delivery)
  const fetchProductOptionGroups = async (productId: number): Promise<PosOptionGroup[]> => {
    if (!store?.id) return [];
    try {
      // Ίδιο endpoint και ίδια μορφή απάντησης (σκέτος πίνακας) με το TakeAway/Delivery:
      // το παλιό `/api/stores/:id/products/:pid/option-groups` δεν υπήρχε στο backend (404 →
      // καμία ομάδα στην οθόνη τραπεζιών, χωρίς μήνυμα).
      const res = await fetch(`${API_BASE_URL}/api/products/${productId}/option-groups`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
      });
      if (!res.ok) return [];
      const data = await res.json();
      const groups: PosOptionGroup[] = Array.isArray(data) ? data : [];
      setProductOptionGroups(groups);
      // Προεπιλογή ΜΟΝΟ όσων επιλογών είναι σημειωμένες is_default (όπως στο TakeAway) — όχι η
      // πρώτη επιλογή κάθε single ομάδας: αλλιώς κάθε ποτό θα έβγαινε «ΣΟΔΑ» και κάθε κυρίως «RARE».
      const defaults: SelectedOptionEntry[] = [];
      for (const g of groups) {
        const def = g.choices.find(c => c.is_default);
        if (def) {
          defaults.push({
            group_id: g.id, group_name: g.name,
            choice_id: def.id, choice_name: def.name,
            price_modifier: def.price_modifier
          });
        }
      }
      setPendingSelectedOptions(defaults);
      return groups;
    } catch (e) {
      return [];
    }
  };

  // Promotion apply/remove on basket (orderItems)
  // Αναίρεση προσφοράς σε μια γραμμή. Γραμμή με κέρασμα/έκπτωση ταμία (6/10/2026) την κρατά: χωρίς
  // την προσφορά η πλήρης τιμή της είναι ξανά η τιμή πριν από την προσφορά.
  const revertPromotionLine = (it: DineInBasketLine): DineInBasketLine => {
    if (!(it.applied_promotion_id && it.original_unit_price != null)) return it;
    const unpromoted = { ...it, applied_promotion_id: null, applied_promotion_name: null, original_unit_price: null };
    if (it.is_treat) return { ...unpromoted, full_unit_price: it.original_unit_price };
    // Έκπτωση ταμία δεν μπαίνει σε γραμμή προσφοράς (isPromotionLine)· αν βρεθεί, φεύγει μαζί της.
    return { ...unpromoted, unit_price: it.original_unit_price, full_unit_price: null };
  };

  const applyPromotion = (promo: EligiblePromotion) => {
    // Revert any previously applied promotion first
    let next = orderItems.map(revertPromotionLine);

    // Apply new promotion line-by-line — όχι σε γραμμές με κέρασμα/έκπτωση ταμία (δεν προστίθενται).
    const lineMap = new Map(promo.lines.map(l => [l.product_id, l]));
    next = next.map(it => {
      const line = lineMap.get(it.product_id);
      if (!line || hasCashierAdjustment(it)) return it;
      return {
        ...it,
        original_unit_price: it.unit_price,
        unit_price: line.new_unit_price,
        applied_promotion_id: promo.promotion_id,
        applied_promotion_name: promo.name
      };
    });

    setOrderItems(next);
    setAppliedPromotionId(promo.promotion_id);
  };

  const removePromotion = () => {
    setOrderItems(orderItems.map(revertPromotionLine));
    setAppliedPromotionId(null);
  };

  // Add weight-based product (mirrors TakeAway weight flow)
  const addWeightProductToOrder = (product: Product, weight: number) => {
    // Η τιμή κιλού ζει στο προεπιλεγμένο μέγεθος — ίδια με του πλακιδίου.
    const pricePerUnit = productDisplayPrice(product, 'dine_in');
    const unit = ((product as any).selling_unit || 'KG').toUpperCase();
    const unitPrice = parseFloat((pricePerUnit * weight).toFixed(2));
    setOrderItems([...orderItems, {
      product_id: product.id,
      size_id: null,
      product_name: product.name,
      size_name: null,
      quantity: 1,
      unit_price: unitPrice,
      weight,
      weight_unit: unit,
      course_number: currentCourse
    }]);
    setShowWeightModal(false);
    setSelectedProduct(null);
  };

  // Handle product click - opens size/options/weight/open-price as needed
  const handleProductClick = async (product: Product) => {
    setSelectedProduct(product);

    // Open price → numpad
    if (product.is_open_price) {
      setOpenPriceInput('');
      setShowOpenPriceModal(true);
      return;
    }

    // Weight-variable → weight modal
    if (product.is_weight_variable) {
      setShowWeightModal(true);
      return;
    }

    // Fetch option groups; for quick add we only show REQUIRED groups
    const groups = await fetchProductOptionGroups(product.id);
    const requiredGroups = groups.filter(g => g.is_required);

    // Ίδιος κανόνας με το app σερβιτόρου (dineInQuickAdd).
    const quick = dineInQuickAdd(product);
    if (quick.pick) {
      setSelectedSize(defaultSizeOf(product.sizes));
      setShowSizeModal(true);
    } else if (quick.size) {
      const defaultSize = quick.size;
      if (requiredGroups.length > 0) {
        setProductOptionGroups(requiredGroups);
        setOptionModalPendingSize(defaultSize);
        setShowOptionModal(true);
      } else {
        addToOrderWithSize(product, defaultSize);
      }
    } else {
      if (requiredGroups.length > 0) {
        setProductOptionGroups(requiredGroups);
        setOptionModalPendingSize(null);
        setShowOptionModal(true);
      } else {
        addToOrderWithoutSize(product);
      }
    }
  };

  // Handle long press for customization
  const handleProductLongPress = async (product: Product) => {
    setSelectedProduct(product);
    setEditingBasketIndex(null); // add-new flow
    const defaultSize = product.sizes?.find(s => s.is_default) || product.sizes?.[0] || null;
    setSelectedSize(defaultSize);
    setRemovedIngredients([]);
    setAddedExtras([]);
    setPendingSelectedOptions([]);

    // Fetch option groups in parallel with size materials so the modal shows
    // every customisation choice the product offers. Without this the modal
    // would inherit whatever option-groups state was last set (or empty),
    // causing it to silently hide ζάχαρη/γάλα/μέγεθος selections.
    await Promise.all([
      fetchProductOptionGroups(product.id),
      defaultSize ? fetchSizeMaterials(defaultSize.id) : Promise.resolve(clearIngredients()),
    ]);

    setShowCustomizeModal(true);
  };

  // Open the ProductCustomizeModal in EDIT mode for an existing basket item.
  // Mirrors handleProductLongPress but seeds the modal with the item's
  // already-chosen size / ingredients / extras / options / notes / discount
  // so the cashier can adjust without losing prior selections.
  const handleEditOrderItem = async (index: number) => {
    const item = orderItems[index];
    if (!item) return;
    const product = products.find(p => p.id === item.product_id);
    if (!product) return;

    setEditingBasketIndex(index);
    setSelectedProduct(product);
    const sizeObj = item.size_id != null ? (product.sizes?.find(s => s.id === item.size_id) || null) : null;
    setSelectedSize(sizeObj);
    setRemovedIngredients(item.removedIngredients || []);
    setAddedExtras(item.addedExtras || []);
    setPendingSelectedOptions(item.selected_options || []);

    // Fetch option groups + size materials, then re-apply the saved options
    // (fetchProductOptionGroups pre-selects defaults).
    await Promise.all([
      fetchProductOptionGroups(product.id),
      sizeObj ? fetchSizeMaterials(sizeObj.id) : Promise.resolve(clearIngredients()),
    ]);
    if (item.selected_options && item.selected_options.length > 0) {
      setPendingSelectedOptions(item.selected_options);
    }

    setShowCustomizeModal(true);
  };

  const handleMouseDown = (product: Product) => {
    const timer = setTimeout(() => {
      handleProductLongPress(product);
    }, 500);
    setLongPressTimer(timer);
  };

  const handleMouseUp = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Ίδιο απλό προϊόν (χωρίς προσαρμογές) → αύξηση ποσότητας στην υπάρχουσα
  // γραμμή αντί για νέα — αλλιώς 5 πατήματα = 5 γραμμές «×1» στο καλάθι.
  // Ποτέ μέσα σε γραμμή με έκπτωση/κέρασμα ταμία: το νέο τεμάχιο θα έπαιρνε σιωπηλά την έκπτωση.
  const addOrMergeOrderItem = (newItem: any) => {
    const isPlain = (it: any) =>
      !(it.removedIngredients?.length) &&
      !(it.addedExtras?.length) &&
      !(it.selected_options?.length) &&
      !it.weight &&
      !it.notes &&
      !it.applied_promotion_id &&
      !hasCashierAdjustment(it);
    setOrderItems(prev => {
      if (isPlain(newItem)) {
        const idx = prev.findIndex(it =>
          it.product_id === newItem.product_id &&
          (it.size_id ?? null) === (newItem.size_id ?? null) &&
          it.unit_price === newItem.unit_price &&
          (it.course_number || 1) === (newItem.course_number || 1) &&
          isPlain(it)
        );
        if (idx !== -1) {
          const next = [...prev];
          next[idx] = { ...next[idx], quantity: next[idx].quantity + 1 };
          return next;
        }
      }
      return [...prev, newItem];
    });
  };

  // Add product to order with size
  const addToOrderWithSize = (product: Product, size: ProductSize, customizations?: {
    removedIngredients?: number[];
    addedExtras?: { name: string; quantity: number; price: number }[];
    selected_options?: SelectedOptionEntry[];
  }) => {
    // Τέλος → feeUnitGross (το μέγεθος αγνοείται)· αλλιώς η τιμή dine-in του μεγέθους.
    // + επιλογές (+ έξτρα): η ΙΔΙΑ συνάρτηση με την εφαρμογή σερβιτόρου (utils/dineInMoney).
    const totalPrice = unitPriceWithAddOns(lineUnitPrice(product, size, 'dine_in'), customizations?.selected_options, customizations?.addedExtras);

    addOrMergeOrderItem({
      product_id: product.id,
      size_id: size.id,
      product_name: product.name,
      size_name: size.name,
      quantity: 1,
      unit_price: totalPrice,
      removedIngredients: customizations?.removedIngredients,
      addedExtras: customizations?.addedExtras,
      selected_options: customizations?.selected_options?.length ? customizations.selected_options : undefined,
      course_number: currentCourse
    });

    // Reset modal states
    setShowSizeModal(false);
    setShowCustomizeModal(false);
    setShowOptionModal(false);
    setSelectedProduct(null);
    setSelectedSize(null);
    setRemovedIngredients([]);
    setAddedExtras([]);
    setProductOptionGroups([]);
    setPendingSelectedOptions([]);
    setOptionModalPendingSize(null);
  };

  // Add product without size
  const addToOrderWithoutSize = (product: Product, customizations?: {
    selected_options?: SelectedOptionEntry[];
    addedExtras?: { name: string; quantity: number; price: number }[];
  }) => {
    // Τέλος → feeUnitGross· μη-τέλος χωρίς μέγεθος → προεπιλεγμένο μέγεθος, όπως τιμολογεί
    // και ο server γραμμή χωρίς size_id (απόφαση 12).
    // + επιλογές (+ έξτρα): η ΙΔΙΑ συνάρτηση με την εφαρμογή σερβιτόρου (utils/dineInMoney).
    const price = unitPriceWithAddOns(lineUnitPrice(product, null, 'dine_in'), customizations?.selected_options, customizations?.addedExtras);

    addOrMergeOrderItem({
      product_id: product.id,
      size_id: null,
      product_name: product.name,
      size_name: null,
      quantity: 1,
      unit_price: price,
      addedExtras: customizations?.addedExtras,
      selected_options: customizations?.selected_options?.length ? customizations.selected_options : undefined,
      course_number: currentCourse
    });

    setShowOptionModal(false);
    setSelectedProduct(null);
    setProductOptionGroups([]);
    setPendingSelectedOptions([]);
    setOptionModalPendingSize(null);
  };

  // Add open-price product with manually entered price
  const addOpenPriceToOrder = (product: Product, price: number) => {
    const defaultSize = product.sizes?.find(s => s.is_default) || product.sizes?.[0] || null;
    
    addOrMergeOrderItem({
      product_id: product.id,
      size_id: defaultSize?.id || null,
      product_name: product.name,
      size_name: defaultSize?.name || null,
      quantity: 1,
      unit_price: price,
      course_number: currentCourse
    });

    setShowOpenPriceModal(false);
    setSelectedProduct(null);
    setOpenPriceInput('');
  };

  // Toggle ingredient removal
  const toggleIngredientRemoval = (ingredientId: number) => {
    setRemovedIngredients(prev =>
      prev.includes(ingredientId)
        ? prev.filter(id => id !== ingredientId)
        : [...prev, ingredientId]
    );
  };

  // (Το updateTableStatus έφυγε με το «Καθαρίστηκε», 12/10/2026 — ήταν ο μόνος καλών του.)

  // Update order item quantity (correction)
  // (Το νεκρό updateItemQuantity αφαιρέθηκε μαζί με το αφύλαχτο PUT endpoint
  // του backend — οι διορθώσεις ποσότητας γίνονται μέσω του DELETE με quantity,
  // που ελέγχει δικαιώματα, stock και εκδίδει διορθωτικό.)

  // Ο προαιρετικός λόγος της «Διόρθωσης» (ένας για όλο το παράθυρο), στο σώμα κάθε DELETE.
  const correctionBody = (quantity: number) => {
    const reason = staffReasonText(correctionReason);
    return JSON.stringify(reason ? { quantity, reason } : { quantity });
  };

  // Delete order item (correction) - generates fiscal correction document
  const deleteOrderItem = async (itemId: number, quantity: number = 1, skipConfirm: boolean = false) => {
    if (!store?.id) return;

    if (!skipConfirm && !window.confirm(t('dineIn:confirmRemoveProduct'))) return;

    setCorrectionLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/order-items/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: correctionBody(quantity)
      });

      if (response.ok) {
        // Τραπέζι με έκπτωση ποσού: τα παραστατικά του τραπεζιού (ζεύγος) μπορεί να μην ολοκληρώθηκαν.
        showDiscountFiscalWarning(await readJsonBody(response));
        // Refresh table details
        if (selectedTable) {
          await fetchTableDetails(selectedTable.id);
        }
      } else {
        const error = await response.json();
        showToast('error', error.error || t('dineIn:deleteFailed'));
      }
    } catch (error) {
      console.error('Failed to delete item:', error);
      showToast('error', t('dineIn:deleteFailed'));
    } finally {
      setCorrectionLoading(false);
    }
  };

  // Move entire session to another table
  const moveSession = async (targetTableId: number) => {
    if (!store?.id || !tableDetails?.session) return;
    
    setMoveLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions/${tableDetails.session.id}/move`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ targetTableId })
      });
      
      if (response.ok) {
        // Σε άλλη ζώνη το τραπέζι φεύγει από την οθόνη: πες πού πήγε.
        const target = moveTargets?.find(tb => tb.id === targetTableId);
        if (target && target.zone_id !== selectedTable?.zone_id) {
          showToast('success', t('dineIn:sessionMovedTo', { table: target.table_number, zone: target.zone_name || '' }));
        }
        setShowMoveModal(false);
        setShowTableModal(false);
        fetchTables();
      } else {
        const error = await response.json();
        showToast('error', error.error || t('dineIn:moveFailed'));
      }
    } catch (error) {
      console.error('Failed to move session:', error);
      showToast('error', t('dineIn:moveFailed'));
    } finally {
      setMoveLoading(false);
    }
  };

  // Move multiple items to another table
  const moveItems = async (targetTableId: number) => {
    if (!store?.id || selectedItemsToMove.length === 0) return;
    
    setMoveLoading(true);
    // Τραπέζι προέλευσης με έκπτωση ποσού: κάθε μετακίνηση βγάζει ζεύγος· η προειδοποίηση μία φορά, στο τέλος.
    let fiscalWarningBody: unknown = null;
    try {
      // Move each selected item
      for (const item of selectedItemsToMove) {
        const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/order-items/${item.id}/move`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ targetTableId })
        });
        
        if (!response.ok) {
          const error = await response.json();
          showToast('error', error.error || t('dineIn:moveItemFailed', { name: item.product_name }));
          break;
        }
        const body = await readJsonBody(response);
        if (body?.fiscalCorrection?.warning) fiscalWarningBody = body;
      }
      showDiscountFiscalWarning(fiscalWarningBody);

      setShowMoveModal(false);
      setSelectedItemsToMove([]);
      // Refresh current table details
      if (selectedTable) {
        await fetchTableDetails(selectedTable.id);
      }
      fetchTables();
    } catch (error) {
      console.error('Failed to move items:', error);
      showToast('error', t('dineIn:moveProductsFailed'));
    } finally {
      setMoveLoading(false);
    }
  };

  // Toggle item selection for move
  const toggleItemForMove = (item: OrderItem) => {
    setSelectedItemsToMove(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  // Get available tables for moving (excluding current table)
  // Όλες οι ζώνες (utils/dineInMoveTargets)· όσο φορτώνουν, τα τραπέζια της ζώνης της οθόνης.
  const getMoveTargetGroups = () => {
    if (!selectedTable) return [];
    const source = moveTargets ?? tables.map(t => ({ ...t, zone_name: selectedZone?.name ?? null }));
    return groupMoveTargets(source, {
      excludeTableId: selectedTable.id,
      currentZoneId: selectedTable.zone_id,
      includeOccupied: moveMode === 'items',
    });
  };

  const renderMoveTargets = (onPick: (table: Table) => void, showName: boolean) => {
    const groups = getMoveTargetGroups();
    return (
      <div className="space-y-4">
        {groups.map(group => (
          <div key={group.zoneId}>
            {groups.length > 1 && (
              <div className="text-xs font-semibold text-t3 mb-2">{group.zoneName}</div>
            )}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {group.tables.map(table => (
                <button
                  key={table.id}
                  onClick={() => onPick(table)}
                  disabled={moveLoading}
                  className={`p-4 rounded-xl border-2 transition-all hover:scale-105 disabled:opacity-50 ${
                    table.status === 'available'
                      ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700 hover:border-green-500'
                      : 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-700 hover:border-red-500'
                  }`}
                >
                  <div className="text-xl font-bold text-t1">{table.table_number}</div>
                  <div className="text-xs text-t3 mt-1">
                    {table.status === 'available' ? t('dineIn:available') : t('dineIn:occupied')}
                  </div>
                  {showName && table.name && (
                    <div className="text-xs text-t4 truncate">{table.name}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Το backend απαντά 2xx και όταν τα παραστατικά της αλλαγής έκπτωσης ΔΕΝ ολοκληρώθηκαν (μισό ζεύγος, πάροχος που
  // δεν απάντησε, απόρριψη): fiscalCorrection.warning. Μένει στην οθόνη αρκετά για να διαβαστεί. Το ίδιο και η
  // «Διόρθωση», το κέρασμα (και η αναίρεσή του) και η μετακίνηση είδους σε τραπέζι με έκπτωση ΠΟΣΟΥ (10/10/2026):
  // βγάζουν κι αυτά ζεύγος, με δικά τους κείμενα.
  const showDiscountFiscalWarning = (body: unknown) => {
    const warning = (body as { fiscalCorrection?: { warning?: unknown } } | null)?.fiscalCorrection?.warning;
    if (typeof warning === 'string' && warning.trim() !== '') showToast('warning', warning, 15000);
  };
  // Σώμα απάντησης χωρίς να πετάει (HTML 504 του proxy, κενό σώμα).
  const readJsonBody = async (response: Response): Promise<any> => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Apply discount to session
  const applyDiscount = async () => {
    if (!store?.id || !tableDetails?.session) return;

    const value = parseFloat(discountValue);
    if (isNaN(value) || value <= 0) {
      showToast('error', t('dineIn:enterValidDiscount'));
      return;
    }
    
    if (discountType === 'percentage' && value > 100) {
      showToast('error', t('dineIn:percentageExceeds100'));
      return;
    }
    
    // Προαιρετικός λόγος (6/10/2026) — μόνο όταν δόθηκε.
    const reason = staffReasonText(sessionDiscountReason);
    setDiscountLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions/${tableDetails.session.id}/discount`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ discountType, discountValue: value, ...(reason ? { reason } : {}) })
      });

      if (response.ok) {
        const body = await readJsonBody(response);
        setShowDiscountModal(false);
        setDiscountValue('');
        setDiscountType('percentage');
        setSessionDiscountReason(NO_STAFF_REASON);
        // Η έκπτωση μπήκε, αλλά τα παραστατικά της (διορθωτικό + νέο Δελτίο Παραγγελίας) δεν ολοκληρώθηκαν: ο ταμίας
        // πρέπει να το ξέρει ΠΡΙΝ πατήσει «Απόδειξη» μπροστά στον πελάτη.
        showDiscountFiscalWarning(body);
        if (selectedTable) {
          await fetchTableDetails(selectedTable.id);
        }
      } else {
        const error = (await readJsonBody(response)) || {};
        showToast('error', error.error || t('dineIn:discountApplyFailed'));
        // Σφάλμα μετά την εγγραφή (π.χ. 504 του proxy όσο περιμέναμε τον πάροχο): το τραπέζι δείχνει τι ισχύει.
        if (selectedTable) await fetchTableDetails(selectedTable.id);
      }
    } catch (error) {
      console.error('Failed to apply discount:', error);
      showToast('error', t('dineIn:discountApplyFailed'));
      if (selectedTable) await fetchTableDetails(selectedTable.id).catch(() => {});
    } finally {
      setDiscountLoading(false);
    }
  };

  // Remove discount from session
  const removeDiscount = async () => {
    if (!store?.id || !tableDetails?.session) return;
    
    setDiscountLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions/${tableDetails.session.id}/discount`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      
      if (response.ok) {
        showDiscountFiscalWarning(await readJsonBody(response));
        if (selectedTable) {
          await fetchTableDetails(selectedTable.id);
        }
      } else {
        const error = (await readJsonBody(response)) || {};
        showToast('error', error.error || t('dineIn:discountRemoveFailed'));
        if (selectedTable) await fetchTableDetails(selectedTable.id);
      }
    } catch (error) {
      console.error('Failed to remove discount:', error);
      showToast('error', t('dineIn:discountRemoveFailed'));
      if (selectedTable) await fetchTableDetails(selectedTable.id).catch(() => {});
    } finally {
      setDiscountLoading(false);
    }
  };

  // Mark item as treat — με τον προαιρετικό λόγο του παραθύρου «Κέρασμα» (6/10/2026).
  const markAsTreat = async (itemId: number) => {
    if (!store?.id) return;

    const reason = staffReasonText(treatReason);
    setTreatLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/order-items/${itemId}/treat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reason ? { reason } : {})
      });

      if (response.ok) {
        showDiscountFiscalWarning(await readJsonBody(response));
        if (selectedTable) {
          await fetchTableDetails(selectedTable.id);
        }
      } else {
        const error = await response.json();
        showToast('error', error.error || t('dineIn:treatFailed'));
      }
    } catch (error) {
      console.error('Failed to mark as treat:', error);
      showToast('error', t('dineIn:treatFailed'));
    } finally {
      setTreatLoading(false);
    }
  };

  // Remove treat from item
  const removeTreat = async (itemId: number) => {
    if (!store?.id) return;
    
    setTreatLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/order-items/${itemId}/treat`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      if (response.ok) {
        showDiscountFiscalWarning(await readJsonBody(response));
        if (selectedTable) {
          await fetchTableDetails(selectedTable.id);
        }
      } else {
        const error = await response.json();
        showToast('error', error.error || t('dineIn:treatUndoFailed'));
      }
    } catch (error) {
      console.error('Failed to remove treat:', error);
      showToast('error', t('dineIn:treatUndoFailed'));
    } finally {
      setTreatLoading(false);
    }
  };

  // Cancel session — opens the shared modal which gathers reason + (optional) PIN.
  const cancelSession = () => {
    if (!store?.id || !tableDetails?.session) return;
    setShowCancelModal(true);
  };

  const submitCancelSession = async (reason: string, force?: boolean) => {
    if (!store?.id || !tableDetails?.session) return;
    // Λόγος προαιρετικός (6/10/2026): μόνο αν γράφτηκε/διαλέχτηκε — όχι πια το γενικό «Ακυρώθηκε από
    // προσωπικό», που έκρυβε ότι δεν δόθηκε λόγος. Ποιος = ο χρήστης (ο server).
    const cleanReason = (reason || '').trim();
    setCancelLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions/${tableDetails.session.id}/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...(cleanReason ? { reason: cleanReason } : {}), force: force === true })
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        // Με απόδειξη/τιμολόγιο: ποιο ακυρωτικό βγήκε στην ΑΑΔΕ (ο server το γράφει ήδη στα ελληνικά).
        if (Array.isArray(data?.finalDocuments) && data.finalDocuments.length > 0 && data.message) {
          showToast('success', data.message);
        }
        setShowTableModal(false);
        fetchTables();
      } else {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || error.error || t('dineIn:cancelFailed'));
      }
    } finally {
      setCancelLoading(false);
    }
  };

  // «Ακύρωση τραπεζιού» με απόδειξη/τιμολόγιο (25/10/2026): τι θα βγει στην ΑΑΔΕ, ΠΡΙΝ το πάτημα — ή γιατί δεν γίνεται.
  const cancelFinalDoc = tableDetails?.session?.final_document || null;
  const cancelModalFiscal = (() => {
    if (!cancelFinalDoc) return null;
    const isInvoice = ['1.1', '2.1'].includes(cancelFinalDoc.invoice_type_code);
    const doc = t(isInvoice ? 'dineIn:cancelAfterReceipt.docInvoice' : 'dineIn:cancelAfterReceipt.docReceipt',
      { number: `${cancelFinalDoc.series}/${cancelFinalDoc.document_number}` });
    const paid = parseFloat(String(tableDetails?.session?.paid_amount ?? 0)) || 0;
    // 'cancelled' (με MARK) = το ακυρωτικό βγήκε σε προηγούμενο πάτημα· μένει η ακύρωση του λογαριασμού.
    const alreadyCancelled = cancelFinalDoc.status === 'cancelled';
    let blocked: string | null = null;
    if (!hasPermission('dine_in_cancel_after_receipt')) blocked = t('dineIn:cancelAfterReceipt.forbidden');
    else if (!cancelFinalDoc.has_mark || !['success', 'cancelled'].includes(cancelFinalDoc.status)) {
      blocked = t('dineIn:cancelAfterReceipt.noMark', { doc });
    }
    return {
      title: t(isInvoice ? 'dineIn:cancelAfterReceipt.titleInvoice' : 'dineIn:cancelAfterReceipt.titleReceipt'),
      blocked,
      notice: (
        <>
          <p>{t(alreadyCancelled ? 'dineIn:cancelAfterReceipt.alreadyCancelled' : 'dineIn:cancelAfterReceipt.body', {
            table: selectedTable?.table_number ?? '', doc, amount: formatMoney(Number(cancelFinalDoc.total_gross_amount)),
          })}</p>
          {paid > 0.004 && <p>{t('dineIn:cancelAfterReceipt.paidNote', { amount: formatMoney(paid) })}</p>}
        </>
      ),
    };
  })();

  // Lookup customer VAT from AADE
  const lookupVat = async () => {
    if (!store?.id || !customerVat || customerVat.length < 9) return;
    
    setLookingUpVat(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store.id}/aade/lookup/${customerVat}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.company) {
          setCustomerName(data.company.name || '');
          setCustomerBranch(data.company.branch_code || 0);
          const addr = data.company.address;
          if (addr) {
            setCustomerStreet(addr.street || '');
            setCustomerNumber(addr.number || '');
            setCustomerPostalCode(addr.postal_code || '');
            setCustomerCity(addr.city || '');
          }
        } else {
          showToast('error', t('dineIn:vatNotFoundInAADE'));
        }
      } else {
        const error = await response.json();
        showToast('error', error.error || t('dineIn:vatLookupFailed'));
      }
    } catch (error) {
      console.error('Failed to lookup VAT:', error);
      showToast('error', t('dineIn:vatLookupFailed'));
    } finally {
      setLookingUpVat(false);
    }
  };

  // Generate receipt directly (no modal)
  const generateReceiptDirect = async () => {
    if (!store?.id || !tableDetails?.session) return;
    
    setGeneratingReceipt(true);
    try {
      const endpoint = `${API_BASE_URL}/api/dine-in/${store.id}/sessions/${tableDetails.session.id}/receipt`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invoiceTypeCode: '11.1' })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        // Receipt will be printed automatically by Print Agent
        setShowTableModal(false);
        fetchTables();
      } else if (response.ok && result.issued) {
        // Πτώση παρόχου: το παραστατικό ΕΚΔΟΘΗΚΕ (με αριθμό), τυπώνεται με σήμανση
        // «Απώλεια Διασύνδεσης» και το τραπέζι κλείδωσε — όχι σφάλμα. Κλείνει το
        // παράθυρο ώστε να μην ξαναπατηθεί η έκδοση.
        showToast('warning', result.message);
        setShowTableModal(false);
        fetchTables();
      } else {
        console.error('❌ Receipt generation failed:', result);
        showToast('error', result.message || result.error || t('dineIn:receiptIssueFailed'));
      }
    } catch (error) {
      console.error('❌ Failed to generate receipt:', error);
      showToast('error', t('dineIn:receiptIssueFailed'));
    } finally {
      setGeneratingReceipt(false);
    }
  };

  // Generate invoice (from modal with customer data)
  const generateReceipt = async () => {
    if (!store?.id || !tableDetails?.session) return;
    
    if (receiptType === 'invoice' && !customerVat) {
      showToast('error', t('dineIn:enterCustomerVAT'));
      return;
    }
    
    setGeneratingReceipt(true);
    try {
      const endpoint = receiptType === 'invoice' 
        ? `${API_BASE_URL}/api/dine-in/${store.id}/sessions/${tableDetails.session.id}/invoice`
        : `${API_BASE_URL}/api/dine-in/${store.id}/sessions/${tableDetails.session.id}/receipt`;
      
      const body: any = {
        invoiceTypeCode: receiptType === 'invoice' ? '1.1' : '11.1'
      };
      
      if (receiptType === 'invoice') {
        body.customerVat = customerVat;
        body.customerName = customerName;
        body.customerStreet = customerStreet;
        body.customerNumber = customerNumber;
        body.customerPostalCode = customerPostalCode;
        body.customerCity = customerCity;
        body.customerBranch = customerBranch;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        // Receipt/Invoice will be printed automatically by Print Agent
        
        setShowReceiptModal(false);
        setShowTableModal(false);
        // Reset form
        setCustomerVat('');
        setCustomerName('');
        setCustomerStreet('');
        setCustomerNumber('');
        setCustomerPostalCode('');
        setCustomerCity('');
        setCustomerBranch(0);
        setReceiptType('receipt');
        fetchTables();
      } else if (response.ok && result.issued) {
        // Πτώση παρόχου: εκδόθηκε (με αριθμό), τυπώνεται με σήμανση, κλείδωσε — βλ. generateReceiptDirect.
        showToast('warning', result.message);
        setShowReceiptModal(false);
        setShowTableModal(false);
        fetchTables();
      } else {
        console.error('❌ Receipt generation failed:', result);
        showToast('error', result.message || result.error || t('dineIn:invoiceIssueFailed'));
      }
    } catch (error) {
      console.error('❌ Failed to generate receipt:', error);
      showToast('error', t('dineIn:invoiceIssueFailed'));
    } finally {
      setGeneratingReceipt(false);
    }
  };

  // Get session total — το session.total_amount είναι ήδη μετά την έκπτωση
  // (total_amount = subtotal - discount_amount, βλ. dineInKitchen discount).
  // Το άθροισμα των items είναι ΠΡΟ έκπτωσης, οπότε μπαίνει μόνο ως fallback.
  const getSessionTotal = () => {
    const sessionTotal = parseFloat(String(tableDetails?.session?.total_amount ?? ''));
    if (!isNaN(sessionTotal)) return sessionTotal;
    if (!tableDetails?.orders) return 0;
    let total = 0;
    tableDetails.orders.forEach((order: any) => {
      if (order.status === 'cancelled') return;
      order.items?.forEach((item: any) => {
        total += parseFloat(item.total_price || 0);
      });
    });
    return total;
  };

  // Ποσό δόσης: η ΙΔΙΑ συνάρτηση με το PDA (utils/dineInMoney.sliceAmount). Η τελευταία δόση
  // χρεώνει ΑΚΡΙΒΩΣ το υπόλοιπο (σύνολο − εισπραγμένα), καμία ενδιάμεση δεν το ξεπερνά — πριν
  // στέλναμε το άθροισμα των στρογγυλεμένων τιμών τεμαχίων (GENERIS τραπέζι 52, έκπτωση 25%:
  // 59,48 για σύνολο 59,47).
  const paymentSlice = (items: PayableUnit[]) =>
    sliceAmount(items, unassignedItems, paymentLedger.total, paymentLedger.paid);

  // «Περισσότερα» στην οθόνη πληρωμής: IRIS και «Επί πιστώσει» κάτω από τα γρήγορα Μετρητά/Κάρτα.
  const [morePaymentMethods, setMorePaymentMethods] = useState(false);

  // Άνοιγμα οθόνης πληρωμής: τεμάχια, πληρωμένα ανά μέθοδο, σύνολο και εισπραγμένα από το
  // utils/dineInMoney.tablePaymentState — την ίδια συνάρτηση με το PDA του σερβιτόρου. Τα
  // πληρωμένα τεμάχια μετρούν ΑΝΑ μέθοδο (2 μπύρες μετρητά + 1 κάρτα = δύο ομάδες).
  const openPaymentModal = () => {
    if (!tableDetails?.orders) return;

    const state = tablePaymentState(tableDetails);
    // Ομάδες «ήδη πληρωμένα» ανά μέθοδο, με τη σειρά που εμφανίζονται στον λογαριασμό (όπως πριν).
    // Το «Επί πιστώσει» έχει δική του ομάδα — ποτέ μέσα στην «κάρτα».
    const firstIndex = (m: PaymentMethod) => state.units.indexOf(state.paid[m][0]);
    const initialGroups = (['cash', 'card', 'iris', 'credit'] as PaymentMethod[])
      .filter(m => state.paid[m].length > 0)
      .sort((a, b) => firstIndex(a) - firstIndex(b))
      .map(method => ({ id: `existing-${method}`, method, items: state.paid[method] }));

    setUnassignedItems(state.unpaid);
    setPaymentGroups(initialGroups);
    setPaymentLedger({ total: state.total, paid: state.alreadyPaid });
    setSelectedItems(new Set());
    setSplitMode(false);
    setMorePaymentMethods(false);
    setShowPaymentModal(true);
  };

  // Toggle item selection for payment
  const toggleItemSelection = (orderId: number, itemId: number, instanceIndex: number) => {
    const key = `${orderId}-${itemId}-${instanceIndex}`;
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Helper: move items from unassigned → paid group in local state (και το ποσό στα εισπραγμένα)
  const applyLocalPayment = (items: PayableUnit[], method: PaymentMethod, amount: number) => {
    setPaymentGroups(prev => [...prev, { id: `group-${Date.now()}`, method, items, amount }]);
    setPaymentLedger(prev => ({ ...prev, paid: roundToCents(prev.paid + amount) }));
    setUnassignedItems(prev => prev.filter(i =>
      !items.some(paid =>
        paid.orderId === i.orderId &&
        paid.itemId === i.itemId &&
        paid.instanceIndex === i.instanceIndex
      )
    ));
    setSelectedItems(new Set());
    setSplitMode(false);
  };

  // Τίποτα να εισπραχθεί (π.χ. ήδη εισπραγμένο όλο το σύνολο): δεν στέλνουμε πληρωμή 0 €.
  const nothingToCollect = (amount: number) => {
    if (amount > MONEY_TOLERANCE) return false;
    showToast('error', t('dineIn:nothingToCollect', { defaultValue: 'Δεν υπάρχει ποσό προς είσπραξη.' }));
    return true;
  };

  // Πληρωμή χωρίς τερματικό για συγκεκριμένα τεμάχια: μετρητά ή «Επί πιστώσει» (6/10/2026).
  // ΙΔΙΑ ροή και για τα δύο — ίδιο ποσό (sliceAmount), ίδια τεμάχια, ίδιοι κανόνες στον server
  // (πρώτα παραστατικό, φρουρός διπλής πληρωμής, αυτόματο κλείσιμο)· αλλάζει μόνο ο τύπος που
  // καταγράφεται. Τα μετρητά στέλνουν ό,τι και πριν (χωρίς paymentMethod = μετρητά).
  const processCashPayment = async (items: PayableUnit[], method: 'cash' | 'credit' = 'cash') => {
    if (!store?.id || !tableDetails?.session || items.length === 0) return;

    const amount = paymentSlice(items);
    if (nothingToCollect(amount)) return;
    setProcessingPayment(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions/${tableDetails.session.id}/cash-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount,
          // Ομαδοποίηση ανά itemId με ΡΗΤΟ πλήθος: χωρίς αυτό ο server
          // χρεώνει ολόκληρη τη γραμμή ως πληρωμένη (βλ. utils/dineInMoney)
          items: aggregatePaymentItems(items),
          ...(method === 'credit' ? { paymentMethod: 'credit' } : {})
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        applyLocalPayment(items, method, amount);
        if (method === 'credit') {
          showToast('success', t('dineIn:creditPaymentRecorded', { defaultValue: 'Καταχωρήθηκε «Επί πιστώσει»' }));
        }

        // Refresh tableDetails so paidItems is up-to-date (fixes reopen bug)
        if (selectedTable) fetchTableDetails(selectedTable.id);

        const remainingCount = unassignedItems.length - items.length;
        if (remainingCount <= 0) {
          setTimeout(() => {
            setShowPaymentModal(false);
            setShowTableModal(false);
            fetchTables();
          }, 400);
        }
      } else {
        // Πρώτα το ελληνικό message (π.χ. 409 receipt_required από παλιό bundle
        // χωρίς F5) — το error είναι κωδικός μηχανής και δεν λέει τίποτα στον ταμία.
        showToast('error', result.message || result.error || t('dineIn:paymentProcessFailed'));
      }
    } catch (error) {
      console.error(`Failed to process ${method} payment:`, error);
      showToast('error', t('dineIn:paymentProcessFailed'));
    } finally {
      setProcessingPayment(false);
    }
  };

  // Όσο περιμένει το τερματικό, ο browser ρωτά πριν από F5 / κλείσιμο καρτέλας: με ανανέωση στη μέση χάνεται η
  // απάντηση της χρέωσης (GENERIS 25/10/2026, τραπέζι 21: F5 στα 108″, η κάρτα πέρασε χωρίς να το μάθει κανείς).
  useEffect(() => {
    if (cardPaymentStatus !== 'processing') return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [cardPaymentStatus]);

  // Κάρτα του τραπεζιού που τρέχει ακόμη από ΑΛΛΗ συσκευή (ή από πριν από ένα F5): το τραπέζι ξαναφορτώνεται ώσπου
  // να βγει αποτέλεσμα, ώστε η ένδειξη να γίνει μόνη της πληρωμή ή «αβέβαιη». Όχι όσο περιμένει αυτό εδώ το ταμείο.
  const cardDoubts = useMemo(() => cardDoubtsOf(tableDetails?.session), [tableDetails]);
  const pollCardInFlight = (showTableModal || showPaymentModal) && hasCardInFlight(cardDoubts)
    && cardPaymentStatus !== 'processing';
  useEffect(() => {
    if (!pollCardInFlight || !selectedTable) return;
    const timer = setTimeout(() => fetchTableDetails(selectedTable.id), CARD_DOUBT_POLL_MS);
    return () => clearTimeout(timer);
  }, [pollCardInFlight, selectedTable, tableDetails, fetchTableDetails]);

  // Process card / IRIS payment via POS terminal
  const processCardPayment = async (items: PayableUnit[], isIris: boolean = false) => {
    if (!store?.id || !tableDetails?.session || items.length === 0) return;

    if (!selectedTerminal) {
      showToast('error', t('dineIn:selectPOSTerminal'));
      return;
    }

    const amount = paymentSlice(items);
    if (nothingToCollect(amount)) return;
    setProcessingPayment(true);
    setCardPaymentStatus('processing');
    setCardPaymentMessage(isIris ? t('dineIn:waitingIRIS') : t('dineIn:waitingPOS'));

    try {
      const response = await fetch(`${API_BASE_URL}/api/dine-in/${store.id}/sessions/${tableDetails.session.id}/card-payment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount,
          // Ομαδοποίηση ανά itemId με ΡΗΤΟ πλήθος: χωρίς αυτό ο server
          // χρεώνει ολόκληρη τη γραμμή ως πληρωμένη (βλ. utils/dineInMoney)
          items: aggregatePaymentItems(items),
          terminalId: selectedTerminal.id,
          isIris
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setCardPaymentStatus('success');
        setCardPaymentMessage(t('dineIn:paymentApproved', { amount: formatMoney(amount) }));

        applyLocalPayment(items, isIris ? 'iris' : 'card', amount);

        // Refresh tableDetails so paidItems is up-to-date (fixes reopen bug)
        if (selectedTable) fetchTableDetails(selectedTable.id);

        const remainingCount = unassignedItems.length - items.length;
        setTimeout(() => {
          setCardPaymentStatus('idle');
          setCardPaymentMessage('');
          setProcessingPayment(false);

          if (remainingCount <= 0) {
            setShowPaymentModal(false);
            setShowTableModal(false);
            fetchTables();
          }
        }, 1500);
        return;
      } else {
        setCardPaymentStatus('failed');
        setCardPaymentMessage(result.message || result.error || t('dineIn:cardPaymentFailed'));
      }
    } catch (error) {
      console.error('Failed to process card payment:', error);
      setCardPaymentStatus('failed');
      setCardPaymentMessage(t('dineIn:posCommunicationFailed'));
    }
    // Φρέσκο τραπέζι: μια αβέβαιη χρέωση φαίνεται πλέον στο ίδιο το τραπέζι (session.card_doubt), όχι μόνο στο παράθυρο.
    if (selectedTable) fetchTableDetails(selectedTable.id);
    // For failed payments, keep overlay visible until dismissed
  };

  
  // Λογαριασμός τραπεζιού: τα ποσά από το dineInMoney (ίδια με την πληρωμή), ο server
  // τα συγκρίνει με το σύνολό του και τυπώνει στον εκτυπωτή της ζώνης.
  const printTableBill = async () => {
    const session = tableDetails?.session;
    if (!store?.id || !session) return;
    const bill = buildBillSummary(tableDetails.orders || [], session.total_amount, tableDetails.paidItems || []);
    if (bill.lines.length === 0) {
      showToast('warning', t('dineIn:billEmpty', { defaultValue: 'Το τραπέζι δεν έχει είδη για λογαριασμό.' }));
      return;
    }
    setPrintingBill(true);
    try {
      const res = await dineInApi.printBill(store.id, session.id, bill);
      showToast('success', t('dineIn:billPrinted', { defaultValue: 'Ο λογαριασμός τυπώνεται ({{printer}}).', printer: res.printer }));
    } catch (error) {
      if (error instanceof DineInApiError && error.code === 'bill_stale' && selectedTable) {
        fetchTableDetails(selectedTable.id);
      }
      showToast('error', error instanceof DineInApiError ? error.message : t('common:unknownError'));
    } finally {
      setPrintingBill(false);
    }
  };

  // Μήνυμα κουζίνας μόνο του («ΠΑΣΟ ΚΥΡΙΩΣ» …): μικρό δελτίο σε όλους τους εκτυπωτές κουζίνας.
  const kitchenPresets = kitchenMessagePresets(dineInSettings);
  const sendKitchenMessage = async (messages: string[]) => {
    const session = tableDetails?.session;
    if (!store?.id || !session || messages.length === 0) return;
    setSendingKitchenMessage(true);
    try {
      const res = await dineInApi.sendKitchenMessage(store.id, session.id, messages);
      showToast('success', t('dineIn:kitchenMessageSent', {
        defaultValue: 'Στάλθηκε στην κουζίνα: {{messages}}',
        messages: res.messages.join(' · ')
      }));
      setKitchenMessageMode(null);
    } catch (error) {
      showToast('error', error instanceof DineInApiError ? error.message : t('common:unknownError'));
    } finally {
      setSendingKitchenMessage(false);
    }
  };

  // Τραπέζι με σύνολο 0 € (όλα κερασμένα ή έκπτωση 100%): ούτε απόδειξη ούτε είσπραξη — κλείνει.
  // Πριν δεν υπήρχε κουμπί και ο ταμίας κατέληγε να κόψει απόδειξη με τις αρχικές τιμές (Π1, 5/10).
  const zeroTotalSession = !!tableDetails?.session
    && (tableDetails.orders || []).some((o: any) => o.status !== 'cancelled')
    && (parseFloat(String(tableDetails.session.total_amount ?? '0')) || 0) <= 0.005;
  const closeZeroTotalTable = async () => {
    const session = tableDetails?.session;
    if (!store?.id || !session) return;
    setClosingZeroTable(true);
    try {
      await dineInApi.closeSession(store.id, session.id);
      showToast('success', t('dineIn:zeroTableClosed', { defaultValue: 'Το τραπέζι έκλεισε (σύνολο 0,00 €).' }));
      setShowTableModal(false);
      fetchTables();
    } catch (error) {
      showToast('error', error instanceof DineInApiError ? error.message : t('common:unknownError'));
    } finally {
      setClosingZeroTable(false);
    }
  };

  // Κρατήσεις: η κεφαλίδα δείχνει όσους έρχονται· η κάτοψη «κρατάει» τραπέζι 2 ώρες πριν.
  const nowMinutes = athensMinutes(floorNow);
  const upcomingReservations = upcomingList(todayReservations, nowMinutes);
  const tableReservations = floorReservations(todayReservations, nowMinutes);
  // Σύνοψη της ζώνης που φαίνεται: μετρητές ανά κατάσταση + ανοιχτό υπόλοιπο.
  const reservedTableIds = new Set<number>(
    Array.from(tableReservations).filter(([, v]) => v.next).map(([id]) => id)
  );
  const floorSummary = summarizeFloor(tables, reservedTableIds);
  const guestsEnabled = dineInSettings?.guest_count_enabled === true;
  // Η κράτηση μπαίνει στο υπόμνημα μόνο όταν το κατάστημα έχει τη Sala — ή όταν υπάρχει
  // ήδη τέτοιο τραπέζι στη σάλα και πρέπει να εξηγηθεί.
  const floorChips = ([
    ['free', true],
    ['occupied', true],
    ['bill', true],
    ['reserved', reservationsOn]
  ] as Array<[FloorTone, boolean]>).filter(([tone, enabled]) => enabled || floorSummary.counts[tone] > 0);

  // Calculate order total
  const orderTotal = orderItems.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);

  // ── Επιλογή γραμμών καλαθιού → «Έκπτωση» / «Κέρασμα» (6/10/2026) ──────────────────────────
  // Μόνο με το δικαίωμα του χρήστη· χωρίς κανένα από τα δύο δεν υπάρχει καν επιλογή.
  const canDiscountLines = hasPermission('dine_in_apply_discount');
  const canTreatLines = hasPermission('dine_in_treat');
  const canSelectLines = canDiscountLines || canTreatLines;
  const selectedIndexList = Array.from(selectedLines).filter(i => i < orderItems.length).sort((a, b) => a - b);
  // Κατάσταση επιλογής: με ≥1 επιλεγμένη γραμμή το πάτημα επιλέγει/αποεπιλέγει αντί για αλλαγή είδους,
  // και κάθε γραμμή δείχνει το κουτάκι της. Αδειάζει η επιλογή → το πάτημα ξανά αλλάζει το είδος.
  const lineSelectionMode = canSelectLines && selectedIndexList.length > 0;
  const selectedBasketLines = selectedIndexList.map(i => orderItems[i]);
  const selectedHaveAdjustment = selectedBasketLines.some(hasCashierAdjustment);
  // Πόσο μειώνουν το σύνολο οι εκπτώσεις/κεράσματα του καλαθιού (για τη σύνοψη πάνω από το σύνολο).
  const basketReductionCents = orderItems.reduce((s, it) => s + lineReductionCents(it), 0);

  const clearLineSelection = () => setSelectedLines(new Set());
  const toggleLineSelection = (index: number) => {
    setSelectedLines(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };
  const selectAllLines = () => setSelectedLines(new Set(orderItems.map((_, i) => i)));
  // Τα κουμπιά ΜΕΣΑ στη γραμμή (−/+, ✕, πιάτο ↑/↓, κουτάκι) δεν φτάνουν στο πάτημα της γραμμής.
  const stopRowPress = (e: React.SyntheticEvent) => e.stopPropagation();

  // Αφαίρεση γραμμής (✕ ή «−» στο 1): οι θέσεις αλλάζουν, άρα και η επιλογή καθαρίζει.
  const removeBasketLine = (index: number) => {
    setOrderItems(prev => prev.filter((_, i) => i !== index));
    clearLineSelection();
  };
  const changeBasketQuantity = (index: number, delta: 1 | -1) => {
    const item = orderItems[index];
    if (!item) return;
    if (delta < 0 && item.quantity <= 1) {
      removeBasketLine(index);
      return;
    }
    setOrderItems(prev => prev.map((it, i) => (i === index ? { ...it, quantity: it.quantity + delta } : it)));
  };
  const moveBasketLineToCourse = (index: number, course: number) => {
    setOrderItems(prev => prev.map((it, i) => (i === index ? { ...it, course_number: course } : it)));
  };

  const applyBasketDiscount = (kind: BasketDiscountKind, value: number, reason: string) => {
    if (kind === 'percent') {
      setOrderItems(prev => applyPercentDiscount(prev, selectedIndexList, value, reason));
    } else {
      const result = applyAmountDiscount(orderItems, selectedIndexList, value, reason);
      setOrderItems(result.lines);
      if (result.split) {
        showToast('info', t('dineIn:lineActions.splitNotice', {
          defaultValue: 'Ένα τεμάχιο μπήκε σε χωριστή γραμμή, ώστε η έκπτωση να βγαίνει ακριβώς στο λεπτό.'
        }));
      }
    }
    clearLineSelection();
    setBasketAction(null);
  };
  const applyBasketTreat = (reason: string) => {
    setOrderItems(prev => applyTreat(prev, selectedIndexList, reason));
    clearLineSelection();
    setBasketAction(null);
  };
  const removeSelectedAdjustments = () => {
    setOrderItems(prev => removeAdjustments(prev, selectedIndexList));
    clearLineSelection();
  };
  // Η έκπτωση με την οποία ανοίγει το ProductCustomizeModal στην επεξεργασία γραμμής (κέρασμα = 0).
  const editedLineDiscountPercent = (line: DineInBasketLine | null | undefined): number =>
    !line || line.is_treat || line.full_unit_price == null ? 0 : Number(line.discount_percent) || 0;
  // Η γραμμή του καλαθιού όπως τη διαβάζουν τα βοηθητικά επεξεργασίας (utils/basketLineEdit:
  // dineInLineEditModalProps, dineInCustomizedLinePrices): original_unit_price = το ποσό ΠΡΙΝ από
  // προσφορά ή έκπτωση/κέρασμα ταμία (στο καλάθι η πλήρης τιμή του ταμία είναι το full_unit_price),
  // discount_percent = η έκπτωση με την οποία ανοίγει το modal (κέρασμα = 0). Αλλιώς γραμμή
  // ζυγιζόμενου/ανοιχτής τιμής με έκπτωση ή κέρασμα θα ξαναχτιζόταν από τη ΜΕΙΩΜΕΝΗ τιμή (ή από 0 €)
  // και το withLineAdjustment θα έβαζε την έκπτωση δεύτερη φορά.
  const basketLineEditView = (line: DineInBasketLine | null | undefined): DineInEditedLine | null =>
    line
      ? { ...line, original_unit_price: line.original_unit_price ?? line.full_unit_price ?? null, discount_percent: editedLineDiscountPercent(line) }
      : null;

  // Μία γραμμή του καλαθιού (με ή χωρίς ομαδοποίηση σε πιάτα): επιλογή, όνομα, ετικέτα
  // έκπτωσης/κεράσματος, αφαίρεση, ποσότητα, τιμή (η πλήρης διαγραμμένη όταν μειώθηκε).
  // Πάτημα στη γραμμή = αλλαγή του είδους (ProductCustomizeModal), όπως στο Take Away· κράτημα ή
  // δεξί κλικ = επιλογή για «Έκπτωση» / «Κέρασμα» (μόνο με δικαίωμα). Σε κατάσταση επιλογής το
  // πάτημα επιλέγει/αποεπιλέγει (απόφαση ιδιοκτήτη 6/10/2026).
  const renderBasketLine = (item: DineInBasketLine, index: number, courseNum?: number) => {
    const selected = selectedLines.has(index);
    const reductionCents = lineReductionCents(item);
    const adjusted = hasCashierAdjustment(item);
    const badge = item.is_treat
      ? t('dineIn:lineActions.treatBadge', { defaultValue: 'ΚΕΡΑΣΜΑ' })
      : adjusted && reductionCents > 0
        ? (item.discount_kind === 'amount'
          ? `−${formatEuroGr(reductionCents)}`
          : `−${formatPercentGr(item.discount_percent ?? effectivePercent(toCents(item.full_unit_price), toCents(item.unit_price)))}%`)
        : null;
    const selectLineLabel = `${t('dineIn:lineActions.selectLine', { defaultValue: 'Επιλογή γραμμής' })}: ${item.product_name}`;
    return (
      <PressableBasketLine
        key={index}
        testId={`basket-line-${index}`}
        label={lineSelectionMode
          ? selectLineLabel
          : `${t('dineIn:lineActions.editLine', { defaultValue: 'Αλλαγή είδους' })}: ${item.product_name}`}
        selected={selected}
        selectionMode={lineSelectionMode}
        onTap={lineSelectionMode ? () => toggleLineSelection(index) : () => { void handleEditOrderItem(index); }}
        onLongPress={canSelectLines ? () => toggleLineSelection(index) : undefined}
        className={`${courseNum !== undefined ? 'mb-2' : ''} ${isMobile ? 'p-3' : 'p-4'}`}
      >
        <div className={`flex justify-between items-start gap-2 ${isMobile ? 'mb-2' : 'mb-3'}`}>
          {lineSelectionMode && (
            <button
              type="button"
              role="checkbox"
              aria-checked={selected}
              aria-label={selectLineLabel}
              onClick={(e) => { e.stopPropagation(); toggleLineSelection(index); }}
              onKeyDown={stopRowPress}
              className={`shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${isMobile ? 'w-9 h-9' : 'w-7 h-7'} ${
                selected ? (v2 ? 'bg-brand border-brand text-brand-fg' : 'bg-blue-600 border-blue-600 text-white') : 'border-line-strong text-transparent hover:border-blue-500'
              }`}
            >
              <Check className={isMobile ? 'w-5 h-5' : 'w-4 h-4'} />
            </button>
          )}
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-base text-t1">{item.product_name}</h4>
            {item.size_name && sizeNameVisible(item) && (
              <p className={`text-sm font-medium ${v2 ? 'text-t3' : 'text-blue-600 dark:text-blue-400'}`}>{t('delivery:sizeLabel')}: {item.size_name}</p>
            )}
            {badge && (
              <p className="mt-1 flex items-center gap-1.5 flex-wrap">
                <span
                  data-testid="basket-line-badge"
                  className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold ${
                    item.is_treat
                      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                      : 'bg-purple-500/15 text-purple-700 dark:text-purple-300'
                  }`}
                >
                  {badge}
                </span>
                {item.adjust_reason && <span className="text-xs text-t3 truncate">{item.adjust_reason}</span>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {/* Move to previous course */}
            {courseNum !== undefined && courseNum > 1 && (
              <button
                onClick={(e) => { e.stopPropagation(); moveBasketLineToCourse(index, courseNum - 1); }}
                onKeyDown={stopRowPress}
                className={`p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded text-t3 hover:text-blue-600 dark:hover:text-blue-400 transition-colors ${isMobile ? 'hidden' : ''}`}
                title={t('dineIn:coursePrev')}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            )}
            {/* Move to next course */}
            {courseNum !== undefined && courseNum < 5 && (
              <button
                onClick={(e) => { e.stopPropagation(); moveBasketLineToCourse(index, courseNum + 1); }}
                onKeyDown={stopRowPress}
                className={`p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded text-t3 hover:text-blue-600 dark:hover:text-blue-400 transition-colors ${isMobile ? 'hidden' : ''}`}
                title={t('dineIn:courseNext')}
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); removeBasketLine(index); }}
              onKeyDown={stopRowPress}
              title={t('dineIn:removeProduct', { defaultValue: 'Αφαίρεση προϊόντος' })}
              className={`hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg text-t3 hover:text-red-600 dark:hover:text-red-400 transition-colors ${isMobile ? 'p-3' : 'p-1'}`}
            >
              <X className={isMobile ? 'w-5 h-5' : 'w-4 h-4'} />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 bg-surface-3 rounded-lg border border-line-strong">
            <button
              onClick={(e) => { e.stopPropagation(); changeBasketQuantity(index, -1); }}
              onKeyDown={stopRowPress}
              className={`flex items-center justify-center text-t2 hover:bg-surface-4 rounded-l-lg transition-colors ${isMobile ? 'w-12 h-12 text-xl' : 'w-8 h-8'}`}
            >
              -
            </button>
            <span className={`text-center font-semibold ${isMobile ? 'w-10 text-lg' : 'w-8'}`}>{item.quantity}</span>
            <button
              onClick={(e) => { e.stopPropagation(); changeBasketQuantity(index, 1); }}
              onKeyDown={stopRowPress}
              className={`flex items-center justify-center text-t2 hover:bg-surface-4 rounded-r-lg transition-colors ${isMobile ? 'w-12 h-12 text-xl' : 'w-8 h-8'}`}
            >
              +
            </button>
          </div>
          <span className="flex items-baseline gap-2">
            {reductionCents > 0 && (
              <span data-testid="basket-line-full-price" className="text-sm text-t4 line-through tabular-nums">
                {v2 ? formatEuro(lineFullCents(item) / 100) : `${formatMoney((lineFullCents(item) / 100))}`}
              </span>
            )}
            <span data-testid="basket-line-price" className="font-bold text-t1 tabular-nums">
              {v2 ? formatEuro(parseFloat(item.unit_price as any) * item.quantity) : `${formatMoney((parseFloat(item.unit_price as any) * item.quantity))}`}
            </span>
          </span>
        </div>
      </PressableBasketLine>
    );
  };

  // (Catalog filtering moved to <ProductCatalog>; Phase 4 refactor)

  // Μετά την έκδοση παραστατικού ο server κλειδώνει τη συνεδρία: νέοι γύροι,
  // έκπτωση, μεταφορά και κεράσματα απορρίπτονται. Κρύβουμε τα κουμπιά αντί να
  // αφήνουμε τον ταμία να τα πατάει και να τρώει σφάλμα.
  const sessionStatus = tableDetails?.session?.status;
  const sessionLocked = !!sessionStatus && sessionStatus !== 'open';
  const lockReason =
    sessionStatus === 'receipt_printed' ? t('dineIn:lockedReceipt', { defaultValue: 'Έχει εκδοθεί απόδειξη — επιτρέπεται μόνο πληρωμή.' }) :
    sessionStatus === 'invoice_printed' ? t('dineIn:lockedInvoice', { defaultValue: 'Έχει εκδοθεί τιμολόγιο — επιτρέπεται μόνο πληρωμή.' }) :
    sessionStatus === 'paid' ? t('dineIn:lockedPaid', { defaultValue: 'Το τραπέζι έχει εξοφληθεί.' }) :
    null;

  return (
    <div className="cashier-dinein h-full flex flex-col bg-surface-1">
      {/* Header */}
      <div className={v2
        ? 'px-5 pt-5 pb-3 bg-surface-1 flex items-center justify-between gap-3 flex-wrap'
        : 'cashier-floor-toolbar bg-surface-2 border-b border-line-strong px-4 py-3 flex items-center justify-between shadow-sm'}>
        <div className="flex items-center gap-4 min-w-0">
          {v2 ? (
            <h1 className="text-2xl font-bold text-t1 leading-tight">{t('dineIn:restaurant')}</h1>
          ) : (
          <h1 className="text-xl font-bold text-t1 flex items-center gap-2">
            <Utensils className="w-5 h-5 text-amber-600" />
            {t('dineIn:restaurant')}
          </h1>
          )}

          {/* Zone tabs */}
          <div className={v2 ? 'flex items-center gap-2 flex-wrap' : 'cashier-zones flex items-center gap-1 bg-surface-3 rounded-lg p-1'}>
            {zones.map(zone => (
              <button
                key={zone.id}
                onClick={() => setSelectedZone(zone)}
                aria-pressed={selectedZone?.id === zone.id}
                className={v2
                  ? `h-10 px-4 rounded-lg border text-sm font-medium transition-colors active:scale-[0.98] ${
                    selectedZone?.id === zone.id
                      ? 'bg-brand border-brand text-brand-fg'
                      : 'bg-surface-2 border-line text-t2 hover:border-line-strong hover:text-t1'
                  }`
                  : `cashier-choice px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  selectedZone?.id === zone.id
                    ? 'bg-surface-1 text-t1 shadow-sm'
                    : 'text-t3 hover:text-t1'
                }`}
              >
                {zone.name}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Υπόμνημα και σύνοψη μαζί: κάθε κατάσταση με το πλήθος της */}
          <div className="hidden md:flex flex-wrap items-center gap-1.5 text-xs font-medium text-t2">
            {floorChips.map(([tone]) => (
              <span key={tone} className={v2
                ? 'flex items-center gap-1.5 px-2.5 h-8 rounded-full bg-surface-2 border border-line whitespace-nowrap'
                : 'cashier-floor-chip flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-3 border border-line whitespace-nowrap'}>
                <span className={`w-2.5 h-2.5 rounded-full ${v2 ? TONE_DOT_V2[tone] : TONE_DOT[tone]}`}></span>
                {t(`dineIn:${TONE_LABEL[tone]}`)}
                <span className="font-bold text-t1 tabular-nums">{floorSummary.counts[tone]}</span>
              </span>
            ))}
            {floorSummary.openBalance > 0 && (
              <span className={v2
                ? 'hidden xl:flex items-center gap-1.5 px-2.5 h-8 rounded-full bg-surface-2 border border-line whitespace-nowrap'
                : 'cashier-floor-chip hidden xl:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-3 border border-line whitespace-nowrap'}>
                {t('dineIn:openBalance')}
                <span className="font-bold text-t1 tabular-nums">{formatTableAmount(floorSummary.openBalance, look)}</span>
              </span>
            )}
          </div>
          
          {/* Upcoming reservations widget */}
          {reservationsOn && upcomingReservations.length > 0 && (
            <div className={v2
              ? 'hidden lg:flex items-center gap-2 px-3 h-10 bg-surface-2 rounded-lg border border-line'
              : 'hidden lg:flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-300 dark:border-blue-700/50'}>
              <CalendarDays className={`w-4 h-4 ${v2 ? 'text-t3' : 'text-blue-600'}`} />
              <div className="flex items-center gap-3">
                {upcomingReservations.slice(0, 3).map((res, idx) => (
                  <div key={res.id} className="flex items-center gap-1 text-sm">
                    <span className={`font-medium ${v2 ? 'text-t1 tabular-nums' : 'text-blue-700'}`}>{res.reservation_time.slice(0, 5)}</span>
                    <span className={v2 ? 'text-t2' : 'text-blue-600'}>{res.customer_name.split(' ')[0]}</span>
                    <span className={v2 ? 'text-t3' : 'text-blue-600 dark:text-blue-400'}>({res.party_size})</span>
                    {idx < Math.min(upcomingReservations.length - 1, 2) && <span className={`mx-1 ${v2 ? 'text-t4' : 'text-blue-700 dark:text-blue-300'}`}>•</span>}
                  </div>
                ))}
                {upcomingReservations.length > 3 && (
                  <span className={`text-xs ${v2 ? 'text-t3' : 'text-blue-500'}`}>{t('dineIn:moreReservations', { count: upcomingReservations.length - 3 })}</span>
                )}
              </div>
            </div>
          )}
          
          {reservationsOn && (
            <button
              type="button"
              onClick={() => setShowReservations(true)}
              className={v2
                ? 'flex items-center gap-2 px-3 h-10 rounded-lg border border-line bg-surface-2 text-t2 text-sm font-medium hover:bg-surface-3 transition-colors'
                : 'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50'}
            >
              <CalendarDays className={v2 ? 'w-4 h-4 text-t3' : 'w-5 h-5'} />
              <span className="hidden sm:inline">{t('dineIn:reservations')}</span>
              {upcomingReservations.length > 0 && (
                <span className={v2
                  ? 'min-w-[1.25rem] h-5 px-1 rounded-full bg-t1 text-surface-2 text-xs font-bold leading-5 text-center'
                  : 'text-xs px-1.5 py-0.5 rounded-full bg-blue-600 text-white'}>{upcomingReservations.length}</span>
              )}
            </button>
          )}
          
          {/* Employee Table Assignments Button */}
          <button
            onClick={() => { setShowAssignmentsModal(true); fetchWaiters(); }}
            className={v2
              ? 'flex items-center gap-2 px-3 h-10 rounded-lg border border-line bg-surface-2 text-t2 text-sm font-medium hover:bg-surface-3 transition-colors'
              : 'flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-3 text-t2 hover:bg-surface-4 transition-colors'}
          >
            <Users className={v2 ? 'w-4 h-4 text-t3' : 'w-4 h-4'} />
            <span className="hidden sm:inline">{t('dineIn:assign')}</span>
          </button>

          {/* Session History Button */}
          <button
            onClick={() => {
              setShowSessionHistory(true);
              fetchSessionHistory();
            }}
            className={v2
              ? 'flex items-center gap-2 px-3 h-10 rounded-lg border border-line bg-surface-2 text-t2 text-sm font-medium hover:bg-surface-3 transition-colors'
              : 'flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-3 text-t2 hover:bg-surface-4 transition-colors'}
          >
            <Clock className={v2 ? 'w-4 h-4 text-t3' : 'w-5 h-5'} />
            <span className="hidden sm:inline">{t('dineIn:history')}</span>
          </button>
          
          <button
            onClick={() => {
              setRefreshing(true);
              fetchTables();
            }}
            className={v2 ? 'w-10 h-10 flex items-center justify-center rounded-lg border border-line bg-surface-2 hover:bg-surface-3' : 'p-2 hover:bg-surface-3 rounded-lg'}
            disabled={refreshing}
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin text-blue-600 dark:text-blue-400' : 'text-t3'}`} />
          </button>
        </div>
      </div>

      {/* Floor Plan */}
      <div className={v2 ? 'flex-1 px-5 pb-5 pt-2 overflow-auto' : 'cashier-floor-container flex-1 p-4 overflow-auto'}>
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600"></div>
          </div>
        ) : zones.length === 0 ? (
          <div className="h-full flex items-center justify-center text-t3">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">{t('dineIn:noZones')}</p>
              <p className="text-sm">{t('dineIn:noZonesHint')}</p>
            </div>
          </div>
        ) : (
          // Ίδιος λογικός καμβάς με τον editor, κλιμακωμένος στην οθόνη του ταμείου:
          // η σάλα δείχνει όπως σχεδιάστηκε, όποιο κι αν είναι το μέγεθος της οθόνης.
          <FloorCanvas
            canvas={zoneCanvas(selectedZone?.layout)}
            // Με σχέδιο από τη Sala οι τοίχοι είναι το περίγραμμα· αλλιώς το παλιό πλαίσιο με πλέγμα.
            className={`${v2 ? '' : 'cashier-floor-plan'} ${hasRoom(selectedZone?.layout) ? '' : 'bg-surface-2 rounded-xl border border-line'}`}
            bottomGap={16}
            minHeight={360}
            overlay={tables.length === 0 && (
              <div className="text-center text-t4">
                <Utensils className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">{t('dineIn:noTablesInZone')}</p>
              </div>
            )}
          >
            {hasRoom(selectedZone?.layout) ? (
              <ZoneRoom layout={selectedZone?.layout} canvas={zoneCanvas(selectedZone?.layout)} />
            ) : (
              <div
                className="absolute inset-0 opacity-5 pointer-events-none rounded-xl"
                style={{
                  backgroundImage: 'linear-gradient(to right, #000 1px, transparent 1px), linear-gradient(to bottom, #000 1px, transparent 1px)',
                  backgroundSize: '10% 10%'
                }}
              />
            )}
            
            {/* Tables with absolute positioning */}
            {tables.map(table => {
              // Stored width/height are in "size units" (5..25) — ίδια σταθερά με τον editor κάτοψης.
              const widthPx = (Number(table.width) || 10) * SIZE_UNIT_PX;
              const heightPx = (Number(table.height) || 10) * SIZE_UNIT_PX;

              const tableRes = tableReservations.get(table.id);
              const tableReservation = tableRes?.next;
              const tone = floorTone(table.status, !!tableReservation);
              const hasSession = table.current_session_id != null;
              // Η παρέα της κράτησης που κάθεται: VIP και αλλεργίες φαίνονται από την κάτοψη.
              const seatedReservation = hasSession ? tableRes?.seated : undefined;
              const seatedAllergies = seatedReservation ? allergiesOf(seatedReservation) : '';
              // Νέα εμφάνιση: ο χρόνος μετρά από τον τελευταίο γύρο (ή την απόδειξη) και παίρνει χρώμα
              // μόνο μετά από όριο· κλασική: πόση ώρα είναι ανοιχτό, όπως πάντα.
              const attention = v2 && hasSession ? tableAttention(table, floorNow) : null;
              const elapsed = hasSession ? (attention ? attention.elapsed : formatElapsed(table.session_opened_at, floorNow)) : '';
              const elapsedLabel = attention && elapsed
                ? [
                    t(`dineIn:v2.since.${attention.basis}`, { time: elapsed }),
                    attention.level !== 'none' && t(`dineIn:v2.attention.${attention.level}`),
                  ].filter(Boolean).join(' · ')
                : elapsed;
              const amount = hasSession ? formatTableAmount(table.session_total, look) : '';
              // Ένα τραπέζι 60px χωρά μία πληροφορία· τα άτομα μπαίνουν μόνο όπου περισσεύει πλάτος.
              const roomy = widthPx >= 84;
              const guests = guestsEnabled && roomy && hasSession && table.guest_count
                ? `${table.guest_count}/${table.capacity} · `
                : '';

              let info: React.ReactNode = null;
              if (tone === 'occupied' || tone === 'bill') {
                info = amount && (
                  <>
                    {tone === 'bill' && roomy && <Receipt className="w-3 h-3 shrink-0" />}
                    <span className="truncate">{guests}{amount}</span>
                  </>
                );
              } else if (tone === 'reserved') {
                info = (
                  <>
                    <CalendarDays className="w-3 h-3 shrink-0" />
                    {tableReservation && <span>{tableReservation.reservation_time.slice(0, 5)}</span>}
                  </>
                );
              } else if (guestsEnabled) {
                info = (
                  <>
                    <Users className="w-3 h-3 shrink-0" />
                    <span>{table.capacity}</span>
                  </>
                );
              }

              return (
                <button
                  key={table.id}
                  onClick={() => handleTableClick(table)}
                  data-status={tableReservation ? 'reserved' : table.status}
                  data-tone={tone}
                  aria-label={[
                    t('dineIn:tableNumber', { number: table.table_number }),
                    t(`dineIn:${TONE_LABEL[tone]}`),
                    seatedReservation && seatedReservation.customer_name,
                    seatedAllergies && t('dineIn:sala.allergies', { text: seatedAllergies }),
                    elapsedLabel,
                    amount
                  ].filter(Boolean).join(', ')}
                  className={`${v2 ? 'xv2-table' : 'cashier-table'} absolute flex flex-col items-center justify-center border-2 transition-all transform hover:scale-105 hover:z-10 ${v2 ? TONE_TILE_V2[tone] : TONE_TILE[tone]} ${
                    table.shape === 'round' ? 'rounded-full' : 'rounded-xl'
                  }`}
                  style={{
                    left: `${table.position_x}%`,
                    top: `${table.position_y}%`,
                    width: `${widthPx}px`,
                    height: `${heightPx}px`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  {/* Πόση ώρα είναι ανοιχτό — αυτό που κοιτάζει πρώτο ο υπεύθυνος σάλας */}
                  {elapsed && (
                    <div
                      className={`cashier-table-time absolute -top-2.5 -right-2 px-1.5 py-px rounded-full border text-[11px] font-semibold tabular-nums leading-tight ${
                        attention ? `inline-flex items-center gap-0.5 ${ATTENTION_BADGE_V2[attention.level]}` : 'bg-surface-0 border-line-strong text-t1'
                      }`}
                      data-attention={attention?.level}
                      title={attention ? elapsedLabel : undefined}
                    >
                      {attention?.basis === 'receipt' && <Receipt className="w-2.5 h-2.5 shrink-0" />}
                      {elapsed}
                    </div>
                  )}
                  {(seatedReservation?.guest_is_vip || seatedAllergies) && (
                    <div
                      className="absolute -top-2.5 -left-2 flex items-center gap-0.5 px-1 py-0.5 rounded-full bg-surface-0 border border-line-strong"
                      title={[seatedReservation?.guest_is_vip ? 'VIP' : '', seatedAllergies && t('dineIn:sala.allergies', { text: seatedAllergies })].filter(Boolean).join(' · ')}
                    >
                      {seatedReservation?.guest_is_vip && <Star className="w-3 h-3 text-amber-500 fill-amber-400" />}
                      {seatedAllergies && <AlertTriangle className="w-3 h-3 text-red-600 dark:text-red-400" />}
                    </div>
                  )}

                  <div className="text-lg font-bold leading-tight">{table.table_number}</div>
                  {info && heightPx >= 48 && (
                    <div className="flex items-center justify-center gap-0.5 max-w-full px-1 text-[11px] font-medium leading-tight opacity-85">
                      {info}
                    </div>
                  )}
                </button>
              );
            })}
            
          </FloorCanvas>
        )}
      </div>

      {/* Λεπτομέρειες τραπεζιού — ΠΑΝΤΑ πλήρης οθόνη, για τον ίδιο λόγο με τη νέα
          παραγγελία: το h-[80vh] δεν λογαριάζει το zoom της ρίζας και στα μικρά ταμεία
          το παράθυρο έπιανε ~60% του ύψους. */}
      {showReservations && store?.id && (
        <ReservationsScreen storeId={store.id} onClose={() => setShowReservations(false)} onOpenTable={openTableById} />
      )}

      {showTableModal && selectedTable && tableDetails && (
        <TableScreen
          table={selectedTable}
          reservation={tableReservations.get(selectedTable.id)?.seated ?? tableReservations.get(selectedTable.id)?.next ?? null}
          details={tableDetails}
          coursesEnabled={dineInSettings?.course_management_enabled === true}
          guestsEnabled={guestsEnabled}
          billPrintEnabled={dineInSettings?.bill_print_enabled === true}
          kitchenMessagesEnabled={!!kitchenPresets}
          can={hasPermission}
          sessionLocked={sessionLocked}
          lockReason={lockReason}
          zeroTotal={zeroTotalSession}
          busy={{ cancel: cancelLoading, bill: printingBill, receipt: generatingReceipt && receiptType === 'receipt', closeZero: closingZeroTable }}
          sizeNameVisible={sizeNameVisible}
          onClose={() => setShowTableModal(false)}
          onNewOrder={() => { setOrderItems([]); setShowOrderModal(true); }}
          onKitchenMessage={() => setKitchenMessageMode('send')}
          onCorrection={() => { setCorrectionReason(NO_STAFF_REASON); setShowCorrectionModal(true); }}
          onMove={() => { setMoveMode('session'); setSelectedItemsToMove([]); setShowMoveModal(true); }}
          onDiscount={() => { setSessionDiscountReason(NO_STAFF_REASON); setShowDiscountModal(true); }}
          onTreat={() => { setTreatReason(NO_STAFF_REASON); setShowTreatModal(true); }}
          onCancel={cancelSession}
          onPrintBill={printTableBill}
          onCloseZero={closeZeroTotalTable}
          onIssueReceipt={() => { setReceiptType('receipt'); generateReceiptDirect(); }}
          onIssueInvoice={() => { setReceiptType('invoice'); setShowReceiptModal(true); }}
          onPayment={openPaymentModal}
          onMarkServed={markOrderServed}
        />
      )}

      {/* Παραγγελία τραπεζιού — ΠΑΝΤΑ πλήρης οθόνη. Με w-[85vw]/h-[90vh] έβγαινε μικρό
          στα ταμεία: το index.html βάζει zoom στη ρίζα (π.χ. 0,75 σε 1024×768) και τα
          vw/vh δεν το λογαριάζουν, οπότε το «85%» γινόταν ~64% της οθόνης. */}
      {showOrderModal && selectedTable && (
        <div className="cashier-overlay fixed inset-0 bg-black/60 flex z-50 p-0">
          <div className="cashier-dialog bg-surface-1 w-full h-full overflow-hidden flex flex-col" style={{ borderRadius: 0 }}>
            {/* Header */}
            <div className={v2 ? 'px-5 py-3 bg-surface-2 border-b border-line' : 'cashier-dialog-header px-6 py-4 bg-surface-2 border-b border-line-strong'}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-4 min-w-0">
                  {v2 ? (
                    <div className="w-12 h-12 shrink-0 rounded-xl bg-brand-soft text-brand flex items-center justify-center text-lg font-bold tabular-nums">
                      {selectedTable.table_number}
                    </div>
                  ) : (
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <Utensils className="w-6 h-6 text-white dark:text-t1" />
                  </div>
                  )}
                  <div>
                    <h2 className="text-xl font-bold text-t1">
                      {t('dineIn:newOrderForTable', { number: selectedTable.table_number })}
                    </h2>
                    {!tableDetails?.session && guestsEnabled && (
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-t3 text-sm">{t('dineIn:guestsLabel')}</span>
                        <div className="flex items-center gap-1 bg-surface-3 rounded-xl p-1">
                          <button
                            onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                            className={`rounded-lg ${v2 ? 'bg-surface-2 border border-line hover:bg-surface-4' : 'bg-surface-4 hover:bg-slate-400 shadow-sm'} flex items-center justify-center font-bold transition-colors text-t1 ${isMobile ? 'w-12 h-12 text-2xl' : 'w-8 h-8 text-lg'}`}
                          >
                            -
                          </button>
                          <span className="w-10 text-center text-lg font-bold text-t1">{guestCount}</span>
                          <button
                            onClick={() => setGuestCount(guestCount + 1)}
                            className={`rounded-lg ${v2 ? 'bg-surface-2 border border-line hover:bg-surface-4' : 'bg-surface-4 hover:bg-slate-400 shadow-sm'} flex items-center justify-center font-bold transition-colors text-t1 ${isMobile ? 'w-12 h-12 text-2xl' : 'w-8 h-8 text-lg'}`}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Νέα εμφάνιση: αναζήτηση πάντα ανοιχτή στην κεφαλίδα (όπως στο Take Away) */}
                {v2 && (
                  <div className="flex-1 flex justify-end min-w-0">
                    <div className="relative w-full max-w-sm">
                      <Search aria-hidden="true" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-t3 h-5 w-5" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('dineIn:v2.searchPlaceholder', { defaultValue: 'Αναζήτηση προϊόντος…' })}
                        className="w-full h-11 pl-11 pr-10 rounded-xl bg-surface-1 border border-line text-base text-t1 placeholder:text-t3 focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => setSearchTerm('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-t3 hover:text-t1 hover:bg-surface-3"
                          aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <button
                  onClick={() => setShowOrderModal(false)}
                  aria-label={t('common:close', { defaultValue: 'Κλείσιμο' })}
                  className={`hover:bg-surface-3 rounded-xl transition-colors shrink-0 ${isMobile ? 'p-3' : 'p-2'}`}
                >
                  <X className="w-6 h-6 text-t3" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 flex overflow-hidden min-h-0 relative">
              {/* Left - Categories & Products (shared component, parity with TakeAway/Delivery) */}
              <div className={`flex-1 min-w-0 flex flex-col bg-surface-1 overflow-hidden ${v2 ? 'pt-4' : ''}`}>
                {/* Top bar: back button + breadcrumb + search */}
                {!v2 && (
                <div className="cashier-toolbar bg-surface-2 border-b border-line-strong px-4 py-2 flex items-center gap-3">
                  {currentView !== 'categories' && (
                    <>
                      <button
                        onClick={handleCatalogBack}
                        className={`bg-surface-3 hover:bg-surface-4 rounded-lg transition-all active:scale-95 text-t2 ${isMobile ? 'p-3' : 'p-2'}`}
                        title={t('common:back', { defaultValue: 'Πίσω' })}
                      >
                        <ChevronUp className="h-5 w-5 -rotate-90" />
                      </button>
                      <button
                        onClick={handleCatalogHome}
                        className={`bg-surface-3 hover:bg-surface-4 rounded-lg transition-all active:scale-95 text-t2 ${isMobile ? 'p-3' : 'p-2'}`}
                        title={t('common:home', { defaultValue: 'Αρχή' })}
                      >
                        <Home className="h-5 w-5" />
                      </button>
                    </>
                  )}
                  {!searchOpen && (
                    <h3 className="text-base font-bold text-t1 flex-shrink-0">
                      {currentView === 'categories' && t('dineIn:categories', { defaultValue: 'Κατηγορίες' })}
                      {currentView === 'subcategories' && getSelectedCategoryObj()?.name}
                      {currentView === 'products' && (selectedSubcategory
                        ? getSelectedCategoryObj()?.subcategories?.find(s => s.id === selectedSubcategory)?.name
                        : getSelectedCategoryObj()?.name)}
                    </h3>
                  )}
                  {!searchOpen && <div className="flex-1" />}
                  {searchOpen ? (
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-t3 h-4 w-4" />
                      <input
                        type="text"
                        autoFocus
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('common:search', { defaultValue: 'Αναζήτηση' })}
                        className={`w-full pl-9 pr-9 rounded-lg bg-surface-3 text-t1 border border-line-strong placeholder-t3 focus:outline-none focus:ring-2 focus:ring-blue-500 ${isMobile ? 'py-3 text-base' : 'py-1.5 text-sm'}`}
                      />
                      <button
                        onClick={() => { setSearchTerm(''); setSearchOpen(false); }}
                        className={`absolute right-2 top-1/2 transform -translate-y-1/2 text-t3 hover:text-t1 ${isMobile ? 'p-2' : 'p-1'}`}
                        title={t('common:close', { defaultValue: 'Κλείσιμο' })}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSearchOpen(true)}
                      className={`bg-surface-3 hover:bg-surface-4 rounded-lg transition-all active:scale-95 text-t2 ${isMobile ? 'p-3' : 'p-2'}`}
                      title={t('common:search', { defaultValue: 'Αναζήτηση' })}
                    >
                      <Search className="h-5 w-5" />
                    </button>
                  )}
                </div>
                )}

                <ProductCatalog
                  categories={dailyCatalog.categories}
                  products={dailyCatalog.products}
                  loading={loading}
                  currentView={currentView}
                  selectedCategory={selectedCategory}
                  selectedSubcategory={selectedSubcategory}
                  searchTerm={searchTerm}
                  categoryColumns={isMobile ? 2 : categoryColumns}
                  productColumns={isMobile ? 2 : productColumns}
                  onCategorySelect={handleCategorySelect}
                  onSubcategorySelect={handleSubcategorySelect}
                  onProductClick={handleProductClick}
                  onProductMouseDown={handleMouseDown}
                  onProductMouseUp={handleMouseUp}
                  getProductPrice={p => productDisplayPrice(p, 'dine_in')}
                  translationNamespace="dineIn"
                  look={v2 ? catalogLookOf(catalogPrefs) : 'classic'}
                  onBack={v2 ? handleCatalogBack : undefined}
                  onHome={v2 ? handleCatalogHome : undefined}
                  onAllProductsSelect={handleCatalogAll}
                />

                {/* Mobile: μπάρα καλαθιού — το sidebar κρύβεται σε μικρές οθόνες */}
                <button
                  onClick={() => setMobileCartOpen(true)}
                  className={`${v2 ? 'bg-brand text-brand-fg' : 'cashier-primary bg-blue-600 text-white shadow-lg'} md:hidden m-3 flex items-center justify-between px-4 py-3 rounded-xl active:scale-[0.98] transition-transform`}
                >
                  <span className="flex items-center gap-2 font-semibold">
                    <ShoppingCart className="w-5 h-5" />
                    {t('dineIn:cart')} ({orderItems.length})
                  </span>
                  <span className="font-bold tabular-nums">{v2 ? formatEuro(orderTotal) : `${formatMoney(orderTotal)}`}</span>
                </button>
              </div>

              {/* Right - Order summary (mobile: full-screen overlay πάνω από τον κατάλογο) */}
              <div className={`${v2 ? 'xv2-basket bg-surface-2 border-l border-line md:border-t-[3px] md:border-t-brand' : 'cashier-basket bg-surface-1 border-l border-line-strong'} ${mobileCartOpen ? 'flex absolute inset-0 z-10' : 'hidden'} md:flex md:static w-full md:w-80 lg:w-96 flex-shrink-0 flex-col`}>
                <div className={v2 ? 'px-5 pt-5 pb-4 border-b border-line bg-surface-2' : 'p-4 border-b border-line-strong bg-surface-2'}>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setMobileCartOpen(false)}
                      className="md:hidden p-3 bg-surface-3 rounded-lg text-t2"
                    >
                      <ChevronUp className="h-5 w-5 -rotate-90" />
                    </button>
                    {!v2 && (
                    <div className="cashier-basket-icon w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5 text-white dark:text-t1" />
                    </div>
                    )}
                    <div>
                      <h3 className={v2 ? 'text-xl font-bold text-t1 leading-tight' : 'text-lg font-bold text-t1'}>
                        {v2 ? t('dineIn:v2.order', { defaultValue: 'Η παραγγελία' }) : t('dineIn:cart')}
                      </h3>
                      <p className="text-sm text-t3">{t('dineIn:basketProductsCount', { count: orderItems.length })}</p>
                    </div>
                  </div>
                </div>

                {/* Course Quick-Select Bar — visible only when course_management_enabled and cart has items */}
                {dineInSettings?.course_management_enabled && orderItems.length > 0 && (
                  <div className={`px-3 py-2 border-b flex gap-1.5 overflow-x-auto ${v2 ? 'border-line bg-surface-2' : 'border-line-strong bg-surface-1'}`}>
                    {[t('products:courses.first'), t('products:courses.second'), t('products:courses.third'), t('products:courses.fourth'), t('products:courses.fifth')].map((label, idx) => {
                      const courseNum = idx + 1;
                      const hasItems = orderItems.some(i => i.course_number === courseNum);
                      return (
                        <button
                          key={courseNum}
                          onClick={() => setCurrentCourse(courseNum)}
                          className={`relative shrink-0 rounded-lg font-semibold transition-colors ${isMobile ? 'px-4 py-2.5 text-sm' : 'px-3 py-1.5 text-xs'} ${
                            courseNum === currentCourse
                              ? (v2 ? 'bg-brand text-brand-fg' : 'bg-blue-600 text-white')
                              : (v2 ? 'bg-surface-1 border border-line text-t2 hover:text-t1' : 'bg-surface-2 text-t3 hover:bg-surface-3 hover:text-t1')
                          }`}
                        >
                          {label}
                          {/* Dot indicator when course has items but is not active */}
                          {hasItems && courseNum !== currentCourse && (
                            <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${v2 ? 'bg-brand' : 'bg-blue-400'}`} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="flex-1 overflow-y-auto p-4">
                  {/* Promotions banner — shows applicable promotions for the current cart */}
                  {/* Γραμμές με κέρασμα/έκπτωση ταμία δεν μετρούν σε προσφορά (δεν προστίθενται). */}
                  {orderItems.length > 0 && store?.id && (
                    <PromotionBanner
                      storeId={store.id}
                      items={orderItems.filter(i => !hasCashierAdjustment(i)).map(i => ({
                        product_id: i.product_id,
                        quantity: i.quantity,
                        unit_price: i.original_unit_price ?? i.unit_price
                      }))}
                      appliedPromotionId={appliedPromotionId}
                      onApply={applyPromotion}
                      onRemove={removePromotion}
                    />
                  )}
                  {/* Επιλογή γραμμών για «Έκπτωση» / «Κέρασμα». ΜΙΑ σειρά, ίδιου ύψους και στις δύο
                      καταστάσεις (οι γραμμές δεν πηδούν κάτω από το δάχτυλο όταν «πιάνει» το κράτημα, και
                      το καλάθι δεν χάνει ύψος): χωρίς επιλογή «Επιλογή όλων» + σύντομη υπόδειξη, με επιλογή
                      «Επιλογή όλων» + «Καμία». */}
                  {canSelectLines && orderItems.length > 0 && (
                    <div className="flex items-center gap-2 mb-3 min-w-0" data-testid="basket-selection-bar">
                      <button
                        type="button"
                        onClick={selectAllLines}
                        className={`shrink-0 rounded-lg bg-surface-2 border border-line-strong text-t1 text-sm font-semibold hover:border-blue-500 active:scale-95 transition-all ${isMobile ? 'h-11 px-4' : 'h-9 px-3'}`}
                      >
                        {t('dineIn:lineActions.selectAll', { defaultValue: 'Επιλογή όλων' })}
                      </button>
                      {lineSelectionMode ? (
                        <button
                          type="button"
                          onClick={clearLineSelection}
                          className={`shrink-0 rounded-lg bg-surface-2 border border-line-strong text-t1 text-sm font-semibold hover:border-blue-500 active:scale-95 transition-all ${isMobile ? 'h-11 px-4' : 'h-9 px-3'}`}
                        >
                          {t('dineIn:lineActions.selectNone', { defaultValue: 'Καμία' })}
                        </button>
                      ) : (
                        <span data-testid="basket-press-hint" className="min-w-0 truncate text-xs text-t3">
                          {t('dineIn:lineActions.pressHint', { defaultValue: 'Πάτα = αλλαγή · κράτα = επιλογή' })}
                        </span>
                      )}
                    </div>
                  )}
                  {orderItems.length === 0 ? (
                    <div className="text-center py-12 text-t4">
                      <div className="w-20 h-20 bg-surface-2 rounded-2xl flex items-center justify-center mx-auto mb-4">
                        <ShoppingCart className="w-10 h-10 text-t4" />
                      </div>
                      <p className="font-medium text-t3">{t('dineIn:emptyCart')}</p>
                      <p className="text-sm mt-1">{t('dineIn:emptyCartHint')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Group items by course if course management is enabled */}
                      {dineInSettings?.course_management_enabled ? (
                        <>
                          {[1, 2, 3, 4, 5].map(courseNum => {
                            const courseItems = orderItems.filter(item => item.course_number === courseNum);
                            const courseLabels = [t('products:courses.first'), t('products:courses.second'), t('products:courses.third'), t('products:courses.fourth'), t('products:courses.fifth')];
                            if (courseItems.length === 0 && courseNum !== currentCourse) return null;
                            
                            return (
                              <div key={courseNum}>
                                {/* Course Header */}
                                <div className="flex items-center gap-2 mb-2">
                                  <div className={`flex-1 h-px ${v2 ? 'bg-line' : courseNum <= currentCourse ? 'bg-blue-500' : 'bg-surface-3'}`} />
                                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                                    v2
                                      ? (courseNum === currentCourse ? 'bg-brand-soft text-brand' : 'bg-surface-3 text-t3')
                                      : courseNum === currentCourse
                                      ? 'bg-blue-600 text-white'
                                      : courseNum < currentCourse
                                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                                        : 'bg-surface-3 text-t3'
                                  }`}>
                                    {courseLabels[courseNum - 1]}
                                  </span>
                                  <div className={`flex-1 h-px ${v2 ? 'bg-line' : courseNum <= currentCourse ? 'bg-blue-500' : 'bg-surface-3'}`} />
                                </div>
                                
                                {/* Course Items */}
                                {courseItems.map((item) => renderBasketLine(item, orderItems.indexOf(item), courseNum))}
                                
                                {/* Empty course placeholder */}
                                {courseItems.length === 0 && courseNum === currentCourse && (
                                  <div className="text-center py-4 text-t4 text-sm border-2 border-dashed border-line-strong rounded-xl mb-2">
                                    {t('dineIn:addProductsToCourse', { course: courseLabels[courseNum - 1] })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          
                          {/* Next Course Button — σε κινητό κρύβεται: τα course
                              tabs στην κορυφή του καλαθιού κάνουν την ίδια
                              δουλειά χωρίς να φουσκώνουν τη λίστα */}
                          {currentCourse < 5 && orderItems.some(item => item.course_number === currentCourse) && (
                            <button
                              onClick={() => setCurrentCourse(currentCourse + 1)}
                              className={`w-full py-2 mt-2 border-2 border-dashed rounded-xl font-medium transition-colors hidden md:flex items-center justify-center gap-2 ${
                                v2 ? 'border-line-strong text-t2 hover:bg-surface-3' : 'border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/20'
                              }`}
                            >
                              <Plus className="w-4 h-4" />
                              {t('dineIn:nextCourse', { course: [t('products:courses.second'), t('products:courses.third'), t('products:courses.fourth'), t('products:courses.fifth')][currentCourse - 1] })}
                            </button>
                          )}
                        </>
                      ) : (
                        /* Standard basket without course grouping */
                        orderItems.map((item, index) => renderBasketLine(item, index))
                      )}
                    </div>
                  )}
                </div>
                
                {/* Total and submit */}
                <div className={v2 ? 'px-5 pt-4 pb-5 border-t border-line bg-surface-2' : 'p-4 border-t border-line-strong bg-surface-2'}>
                  {/* Ενέργειες για τις ΕΠΙΛΕΓΜΕΝΕΣ γραμμές(6/10/2026) — αντί για τις σταθερές εκπτώσεις
                      όλης της παραγγελίας. Κάθε κουμπί μόνο με το δικαίωμα του χρήστη. */}
                  {canSelectLines && selectedIndexList.length > 0 && (
                    <div
                      role="toolbar"
                      aria-label={t('dineIn:lineActions.toolbar', { defaultValue: 'Ενέργειες επιλεγμένων γραμμών' })}
                      data-testid="basket-line-actions"
                      className={`mb-3 p-1.5 rounded-xl border flex items-center gap-1.5 flex-nowrap overflow-x-auto ${v2 ? 'border-brand-line bg-brand-soft' : 'border-blue-500/50 bg-blue-500/10'}`}
                    >
                      {/* Συμπαγής μπάρα: σε μία σειρά δίπλα στα κουμπιά — αλλιώς τύλιγε σε δύο και το καλάθι
                          (768px ύψος) έμενε με ένα είδος ορατό, άρα δεν διάλεγες εύκολα δεύτερο. */}
                      {/* Πόσες επιλέχθηκαν: σήμα «✓ 3» (το κείμενο «3 επιλεγμένες» κοβόταν δίπλα στα κουμπιά). */}
                      <span
                        data-testid="basket-selected-count"
                        role="status"
                        aria-label={t('dineIn:lineActions.selectedCount', { count: selectedIndexList.length, defaultValue: '{{count}} επιλεγμένες' })}
                        title={t('dineIn:lineActions.selectedCount', { count: selectedIndexList.length, defaultValue: '{{count}} επιλεγμένες' })}
                        className={`shrink-0 inline-flex items-center gap-1 h-7 px-2 rounded-full text-xs font-bold tabular-nums ${v2 ? 'bg-brand text-brand-fg' : 'bg-blue-600 text-white'}`}
                      >
                        <Check className="w-3.5 h-3.5" />
                        {selectedIndexList.length}
                      </span>
                      {canDiscountLines && (
                        <button
                          type="button"
                          onClick={() => setBasketAction('discount')}
                          className={`shrink-0 inline-flex items-center gap-1 rounded-lg text-sm font-bold active:scale-95 transition-all ${v2 ? 'bg-surface-2 border border-line-strong text-t1 hover:bg-surface-3' : 'bg-purple-600 hover:bg-purple-700 text-white'} ${isMobile ? 'h-12 px-3' : 'h-10 px-2.5'}`}
                        >
                          <Percent className={`w-4 h-4 ${v2 ? 'text-purple-600 dark:text-purple-400' : ''}`} />
                          {t('dineIn:lineActions.discount', { defaultValue: 'Έκπτωση' })}
                        </button>
                      )}
                      {canTreatLines && (
                        <button
                          type="button"
                          onClick={() => setBasketAction('treat')}
                          className={`shrink-0 inline-flex items-center gap-1 rounded-lg text-sm font-bold active:scale-95 transition-all ${v2 ? 'bg-surface-2 border border-line-strong text-t1 hover:bg-surface-3' : 'bg-emerald-600 hover:bg-emerald-700 text-white'} ${isMobile ? 'h-12 px-3' : 'h-10 px-2.5'}`}
                        >
                          <Gift className={`w-4 h-4 ${v2 ? 'text-emerald-600 dark:text-emerald-400' : ''}`} />
                          {t('dineIn:lineActions.treat', { defaultValue: 'Κέρασμα' })}
                        </button>
                      )}
                      {selectedHaveAdjustment && (
                        <button
                          type="button"
                          onClick={removeSelectedAdjustments}
                          className={`shrink-0 inline-flex items-center gap-1 rounded-lg bg-surface-1 border border-line-strong text-t1 text-sm font-bold hover:border-red-500 active:scale-95 transition-all ${isMobile ? 'h-12 px-3' : 'h-10 px-2.5'}`}
                        >
                          <Undo2 className="w-4 h-4" />
                          {t('dineIn:lineActions.remove', { defaultValue: 'Αφαίρεση' })}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={clearLineSelection}
                        aria-label={t('dineIn:lineActions.clearSelection', { defaultValue: 'Καθαρισμός επιλογής' })}
                        className={`ml-auto shrink-0 rounded-lg hover:bg-surface-3 text-t2 flex items-center justify-center ${isMobile ? 'w-12 h-12' : 'w-9 h-9'}`}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                  {/* Μηνύματα κουζίνας ΜΑΖΙ με τον γύρο («ΟΛΑ ΜΑΖΙ» …) — στην κορυφή του δελτίου κουζίνας. */}
                  {kitchenPresets && orderItems.length > 0 && (
                    <div className="mb-3 flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => setKitchenMessageMode('attach')}
                        className={`inline-flex items-center gap-2 rounded-lg bg-surface-2 border border-line-strong text-t1 text-sm font-semibold hover:border-teal-500 active:scale-95 transition-all ${isMobile ? 'h-12 px-4' : 'h-10 px-3'}`}
                      >
                        <MessageSquare className={`w-4 h-4 ${v2 ? 'text-t3' : 'text-teal-600 dark:text-teal-400'}`} />
                        {t('dineIn:kitchenMessage', { defaultValue: 'Μήνυμα κουζίνας' })}
                      </button>
                      {orderKitchenMessages.map(message => (
                        <span key={message} className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg text-xs font-bold ${v2 ? 'bg-t1 text-surface-2' : 'bg-teal-600 text-white'}`}>
                          {message}
                          <button
                            type="button"
                            onClick={() => setOrderKitchenMessages(prev => prev.filter(m => m !== message))}
                            aria-label={t('common:remove', { defaultValue: 'Αφαίρεση' })}
                            className={`p-1 rounded-md ${v2 ? 'hover:bg-t2' : 'hover:bg-teal-700'}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Πόσο μειώνουν το σύνολο οι εκπτώσεις/κεράσματα των γραμμών */}
                  {basketReductionCents > 0 && (
                    <div className="flex items-center justify-between mb-1 text-sm" data-testid="basket-reduction">
                      <span className="text-t3">{t('dineIn:lineActions.reductionSummary', { defaultValue: 'Εκπτώσεις & κεράσματα' })}</span>
                      <span className={`font-semibold tabular-nums ${v2 ? 'text-t2' : 'text-purple-700 dark:text-purple-300'}`}>−{formatEuroGr(basketReductionCents)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <span className={v2 ? 'text-base font-semibold text-t1' : 'text-lg font-semibold text-t2'}>{t('common:total')}</span>
                    <span className={v2 ? 'text-[1.75rem] leading-none font-bold text-t1 tabular-nums' : 'text-2xl font-bold text-t1'} data-testid="basket-total">
                      {v2 ? formatEuro(parseFloat(orderTotal as any) || 0) : `${formatMoney((parseFloat(orderTotal as any) || 0))}`}
                    </span>
                  </div>
                  <button
                    onClick={handleCreateOrder}
                    disabled={orderItems.length === 0 || creatingOrder}
                    aria-busy={creatingOrder}
                    data-testid="send-to-kitchen"
                    className={v2
                      ? 'w-full h-14 bg-brand text-brand-fg rounded-xl font-semibold text-lg hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all active:scale-[0.98]'
                      : 'cashier-primary w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all active:scale-95'}
                  >
                    {creatingOrder ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                    {creatingOrder
                      ? t('dineIn:sendingToKitchen', { defaultValue: 'Αποστολή…' })
                      : t('dineIn:sendToKitchen')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* «Έκπτωση» / «Κέρασμα» για τις επιλεγμένες γραμμές του καλαθιού */}
      {showOrderModal && basketAction && selectedBasketLines.length > 0 && (
        <BasketActionDialog
          mode={basketAction}
          lines={selectedBasketLines}
          isMobile={isMobile}
          onClose={() => setBasketAction(null)}
          onApplyDiscount={applyBasketDiscount}
          onApplyTreat={applyBasketTreat}
        />
      )}

      {/* Μήνυμα κουζίνας: μόνο του (από το τραπέζι) ή μαζί με τον γύρο (από το καλάθι) */}
      {kitchenMessageMode && kitchenPresets && (
        <KitchenMessageModal
          tableNumber={selectedTable?.table_number}
          presets={kitchenPresets}
          mode={kitchenMessageMode}
          initial={kitchenMessageMode === 'attach' ? orderKitchenMessages : []}
          busy={sendingKitchenMessage}
          isMobile={isMobile}
          onClose={() => setKitchenMessageMode(null)}
          onConfirm={(messages) => {
            if (kitchenMessageMode === 'attach') {
              setOrderKitchenMessages(messages);
              setKitchenMessageMode(null);
            } else {
              void sendKitchenMessage(messages);
            }
          }}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && tableDetails?.session && (() => {
        // Helpers scoped to modal render
        const selectedItemsList = unassignedItems.filter(i =>
          selectedItems.has(`${i.orderId}-${i.itemId}-${i.instanceIndex}`)
        );
        // Τα ποσά των κουμπιών = ό,τι θα χρεωθεί (sliceAmount), όχι το άθροισμα τιμών τεμαχίων.
        const remainingToPay = paymentSlice(unassignedItems);
        const amountToPay = splitMode ? paymentSlice(selectedItemsList) : remainingToPay;
        const paidTotal = paymentLedger.paid;
        const allPaid = unassignedItems.length === 0;

        const payBtnClass = (color: string) =>
          `flex-1 py-4 ${color} text-white dark:text-t1 rounded-2xl font-bold text-base flex flex-col items-center justify-center gap-0.5 active:opacity-80 transition-opacity`;

        return (
          <div className="cashier-overlay fixed inset-0 bg-black/60 z-[60] flex items-end sm:items-center justify-center">
            <div className="cashier-dialog bg-surface-2 dark:bg-surface-0 w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl flex flex-col overflow-hidden shadow-2xl" style={{ maxHeight: '92dvh' }}>

              {/* ── Header ── */}
              <div className="cashier-dialog-header flex items-start justify-between px-5 pt-5 pb-4 border-b border-line flex-shrink-0">
                <div className="flex-1 min-w-0">
                  <h2 className="font-bold text-xl text-t1 leading-tight">
                    {t('dineIn:tableNumber', { number: selectedTable?.table_number })}
                  </h2>
                  <div className="flex items-baseline gap-3 mt-0.5">
                    <span className="text-2xl font-extrabold text-t1">
                      {formatMoney(getSessionTotal())}
                    </span>
                    {paidTotal > 0 && (
                      <span className="text-sm text-green-600 dark:text-green-400 font-semibold">
                        {t('dineIn:paidAmount', { amount: formatMoney(paidTotal) })}
                      </span>
                    )}
                  </div>
                  {/* POS Terminal selector */}
                  {posTerminals.length > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <CreditCard className="w-4 h-4 text-t3 flex-shrink-0" />
                      <select
                        value={selectedTerminal?.id || ''}
                        onChange={(e) => {
                          const terminal = posTerminals.find(term => term.id === parseInt(e.target.value));
                          setSelectedTerminal(terminal || null);
                          if (terminal && employee?.id && store?.id) {
                            localStorage.setItem(`pos_terminal_${employee.id}_${store.id}`, terminal.id.toString());
                          }
                        }}
                        className={`text-sm border border-line-strong rounded-lg bg-surface-2 text-t1 ${isMobile ? 'px-3 py-2.5' : 'px-2 py-1'}`}
                      >
                        {posTerminals.map(term => (
                          <option key={term.id} value={term.id}>
                            {term.terminal_name}{term.is_default ? t('dineIn:defaultSuffix') : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className={`ml-3 mt-0.5 bg-surface-2 rounded-xl hover:bg-surface-3 flex-shrink-0 ${isMobile ? 'p-3' : 'p-2'}`}
                >
                  <X className="w-5 h-5 text-t2" />
                </button>
              </div>

              {/* ── Scrollable body ── */}
              <div className="flex-1 overflow-y-auto overscroll-contain">

                {/* Κάρτα που τρέχει ή ίσως χρεώθηκε — πάνω από όλα, πριν από νέα χρέωση. */}
                <CardDoubtNotice doubts={cardDoubts} className="px-4 pt-4" />

                {/* Already paid — aggregated by method */}
                {paymentGroups.length > 0 && (
                  <div className="px-4 pt-4 pb-2">
                    <div className="flex items-center gap-2 mb-3">
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm font-bold text-green-600 dark:text-green-400 uppercase tracking-wide">{t('dineIn:paid')}</span>
                      <span className="ml-auto text-sm font-bold text-green-600 dark:text-green-400">{formatMoney(paidTotal)}</span>
                    </div>
                    <div className="space-y-2">
                      {paymentGroups.map(group => {
                        // «Επί πιστώσει»: δική του ετικέτα και χρώμα — ποτέ «ΚΑΡΤΑ» (κανένα χρήμα δεν εισπράχθηκε).
                        const methodLabel = group.method === 'cash'
                          ? t('common:payment.cash').toUpperCase()
                          : group.method === 'iris'
                          ? 'IRIS'
                          : group.method === 'credit'
                          ? 'ΕΠΙ ΠΙΣΤΩΣΕΙ'
                          : t('common:payment.card').toUpperCase();
                        const badgeClass = group.method === 'cash'
                          ? 'bg-green-600'
                          : group.method === 'iris'
                          ? 'bg-cyan-600'
                          : group.method === 'credit'
                          ? 'bg-amber-600'
                          : 'bg-blue-600';
                        const borderClass = group.method === 'cash'
                          ? 'border-green-300 dark:border-green-700/50'
                          : group.method === 'iris'
                          ? 'border-cyan-300 dark:border-cyan-700/50'
                          : group.method === 'credit'
                          ? 'border-amber-300 dark:border-amber-700/50'
                          : 'border-blue-300 dark:border-blue-700/50';
                        // Aggregate items in group by itemId+name
                        const aggregated: Record<string, { name: string; size: string; count: number; unitPrice: number }> = {};
                        group.items.forEach(it => {
                          const k = `${it.itemId}-${it.name}-${it.size}`;
                          if (!aggregated[k]) aggregated[k] = { name: it.name, size: instanceSize(it), count: 0, unitPrice: it.price };
                          aggregated[k].count++;
                        });
                        return (
                          <div key={group.id} className={`bg-surface-1 border rounded-xl px-3 py-2.5 ${borderClass}`}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white dark:text-t1 ${badgeClass}`}>
                                {methodLabel}
                              </span>
                              <span className="ml-auto text-sm font-semibold text-t2">
                                {formatMoney((group.amount ?? sumUnits(group.items)))}
                              </span>
                            </div>
                            <div className="space-y-0.5">
                              {Object.values(aggregated).map((ag, idx) => (
                                <div key={idx} className="flex justify-between text-sm text-t3">
                                  <span>{ag.count}× {ag.name}{ag.size ? ` (${ag.size})` : ''}</span>
                                  <span>{formatMoney((ag.count * ag.unitPrice))}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {!allPaid && <div className="mt-4 border-t border-line" />}
                  </div>
                )}

                {/* Οι πληρωμές του τραπεζιού, με «Ακύρωση λάθος πληρωμής» (μετρητά / «Επί πιστώσει»,
                    μόνιμη από 6/10/2026) — μόνο με το δικό της δικαίωμα dine_in_void_payment. Τι
                    ακυρώνεται το λέει ο server· μετά την ακύρωση το τραπέζι ξαναφορτώνει. Χωρίς
                    πληρωμές η λίστα δεν δείχνεται. */}
                {store?.id && (
                  <TablePaymentsList
                    storeId={store.id}
                    sessionId={tableDetails.session.id}
                    canVoid={hasPermission('dine_in_void_payment')}
                    refreshKey={unassignedItems.length}
                    onVoided={(result) => {
                      showToast('success', result.message);
                      setShowPaymentModal(false);
                      if (selectedTable) fetchTableDetails(selectedTable.id);
                      fetchTables();
                    }}
                  />
                )}

                {/* Items to pay */}
                {!allPaid && (
                  <div className="px-4 pt-3 pb-4">
                    {/* Section header — only shown in split mode */}
                    {splitMode && (
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-sm font-bold text-t2 uppercase tracking-wide">{t('dineIn:selectItems')}</span>
                        <span className="ml-auto text-sm text-t3">
                          {selectedItemsList.length > 0
                            ? t('dineIn:selectedAmountLabel', { amount: formatMoney(amountToPay) })
                            : t('dineIn:remainingAmountLabel', { amount: formatMoney(remainingToPay) })}
                        </span>
                      </div>
                    )}
                    <div className="space-y-2">
                      {unassignedItems.map(item => {
                        const key = `${item.orderId}-${item.itemId}-${item.instanceIndex}`;
                        const isSelected = selectedItems.has(key);
                        const sizeLabel = instanceSize(item);
                        return (
                          <div
                            key={key}
                            onClick={() => splitMode && toggleItemSelection(item.orderId, item.itemId, item.instanceIndex)}
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors ${
                              splitMode
                                ? isSelected
                                  ? 'bg-blue-100 dark:bg-blue-900/40 border-blue-500 cursor-pointer'
                                  : 'bg-surface-1 border-line cursor-pointer active:bg-surface-2'
                                : 'bg-surface-1 border-line'
                            }`}
                          >
                            {/* Checkbox in split mode */}
                            {splitMode && (
                              <div className={`w-5 h-5 rounded flex-shrink-0 border-2 flex items-center justify-center transition-colors ${
                                isSelected ? 'bg-blue-500 border-blue-500' : 'border-line-strong'
                              }`}>
                                {isSelected && <Check className="w-3 h-3 text-white dark:text-t1" />}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-t1">{item.name}</span>
                              {sizeLabel && <span className="text-sm text-t3 ml-1.5">({sizeLabel})</span>}
                            </div>
                            <span className="font-bold text-t1 flex-shrink-0">{formatMoney(item.price)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Footer ── */}
              <div className="flex-shrink-0 px-4 pb-6 pt-3 border-t border-line space-y-2 light:bg-surface-2 dark:bg-surface-0">
                {processingPayment ? (
                  <button disabled className="w-full py-4 bg-surface-2 text-t1 rounded-2xl font-bold flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('common:loading')}
                  </button>
                ) : allPaid ? (
                  /* All paid — completion */
                  <button
                    onClick={() => { setShowPaymentModal(false); setShowTableModal(false); fetchTables(); }}
                    className="w-full py-4 bg-green-600 text-white rounded-2xl font-bold text-lg flex items-center justify-center gap-2 active:opacity-80"
                  >
                    <Check className="w-5 h-5" />
                    {t('dineIn:complete')}
                  </button>
                ) : splitMode && selectedItemsList.length === 0 ? (
                  /* Split mode, nothing selected */
                  <>
                    <div className="w-full py-3 rounded-2xl bg-surface-2 dark:bg-surface-1 border border-line text-center text-t3 text-sm font-medium">
                      {t('dineIn:tapToSelectItems')}
                    </div>
                    <button
                      onClick={() => { setSplitMode(false); setSelectedItems(new Set()); }}
                      className="w-full py-3 bg-surface-2 text-t2 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 active:opacity-80"
                    >
                      <X className="w-4 h-4" />
                      {t('dineIn:cancelSplit')}
                    </button>
                  </>
                ) : (
                  /* Payment buttons: Μετρητά + Κάρτα γρήγορα· IRIS και «Επί πιστώσει» στα «Περισσότερα».
                     Όλα χρεώνουν τα ΙΔΙΑ τεμάχια και το ΙΔΙΟ ποσό (amountToPay). */
                  <>
                    <div className="flex gap-2">
                      {/* Cash */}
                      <button
                        onClick={() => {
                          const items = splitMode ? selectedItemsList : [...unassignedItems];
                          if (items.length > 0) processCashPayment(items);
                        }}
                        className={`cashier-payment-action ${payBtnClass('bg-green-600 hover:bg-green-700')}`}
                      >
                        <span className="text-xs opacity-80">{t('common:payment.cash').toUpperCase()}</span>
                        <span>{formatMoney(amountToPay)}</span>
                      </button>
                      {/* Card */}
                      <button
                        onClick={() => {
                          const items = splitMode ? selectedItemsList : [...unassignedItems];
                          if (items.length > 0) processCardPayment(items, false);
                        }}
                        className={`cashier-payment-action ${payBtnClass('bg-blue-600 hover:bg-blue-700')}`}
                      >
                        <span className="text-xs opacity-80">{t('common:payment.card').toUpperCase()}</span>
                        <span>{formatMoney(amountToPay)}</span>
                      </button>
                      {/* Περισσότερα: IRIS + «Επί πιστώσει» */}
                      <button
                        type="button"
                        aria-expanded={morePaymentMethods}
                        aria-controls="cashier-more-payment-methods"
                        onClick={() => setMorePaymentMethods(open => !open)}
                        className={`px-3 py-4 rounded-2xl border font-semibold text-xs flex flex-col items-center justify-center gap-1 active:opacity-80 transition-colors ${
                          morePaymentMethods
                            ? 'bg-surface-3 border-line-strong text-t1'
                            : 'bg-surface-2 dark:bg-surface-1 border-line-strong text-t2 hover:text-t1'
                        }`}
                      >
                        {morePaymentMethods ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        {t('dineIn:morePaymentMethods', { defaultValue: 'Περισσότερα' })}
                      </button>
                    </div>

                    {morePaymentMethods && (
                      <div id="cashier-more-payment-methods" className="flex gap-2">
                        {/* IRIS */}
                        <button
                          onClick={() => {
                            const items = splitMode ? selectedItemsList : [...unassignedItems];
                            if (items.length > 0) processCardPayment(items, true);
                          }}
                          className={`cashier-payment-action ${payBtnClass('bg-cyan-600 hover:bg-cyan-700')}`}
                        >
                          <span className="text-xs opacity-80">IRIS</span>
                          <span>{formatMoney(amountToPay)}</span>
                        </button>
                        {/* «Επί πιστώσει»: ίδια ροή με τα μετρητά (ποσό, τεμάχια, χωριστά), άλλος τύπος πληρωμής */}
                        <button
                          onClick={() => {
                            const items = splitMode ? selectedItemsList : [...unassignedItems];
                            if (items.length > 0) processCashPayment(items, 'credit');
                          }}
                          className={`cashier-payment-action ${payBtnClass('bg-amber-600 hover:bg-amber-700')}`}
                        >
                          <span className="text-xs opacity-80 flex items-center gap-1">
                            <HandCoins className="w-3.5 h-3.5" />
                            ΕΠΙ ΠΙΣΤΩΣΕΙ
                          </span>
                          <span>{formatMoney(amountToPay)}</span>
                        </button>
                      </div>
                    )}

                    {/* Split toggle */}
                    {!splitMode ? (
                      <button
                        onClick={() => setSplitMode(true)}
                        className={`w-full bg-surface-2 dark:bg-surface-1 border border-line-strong text-t3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 hover:border-line-strong hover:text-t2 active:opacity-80 ${isMobile ? 'py-3' : 'py-2.5'}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                        {t('dineIn:splitBill')}
                      </button>
                    ) : (
                      <button
                        onClick={() => { setSplitMode(false); setSelectedItems(new Set()); }}
                        className={`w-full bg-surface-2 dark:bg-surface-1 border border-line-strong text-t3 rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 hover:border-line-strong hover:text-t2 active:opacity-80 ${isMobile ? 'py-3' : 'py-2.5'}`}
                      >
                        <X className="w-4 h-4" />
                        {t('dineIn:cancelSplit')}
                      </button>
                    )}
                  </>
                )}
              </div>

            </div>
          </div>
        );
      })()}

      {/* Card Payment Overlay */}
      {cardPaymentStatus !== 'idle' && (
        <div className="cashier-overlay fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <div className="cashier-dialog bg-surface-1 rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
            {cardPaymentStatus === 'processing' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
                  <CreditCard className="w-10 h-10 text-blue-600 animate-pulse" />
                </div>
                <div className="mb-4">
                  <div className="w-12 h-12 mx-auto border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                </div>
                <h3 className="text-xl font-bold text-t1 mb-2">{t('dineIn:processingPayment')}</h3>
                <p className="text-t2">{cardPaymentMessage}</p>
                <p className="text-sm text-t4 mt-4">{t('delivery:pleaseWait')}</p>
                <p className="text-sm font-semibold text-amber-700 dark:text-amber-300 mt-2" data-testid="card-wait-no-refresh">
                  {t('dineIn:cardWaitNoRefresh', {
                    defaultValue: 'Μην ανανεώσεις τη σελίδα και μην κλείσεις το παράθυρο όσο περιμένει το τερματικό — χάνεται η απάντηση της χρέωσης.',
                  })}
                </p>
              </>
            )}
            
            {cardPaymentStatus === 'success' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 bg-green-100 dark:bg-green-900/40 rounded-full flex items-center justify-center">
                  <Check className="w-10 h-10 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-green-600 dark:text-green-400 mb-2">{t('dineIn:paymentSuccess')}</h3>
                <p className="text-t2 text-lg">{cardPaymentMessage}</p>
              </>
            )}
            
            {cardPaymentStatus === 'failed' && (
              <>
                <div className="w-20 h-20 mx-auto mb-6 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center">
                  <X className="w-10 h-10 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">{t('delivery:paymentFailed')}</h3>
                <p className="text-t2">{cardPaymentMessage}</p>
                <button
                  onClick={() => {
                    setCardPaymentStatus('idle');
                    setCardPaymentMessage('');
                    setProcessingPayment(false);
                  }}
                  className="mt-6 px-6 py-3 bg-surface-3 text-t1 rounded-xl font-semibold hover:bg-surface-4 transition-colors"
                >
                  {t('common:close')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Correction Modal */}
      {showCorrectionModal && tableDetails && (
        <div className="cashier-overlay fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="cashier-dialog bg-surface-1 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="cashier-dialog-header px-6 py-4 bg-surface-2 border-b border-line-strong flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-t1">{t('dineIn:editOrder')}</h2>
                <p className="text-sm text-t3">{t('dineIn:editOrderHint')}</p>
              </div>
              <button
                onClick={() => setShowCorrectionModal(false)}
                className={`hover:bg-surface-3 rounded-xl transition-colors ${isMobile ? 'p-3' : 'p-2'}`}
              >
                <X className="w-6 h-6 text-t3" />
              </button>
            </div>

            {/* Προαιρετικός λόγος (6/10/2026): ένας για όλο το παράθυρο, πάει με κάθε −1 / ✕. */}
            <div className="px-6 py-3 border-b border-line-strong bg-surface-1">
              <StaffReasonPicker
                value={correctionReason}
                onChange={setCorrectionReason}
                label={t('dineIn:staffReasons.correctionTitle', { defaultValue: 'Λόγος (προαιρετικός)' })}
                isMobile={isMobile}
              />
            </div>

            {/* Orders List */}
            <div className="flex-1 overflow-y-auto p-6">
              {tableDetails.orders.length === 0 ? (
                <div className="text-center py-12 text-t4">
                  <p>{t('dineIn:noOrdersToEdit')}</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {tableDetails.orders.map(order => (
                    <div key={order.id} className="bg-surface-2 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-t1">{t('dineIn:orderNumber', { number: order.order_number })}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            order.status === 'served' ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400' :
                            order.status === 'ready' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' :
                            order.status === 'preparing' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-400' :
                            'bg-surface-3 text-t2'
                          }`}>
                            {ORDER_STATUS_LABELS[order.status] || order.status}
                          </span>
                        </div>
                        <span className="text-sm text-t3">
                          {new Date(order.created_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      
                      <div className="space-y-3">
                        {order.items.map(item => (
                          <div key={item.id} className="bg-surface-3 rounded-lg p-3 border border-line-strong">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-medium text-t1">
                                  {item.product_name}
                                  {item.size_name && sizeNameVisible(item) && <span className="text-blue-600 dark:text-blue-400 ml-1">({item.size_name})</span>}
                                </div>
                                <div className="text-sm text-t3">
                                  {t('dineIn:priceEachLabel', { price: formatMoney(parseFloat(item.unit_price as any)) })}
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-3">
                                {/* Quantity display and reduce */}
                                <div className="flex items-center gap-2">
                                  <span className="text-t3">{t('dineIn:qtyLabel')}</span>
                                  <span className="font-bold text-lg text-t1">{item.quantity}</span>
                                  {item.quantity > 1 && (
                                    <button
                                      onClick={() => {
                                        // Use deleteOrderItem with quantity=1 to generate fiscal correction document
                                        setCorrectionLoading(true);
                                        fetch(`${API_BASE_URL}/api/dine-in/${store?.id}/order-items/${item.id}`, {
                                          method: 'DELETE',
                                          headers: {
                                            'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
                                            'Content-Type': 'application/json'
                                          },
                                          body: correctionBody(1)
                                        }).then(async (response) => {
                                          if (response.ok) {
                                            showDiscountFiscalWarning(await readJsonBody(response));
                                            if (selectedTable) await fetchTableDetails(selectedTable.id);
                                          } else {
                                            const error = await response.json();
                                            showToast('error', error.error || t('dineIn:correctionFailed'));
                                          }
                                        }).catch(() => {
                                          showToast('error', t('dineIn:correctionFailed'));
                                        }).finally(() => {
                                          setCorrectionLoading(false);
                                        });
                                      }}
                                      disabled={correctionLoading}
                                      className={`rounded-lg bg-amber-100 dark:bg-amber-900/40 hover:bg-amber-200 dark:hover:bg-amber-900/60 text-amber-700 dark:text-amber-400 flex items-center justify-center font-bold disabled:opacity-50 transition-colors ${isMobile ? 'w-12 h-12 text-lg' : 'w-8 h-8'}`}
                                      title={t('dineIn:decreaseQuantity')}
                                    >
                                      -1
                                    </button>
                                  )}
                                </div>
                                
                                {/* Total */}
                                <div className="w-20 text-right font-bold text-t1">
                                  {formatMoney(parseFloat(item.total_price as any))}
                                </div>
                                
                                {/* Delete button */}
                                <button
                                  onClick={() => deleteOrderItem(item.id, item.quantity)}
                                  disabled={correctionLoading}
                                  className={`text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50 ${isMobile ? 'p-3' : 'p-2'}`}
                                  title={t('dineIn:removeProduct')}
                                >
                                  <X className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                            
                            {item.is_treat && (
                              <div className="mt-2">
                                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 text-xs rounded-full">{t('dineIn:treatBadge')}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-line-strong bg-surface-2 flex items-center justify-between">
              <div>
                <span className="text-t3">{t('dineIn:sessionTotal')}</span>
                <span className="ml-2 text-2xl font-bold text-t1">
                  {formatMoney(parseFloat(tableDetails.session?.total_amount as any || 0))}
                </span>
              </div>
              <button
                onClick={() => setShowCorrectionModal(false)}
                className="px-6 py-3 bg-surface-3 text-t1 rounded-xl font-semibold hover:bg-surface-4 transition-colors"
              >
                {t('dineIn:complete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Modal */}
      {showMoveModal && selectedTable && (
        <div className="cashier-overlay fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="cashier-dialog bg-surface-1 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="cashier-dialog-header px-6 py-4 bg-surface-2 border-b border-line-strong flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-t1">{t('dineIn:moveFromTable', { number: selectedTable.table_number })}</h2>
                <p className="text-sm text-t3">
                  {moveMode === 'session'
                    ? t('dineIn:moveWholeTable')
                    : selectedItemsToMove.length > 0
                      ? t('dineIn:itemsSelected', { count: selectedItemsToMove.length })
                      : t('dineIn:selectItemsToMove')
                  }
                </p>
              </div>
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setSelectedItemsToMove([]);
                  setMoveMode('session');
                }}
                className={`hover:bg-surface-3 rounded-xl transition-colors ${isMobile ? 'p-3' : 'p-2'}`}
              >
                <X className="w-6 h-6 text-t3" />
              </button>
            </div>
            
            {/* Mode Toggle */}
            {tableDetails?.orders && tableDetails.orders.length > 0 && (
              <div className="px-6 py-3 bg-surface-2 border-b border-line-strong">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setMoveMode('session');
                      setSelectedItemsToMove([]);
                    }}
                    className={`flex-1 px-4 rounded-lg font-medium text-sm transition-colors ${isMobile ? 'py-3' : 'py-2'} ${
                      moveMode === 'session'
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-surface-3 text-t2 border border-line-strong hover:bg-surface-4'
                    }`}
                  >
                    {t('dineIn:moveWholeTableBtn')}
                  </button>
                  <button
                    onClick={() => setMoveMode('items')}
                    className={`flex-1 px-4 rounded-lg font-medium text-sm transition-colors ${isMobile ? 'py-3' : 'py-2'} ${
                      moveMode === 'items'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-surface-3 text-t2 border border-line-strong hover:bg-surface-4'
                    }`}
                  >
                    {t('dineIn:moveProductsBtn')}
                  </button>
                </div>
              </div>
            )}
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {moveMode === 'session' ? (
                /* Table Selection for entire session */
                <div className="p-6">
                  <h3 className="text-sm font-semibold text-t3 uppercase tracking-wide mb-3">
                    {t('dineIn:selectDestinationTable')}
                  </h3>

                  {getMoveTargetGroups().length === 0 ? (
                    <div className="text-center py-8 text-t4">
                      <p className="font-medium">{t('dineIn:noAvailableTables')}</p>
                      <p className="text-sm mt-1">{t('dineIn:allTablesOccupied')}</p>
                    </div>
                  ) : renderMoveTargets(table => moveSession(table.id), true)}
                </div>
              ) : (
                /* Multi-select Items */
                <div className="p-6">
                  {/* Selected items summary */}
                  {selectedItemsToMove.length > 0 && (
                    <div className="mb-4 p-3 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl border border-indigo-300 dark:border-indigo-700">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-indigo-700 dark:text-indigo-300">{t('dineIn:selectedCount', { count: selectedItemsToMove.length })}</span>
                        <button
                          onClick={() => setSelectedItemsToMove([])}
                          className="px-2 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                        >
                          {t('dineIn:clearSelection')}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedItemsToMove.map(item => (
                          <span key={item.id} className="px-2 py-1 bg-indigo-100 dark:bg-indigo-800/50 text-indigo-700 dark:text-indigo-300 text-sm rounded-lg">
                            {item.quantity}x {item.product_name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <h3 className="text-sm font-semibold text-t3 uppercase tracking-wide mb-3">
                    {selectedItemsToMove.length > 0 ? t('dineIn:addOrSelectTable') : t('dineIn:selectItemsToMove')}
                  </h3>
                  
                  <div className="space-y-4">
                    {tableDetails?.orders.map(order => (
                      <div key={order.id} className="bg-surface-2 rounded-xl p-4">
                        <div className="text-sm font-semibold text-t2 mb-2">
                          {t('dineIn:orderNumber', { number: order.order_number })}
                        </div>
                        <div className="space-y-2">
                          {order.items.map(item => {
                            const isSelected = selectedItemsToMove.some(i => i.id === item.id);
                            return (
                              <button
                                key={item.id}
                                onClick={() => toggleItemForMove(item)}
                                className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-colors text-left ${
                                  isSelected 
                                    ? 'bg-indigo-100 dark:bg-indigo-900/40 border-indigo-500' 
                                    : 'bg-surface-3 border-line-strong hover:border-indigo-500 hover:bg-indigo-100 dark:hover:bg-indigo-900/20'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                    isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-400'
                                  }`}>
                                    {isSelected && (
                                      <svg className="w-3 h-3 text-white dark:text-t1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>
                                  <div>
                                    <span className="text-t3">{item.quantity}x</span>
                                    <span className="font-medium text-t1 ml-1">{item.product_name}</span>
                                    {item.size_name && sizeNameVisible(item) && (
                                      <span className="text-blue-600 dark:text-blue-400 ml-1">({item.size_name})</span>
                                    )}
                                  </div>
                                </div>
                                <span className="font-semibold text-t1">
                                  {formatMoney(parseFloat(item.total_price as any))}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Target table selection - only show when items are selected */}
                  {selectedItemsToMove.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-line-strong">
                      <h3 className="text-sm font-semibold text-t3 uppercase tracking-wide mb-3">
                        {t('dineIn:selectDestinationTable')}
                      </h3>

                      {getMoveTargetGroups().length === 0 ? (
                        <div className="text-center py-4 text-t4">
                          <p className="font-medium">{t('dineIn:noAvailableTables')}</p>
                        </div>
                      ) : renderMoveTargets(table => moveItems(table.id), false)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-line-strong bg-surface-2">
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setSelectedItemsToMove([]);
                  setMoveMode('session');
                }}
                disabled={moveLoading}
                className="w-full py-3 bg-surface-3 text-t2 rounded-xl font-semibold hover:bg-surface-4 transition-colors disabled:opacity-50"
              >
                {t('common:cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Discount Modal */}
      {showDiscountModal && tableDetails?.session && (
        <div className="cashier-overlay fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="cashier-dialog bg-surface-1 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="cashier-dialog-header px-6 py-4 bg-surface-2 border-b border-line-strong flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-t1">{t('dineIn:applyDiscount')}</h2>
                <p className="text-sm text-t3">{t('dineIn:tableNumber', { number: selectedTable?.table_number })}</p>
              </div>
              <button
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountValue('');
                  setDiscountType('percentage');
                }}
                className={`hover:bg-surface-3 rounded-xl transition-colors ${isMobile ? 'p-3' : 'p-2'}`}
              >
                <X className="w-6 h-6 text-t3" />
              </button>
            </div>
            
            {/* Current discount info */}
            {tableDetails.session.discount_amount && (
              <div className="px-6 py-3 bg-purple-50 dark:bg-purple-900/30 border-b border-line-strong">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-purple-600 dark:text-purple-400 font-medium">{t('dineIn:currentDiscount')} </span>
                    <span className="text-purple-700 dark:text-purple-300 font-bold">
                      {tableDetails.session.discount_type === 'percentage' 
                        ? `${tableDetails.session.discount_value}%`
                        : `${formatMoney(Number(tableDetails.session.discount_value || 0))}`
                      }
                    </span>
                    <span className="text-purple-600 dark:text-purple-400 ml-2">
                      (-{formatMoney(Number(tableDetails.session.discount_amount || 0))})
                    </span>
                  </div>
                  <button
                    onClick={removeDiscount}
                    disabled={discountLoading}
                    className={`text-sm bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-800/70 disabled:opacity-50 ${isMobile ? 'px-4 py-2.5' : 'px-3 py-1'}`}
                  >
                    {t('dineIn:removeDiscount')}
                  </button>
                </div>
              </div>
            )}
            
            {/* Content */}
            <div className="p-6 space-y-4 flex-1 overflow-y-auto">
              {/* Discount Type Toggle */}
              <div>
                <label className="text-sm font-semibold text-t3 block mb-2">{t('dineIn:discountType')}</label>
                <div className="flex gap-2">
                  <button
                    aria-pressed={discountType === 'percentage'}
                    onClick={() => setDiscountType('percentage')}
                    className={`cashier-choice flex-1 py-3 rounded-xl font-semibold transition-colors ${
                      discountType === 'percentage'
                        ? 'bg-purple-600 text-white'
                        : 'bg-surface-3 text-t2 hover:bg-surface-4'
                    }`}
                  >
                    {t('dineIn:percentageOption')}
                  </button>
                  <button
                    aria-pressed={discountType === 'amount'}
                    onClick={() => setDiscountType('amount')}
                    className={`cashier-choice flex-1 py-3 rounded-xl font-semibold transition-colors ${
                      discountType === 'amount'
                        ? 'bg-purple-600 text-white'
                        : 'bg-surface-3 text-t2 hover:bg-surface-4'
                    }`}
                  >
                    {t('dineIn:amountOption')}
                  </button>
                </div>
              </div>
              
              {/* Discount Value — opens the touch-friendly numpad instead of
                  relying on the OS soft keyboard, which doesn't always show up
                  on cashier tablets. */}
              <div>
                <label className="text-sm font-semibold text-t3 block mb-2">
                  {discountType === 'percentage' ? t('dineIn:discountPercentLabel') : t('dineIn:discountAmountLabel')}
                </label>
                <button
                  type="button"
                  onClick={() => setSessionDiscountNumpadOpen(true)}
                  className="w-full px-4 py-3 text-2xl font-bold text-center border-2 border-line-strong bg-surface-2 text-t1 rounded-xl hover:border-purple-500 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  <span>{discountValue && parseFloat(discountValue) > 0 ? discountValue : '—'}</span>
                  <span className="text-t4">{discountType === 'percentage' ? '%' : '€'}</span>
                </button>
              </div>
              
              {/* Quick buttons */}
              {discountType === 'percentage' && (
                <div className="flex gap-2">
                  {[5, 10, 15, 20, 25, 50].map(pct => (
                    <button
                      key={pct}
                      onClick={() => setDiscountValue(pct.toString())}
                      className={`flex-1 bg-surface-2 text-t2 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/40 hover:text-purple-700 dark:hover:text-purple-300 font-medium transition-colors ${isMobile ? 'py-3' : 'py-2'}`}
                    >
                      {pct}%
                    </button>
                  ))}
                </div>
              )}
              
              {/* Preview */}
              {discountValue && parseFloat(discountValue) > 0 && (
                <div className="p-4 bg-surface-2 rounded-xl">
                  <div className="flex justify-between text-t3">
                    <span>{t('common:subtotal')}:</span>
                    <span>{formatMoney(Number(tableDetails.session.total_amount))}</span>
                  </div>
                  <div className="flex justify-between text-purple-600 font-medium">
                    <span>{t('common:discount')}:</span>
                    <span>
                      -{formatMoney(discountType === 'percentage'
                        ? (Number(tableDetails.session.total_amount) * parseFloat(discountValue)) / 100
                        : Math.min(parseFloat(discountValue), Number(tableDetails.session.total_amount))
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-xl font-bold text-t1 mt-2 pt-2 border-t border-line-strong">
                    <span>{t('dineIn:newTotal')}</span>
                    <span>
                      {formatMoney(discountType === 'percentage'
                        ? Number(tableDetails.session.total_amount) * (1 - parseFloat(discountValue) / 100)
                        : Math.max(0, Number(tableDetails.session.total_amount) - parseFloat(discountValue))
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Προαιρετικός λόγος (6/10/2026) */}
              <StaffReasonPicker value={sessionDiscountReason} onChange={setSessionDiscountReason} isMobile={isMobile} />
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-line-strong bg-surface-2 flex gap-3">
              <button
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountValue('');
                  setDiscountType('percentage');
                }}
                className="flex-1 py-3 bg-surface-3 text-t2 rounded-xl font-semibold hover:bg-surface-4 transition-colors"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={applyDiscount}
                disabled={discountLoading || !discountValue || parseFloat(discountValue) <= 0}
                className="flex-1 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {discountLoading ? t('dineIn:applying') : t('dineIn:applyDiscount')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Treat Modal */}
      {showTreatModal && tableDetails?.session && (
        <div className="cashier-overlay fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="cashier-dialog bg-surface-1 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="cashier-dialog-header px-6 py-4 bg-surface-2 border-b border-line-strong flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-t1">{t('dineIn:treat')}</h2>
                <p className="text-sm text-t3">{t('dineIn:treatHint')}</p>
              </div>
              <button
                onClick={() => setShowTreatModal(false)}
                className={`hover:bg-surface-3 rounded-xl transition-colors ${isMobile ? 'p-3' : 'p-2'}`}
              >
                <X className="w-6 h-6 text-t3" />
              </button>
            </div>

            {/* Προαιρετικός λόγος (6/10/2026): πάει με κάθε «Κέρασμα» αυτού του παραθύρου. */}
            <div className="px-6 py-3 border-b border-line-strong bg-surface-1">
              <StaffReasonPicker value={treatReason} onChange={setTreatReason} isMobile={isMobile} />
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                {tableDetails.orders.map(order => (
                  <div key={order.id} className="bg-surface-2 rounded-xl p-4">
                    <div className="text-sm font-semibold text-t2 mb-3">
                      Order #{order.order_number}
                    </div>
                    <div className="space-y-2">
                      {order.items.map(item => (
                        <div
                          key={item.id}
                          className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                            item.is_treat 
                              ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700' 
                              : 'bg-surface-3 border-line-strong'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            {item.is_treat && (
                              <Gift className="w-5 h-5 text-green-600" />
                            )}
                            <div>
                              <span className="text-t3">{item.quantity}x</span>
                              <span className="font-medium text-t1 ml-1">{item.product_name}</span>
                              {item.size_name && sizeNameVisible(item) && (
                                <span className="text-blue-600 dark:text-blue-400 ml-1">({item.size_name})</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {item.is_treat ? (
                              <>
                                <span className="text-green-600 dark:text-green-400 font-medium line-through">
                                  {formatMoney(Number(item.original_price || item.unit_price || 0))}
                                </span>
                                <span className="text-green-600 dark:text-green-400 font-bold">{t('dineIn:freeLabel')}</span>
                                <button
                                  onClick={() => removeTreat(item.id)}
                                  disabled={treatLoading}
                                  className={`text-sm bg-surface-4 text-t2 rounded-lg hover:bg-slate-400 disabled:opacity-50 ${isMobile ? 'px-4 py-2.5' : 'px-3 py-1'}`}
                                >
                                  {t('dineIn:undo')}
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="font-semibold text-t1">
                                  {formatMoney(Number(item.total_price || 0))}
                                </span>
                                <button
                                  onClick={() => markAsTreat(item.id)}
                                  disabled={treatLoading}
                                  className={`text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 ${isMobile ? 'px-4 py-2.5' : 'px-3 py-1'}`}
                                >
                                  {t('dineIn:treat')}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-line-strong bg-surface-2">
              <button
                onClick={() => setShowTreatModal(false)}
                className="w-full py-3 bg-surface-3 text-t1 rounded-xl font-semibold hover:bg-surface-4 transition-colors"
              >
                {t('dineIn:finishTreat')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Size Selection Modal */}
      {/* Size selection — shared component, immediate-add UX (parity with TakeAway) */}
      {showSizeModal && selectedProduct && (
        <ProductSizeModal
          channel="dine_in"
          product={selectedProduct}
          selectedSize={selectedSize}
          onSelectSize={(size) => {
            setShowSizeModal(false);
            // If there are required option groups still pending, route through the options modal
            const requiredOnly = productOptionGroups.filter(g => g.is_required);
            if (requiredOnly.length > 0) {
              setProductOptionGroups(requiredOnly);
              setOptionModalPendingSize(size);
              setShowOptionModal(true);
            } else {
              addToOrderWithSize(selectedProduct, size, {
                selected_options: pendingSelectedOptions.length ? pendingSelectedOptions : undefined
              });
            }
          }}
          onClose={() => {
            setShowSizeModal(false);
            setSelectedProduct(null);
            setSelectedSize(null);
            setProductOptionGroups([]);
            setPendingSelectedOptions([]);
          }}
          mode="immediate"
          translationNamespace="dineIn"
        />
      )}

      {/* Required option groups — quick-click flow (parity with TakeAway) */}
      {showOptionModal && selectedProduct && (
        <ProductOptionsModal
          channel="dine_in"
          product={selectedProduct}
          pendingSize={optionModalPendingSize}
          groups={productOptionGroups}
          selectedOptions={pendingSelectedOptions}
          onChange={setPendingSelectedOptions}
          onConfirm={() => {
            // Validate required groups have at least one selection
            const missing = productOptionGroups
              .filter(g => g.is_required)
              .filter(g => !pendingSelectedOptions.some(o => o.group_id === g.id));
            if (missing.length > 0) {
              showToast('error', t('dineIn:requiredOptionsMissing', { defaultValue: 'Παρακαλώ επιλέξτε όλες τις απαιτούμενες επιλογές' }));
              return;
            }
            if (selectedProduct) {
              if (optionModalPendingSize) {
                addToOrderWithSize(selectedProduct, optionModalPendingSize, { selected_options: pendingSelectedOptions });
              } else {
                addToOrderWithoutSize(selectedProduct, { selected_options: pendingSelectedOptions });
              }
            }
          }}
          onClose={() => {
            setShowOptionModal(false);
            setProductOptionGroups([]);
            setPendingSelectedOptions([]);
            setOptionModalPendingSize(null);
            setSelectedProduct(null);
          }}
          translationNamespace="dineIn"
        />
      )}

      {/* Weight-variable products — shared component */}
      {showWeightModal && selectedProduct && (
        <WeightModal
          product={selectedProduct}
          pricePerUnit={productDisplayPrice(selectedProduct, 'dine_in')}
          onConfirm={(p, weight) => addWeightProductToOrder(p, weight)}
          onClose={() => {
            setShowWeightModal(false);
            setSelectedProduct(null);
          }}
        />
      )}

      {/* Invoice Modal */}
      {showReceiptModal && tableDetails?.session && (
        <div className="cashier-overlay fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="cashier-dialog bg-surface-1 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="cashier-dialog-header flex items-center justify-between p-4 border-b border-line-strong sticky top-0 bg-surface-1">
              <h2 className="font-bold text-xl text-t1">{t('dineIn:issueInvoice')}</h2>
              <button
                onClick={() => {
                  setShowReceiptModal(false);
                  setCustomerVat('');
                  setCustomerName('');
                  setCustomerStreet('');
                  setCustomerNumber('');
                  setCustomerPostalCode('');
                  setCustomerCity('');
                  setCustomerBranch(0);
                }}
                className={`text-t3 hover:bg-surface-2 rounded-lg ${isMobile ? 'p-3' : 'p-2'}`}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Customer fields */}
              <div className="space-y-3 p-4 bg-surface-2 rounded-xl">
                  <div>
                    <label className="block text-sm font-medium text-t2 mb-1">{t('dineIn:customerVAT')}</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customerVat}
                        onChange={(e) => setCustomerVat(e.target.value.replace(/\D/g, ''))}
                        placeholder={t('dineIn:enterVAT')}
                        className="flex-1 p-3 border border-line-strong bg-surface-3 text-t1 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-t3"
                        maxLength={9}
                      />
                      <button
                        onClick={lookupVat}
                        disabled={lookingUpVat || customerVat.length < 9}
                        className="cashier-primary px-4 py-3 bg-blue-600 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {lookingUpVat ? <Loader2 className="w-5 h-5 animate-spin" /> : t('common:search')}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-t2 mb-1">{t('dineIn:companyName')}</label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder={t('dineIn:companyNamePlaceholder')}
                      className="w-full p-3 border border-line-strong bg-surface-3 text-t1 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-t3"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-t2 mb-1">{t('dineIn:street')}</label>
                      <input
                        type="text"
                        value={customerStreet}
                        onChange={(e) => setCustomerStreet(e.target.value)}
                        placeholder={t('dineIn:street')}
                        className="w-full p-3 border border-line-strong bg-surface-3 text-t1 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-t3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-t2 mb-1">{t('dineIn:streetNumber')}</label>
                      <input
                        type="text"
                        value={customerNumber}
                        onChange={(e) => setCustomerNumber(e.target.value)}
                        placeholder={t('dineIn:streetNumberShort')}
                        className="w-full p-3 border border-line-strong bg-surface-3 text-t1 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-t3"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-sm font-medium text-t2 mb-1">{t('delivery:postalCodeShort')}</label>
                      <input
                        type="text"
                        value={customerPostalCode}
                        onChange={(e) => setCustomerPostalCode(e.target.value)}
                        placeholder={t('delivery:postalCodeShort')}
                        className="w-full p-3 border border-line-strong bg-surface-3 text-t1 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-t3"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-t2 mb-1">{t('delivery:city')}</label>
                      <input
                        type="text"
                        value={customerCity}
                        onChange={(e) => setCustomerCity(e.target.value)}
                        placeholder={t('delivery:city')}
                        className="w-full p-3 border border-line-strong bg-surface-3 text-t1 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 placeholder-t3"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-t2 mb-1">{t('settings:page.branchLabel')}</label>
                    <input
                      type="number"
                      value={customerBranch}
                      onChange={(e) => setCustomerBranch(parseInt(e.target.value) || 0)}
                      placeholder="0"
                      min={0}
                      className="w-24 p-3 border border-line-strong bg-surface-3 text-t1 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <span className="ml-2 text-sm text-t3">{t('dineIn:branchHint')}</span>
                  </div>
                </div>
              
              {/* Summary */}
              <div className="p-4 bg-surface-2 rounded-xl">
                <div className="flex justify-between items-center text-lg font-bold text-t1">
                  <span>{t('common:total')}</span>
                  <span>{formatMoney(getSessionTotal())}</span>
                </div>
                <p className="text-sm text-t3 mt-1">
                  {t('dineIn:tableOrdersSummary', { number: selectedTable?.table_number, count: tableDetails?.orders?.length || 0 })}
                </p>
              </div>
              
              {/* Info */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium">ℹ️ {t('common:info')}</p>
                <p>{t('dineIn:invoiceOnCreditInfo')}</p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="p-4 border-t border-line-strong sticky bottom-0 bg-surface-1">
              <button
                onClick={generateReceipt}
                disabled={generatingReceipt || !customerVat}
                className="w-full p-4 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-indigo-600 text-white"
              >
                {generatingReceipt ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('dineIn:issuing')}</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-5 h-5" />
                    <span>{t('dineIn:issueInvoiceBtn')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Open Price Modal */}
      {showOpenPriceModal && selectedProduct && (
        <OpenPriceModal
          product={selectedProduct}
          onConfirm={addOpenPriceToOrder}
          onClose={() => {
            setShowOpenPriceModal(false);
            setSelectedProduct(null);
            setOpenPriceInput('');
          }}
        />
      )}

      {/* Customization Modal — shared component (parity with takeaway) */}
      {showCustomizeModal && selectedProduct && (
        <ProductCustomizeModal
          channel="dine_in"
          open={showCustomizeModal}
          product={selectedProduct}
          initialSize={selectedSize}
          initialRemovedIngredients={removedIngredients}
          initialAddedExtras={addedExtras as any}
          initialSelectedOptions={pendingSelectedOptions}
          // Επεξεργασία γραμμής (utils/basketLineEdit): βάρος, ποσό ζυγιζόμενου/ανοιχτής τιμής, σημείωση και
          // η έκπτωση της γραμμής ως αρχική τιμή (initialDiscount· κέρασμα = 0).
          // ΧΩΡΙΣ κουμπιά έκπτωσης στα τραπέζια (απόφαση ιδιοκτήτη 6/10/2026 — αντικαθιστά το «μόνο με
          // δικαίωμα» της P5-E18): ένας τρόπος έκπτωσης, κράτημα γραμμής → «Έκπτωση» (% ή €, με λόγο). Το
          // initialDiscount μένει: στην επεξεργασία η γραμμή ΚΡΑΤΑ την έκπτωσή της (το modal την επιστρέφει
          // αμετάβλητη — withLineAdjustment παρακάτω).
          {...dineInLineEditModalProps(
            basketLineEditView(editingBasketIndex !== null ? orderItems[editingBasketIndex] : null),
            false, // showDiscount: ποτέ στα τραπέζια, ούτε με το δικαίωμα
            selectedProduct.is_open_price
          )}
          isEditing={editingBasketIndex !== null}
          productIngredients={productIngredients as any}
          productOptionGroups={productOptionGroups}
          loadingMaterials={loadingMaterials}
          warehouseMode={store?.warehouse !== false}
          translationNamespace="dineIn"
          onSizeChange={(size) => { setSelectedSize(size); fetchSizeMaterials(size.id); }}
          onClose={() => {
            setShowCustomizeModal(false);
            setEditingBasketIndex(null);
            setSelectedProduct(null);
            setSelectedSize(null);
            setRemovedIngredients([]);
            setAddedExtras([]);
            setPendingSelectedOptions([]);
          }}
          onConfirm={(r: ProductCustomizeResult) => {
            // Επεξεργασία γραμμής ζυγιζόμενου/ανοιχτής τιμής: το ποσό της (ζυγιζόμενο με άλλο μέγεθος:
            // κιλό × βάρος, utils/basketLineEdit). Νέα γραμμή (editing null): η τιμή dine-in του μεγέθους.
            // + επιλογές + έξτρα, στρογγυλοποίηση στο λεπτό: dineInCustomizedLinePrices (δοκιμασμένο).
            // Εδώ μόνο η ΠΛΗΡΗΣ τιμή — γι' αυτό discount 0· έκπτωση/κέρασμα τα βάζει το withLineAdjustment.
            const editing = editingBasketIndex !== null ? orderItems[editingBasketIndex] : null;
            const fullUnit = dineInCustomizedLinePrices(
              selectedProduct!, r.size ?? null, basketLineEditView(editing), { ...r, discount: 0 }
            ).unit_price;

            // Build the line in a shape shared by add and edit — στην ΠΛΗΡΗ τιμή, χωρίς έκπτωση/κέρασμα
            // (τα βάζει το withLineAdjustment). quantity is preserved on edit so the cashier doesn't
            // lose their "x3" when they only wanted to tweak ingredients/options.
            const builtLine = (existingQuantity: number) => ({
              product_id: selectedProduct!.id,
              size_id: r.size?.id ?? null,
              product_name: selectedProduct!.name,
              size_name: r.size?.name ?? null,
              quantity: existingQuantity,
              unit_price: fullUnit,
              removedIngredients: r.removedIngredients,
              removedIngredientNames: r.removedIngredientNames,
              addedExtras: r.addedExtras,
              selected_options: r.selectedOptions.length ? r.selectedOptions : undefined,
              original_unit_price: undefined,
              full_unit_price: undefined,
              is_treat: undefined,
              discount_kind: undefined,
              discount_percent: undefined,
              adjust_reason: undefined,
              notes: r.specialInstructions || undefined,
            });

            // Έκπτωση του modal = ίδιος κανόνας και ίδια πεδία με την «Έκπτωση %» του καλαθιού
            // (utils/dineInBasketDiscount). Στην επεξεργασία κρατιέται ό,τι είχε η γραμμή:
            //  - έκπτωση € που δεν πειράχτηκε στο modal → ίδια έκπτωση ανά τεμάχιο στη νέα πλήρη τιμή·
            //  - κέρασμα (το modal δείχνει 0%) → μένει κέρασμα, με τη νέα πλήρη τιμή.
            const withLineAdjustment = (line: DineInBasketLine): DineInBasketLine => {
              if (r.discount > 0) {
                if (editing?.discount_kind === 'amount' && editing.full_unit_price != null
                  && r.discount === editedLineDiscountPercent(editing)) {
                  const perUnitCut = toCents(editing.full_unit_price) - toCents(editing.unit_price);
                  const fullCents = toCents(line.unit_price);
                  const unitCents = Math.max(0, fullCents - perUnitCut);
                  if (unitCents >= fullCents) return line;
                  return {
                    ...line,
                    full_unit_price: fullCents / 100,
                    unit_price: unitCents / 100,
                    discount_kind: 'amount',
                    discount_percent: effectivePercent(fullCents, unitCents),
                    adjust_reason: editing.adjust_reason ?? null
                  };
                }
                const reason = editing && !editing.is_treat && editing.full_unit_price != null ? editing.adjust_reason : null;
                return applyPercentDiscount([line], [0], r.discount, reason)[0];
              }
              if (editing?.is_treat) return applyTreat([line], [0], editing.adjust_reason)[0];
              return line;
            };

            if (editingBasketIndex !== null) {
              // Edit existing item — keep its course_number + quantity intact.
              setOrderItems(prev => prev.map((it, i) =>
                i === editingBasketIndex
                  ? withLineAdjustment({ ...it, ...builtLine(it.quantity) })
                  : it
              ));
            } else {
              // Add new item to the current course with quantity = 1.
              setOrderItems(prev => [...prev, withLineAdjustment({
                ...builtLine(1),
                course_number: currentCourse,
              })]);
            }
            // Cleanup
            setShowCustomizeModal(false);
            setEditingBasketIndex(null);
            setSelectedProduct(null);
            setSelectedSize(null);
            setRemovedIngredients([]);
            setAddedExtras([]);
            setPendingSelectedOptions([]);
            setProductOptionGroups([]);
          }}
        />
      )}


      {/* Session History Modal */}
      {showSessionHistory && (
        <div className="cashier-overlay fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="cashier-dialog bg-surface-1 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="cashier-dialog-header p-4 border-b border-line-strong bg-surface-2 dark:bg-surface-1 light:rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-t1">{t('dineIn:sessionHistory')}</h2>
                <p className="text-sm text-t3">{t('dineIn:sessionHistoryHint')}</p>
              </div>
              <button
                onClick={() => {
                  setShowSessionHistory(false);
                  setSelectedHistorySession(null);
                }}
                className={`hover:bg-surface-2 rounded-lg ${isMobile ? 'p-3' : 'p-2'}`}
              >
                <X className="w-6 h-6 text-t3" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                </div>
              ) : sessionHistory.length === 0 ? (
                <div className="text-center py-12 text-t3">
                  <Clock className="w-12 h-12 mx-auto mb-3 text-t4" />
                  <p>{t('dineIn:noClosedSessionsToday')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sessionHistory.map((session) => (
                    <div
                      key={session.id}
                      className="bg-surface-2 rounded-xl p-4 hover:bg-surface-3 transition-colors cursor-pointer"
                      onClick={async () => {
                        // Fetch session details when clicked
                        try {
                          const response = await fetch(`${API_BASE_URL}/api/dine-in/${store?.id}/sessions/${session.id}/details`, {
                            headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
                          });
                          if (response.ok) {
                            const data = await response.json();
                            setSelectedHistorySession({ ...session, orders: data.orders || [] });
                          }
                        } catch (error) {
                          console.error('Failed to fetch session details:', error);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-lg font-bold text-t1">
                              {t('dineIn:tableNumber', { number: session.table_number })}
                            </span>
                            {session.zone_name && (
                              <span className="text-sm text-t3">({session.zone_name})</span>
                            )}
                            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400 text-xs rounded-full">
                              {t('dineIn:closed')}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                            <div>
                              <span className="text-t3">{t('dineIn:openedAt')}</span>
                              <span className="ml-1 font-medium text-t2">
                                {new Date(session.opened_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div>
                              <span className="text-t3">{t('dineIn:closedAt')}</span>
                              <span className="ml-1 font-medium text-t2">
                                {new Date(session.closed_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            {guestsEnabled && (
                              <div>
                                <span className="text-t3">{t('dineIn:guestsLabel')}</span>
                                <span className="ml-1 font-medium text-t2">{session.guest_count}</span>
                              </div>
                            )}
                            <div>
                              <span className="text-t3">{t('dineIn:ordersLabel')}</span>
                              <span className="ml-1 font-medium text-t2">{session.order_count}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-2 text-sm text-t3">
                            <span>{t('dineIn:openedBy', { name: session.opened_by_name })}</span>
                            {session.closed_by_name && <span>{t('dineIn:closedBy', { name: session.closed_by_name })}</span>}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-2xl font-bold text-t1">
                            {formatMoney(parseFloat(String(session.total_amount || '0')))}
                          </div>
                          {parseFloat(String(session.discount_amount || '0')) > 0 && (
                            <div className="text-sm text-red-600 dark:text-red-400">
                              {t('dineIn:discountBadge', { amount: formatMoney(parseFloat(String(session.discount_amount))) })}
                            </div>
                          )}
                          <div className="text-sm text-green-600 dark:text-green-400 mt-1">
                            {t('dineIn:totalPaid', { amount: formatMoney(parseFloat(String(session.total_paid || '0'))) })}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Summary Footer */}
            {sessionHistory.length > 0 && (
              <div className="p-4 border-t border-line-strong bg-surface-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-t3">
                    {t('dineIn:totalSessionsCount', { count: sessionHistory.length })}
                  </div>
                  <div className="text-lg font-bold text-t1">
                    {t('dineIn:sessionTotalAmount', { amount: formatMoney(sessionHistory.reduce((sum, s) => sum + parseFloat(String(s.total_amount || '0')), 0)) })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Session Details Modal (when clicking on a history session) */}
      {selectedHistorySession && (
        <div className="cashier-overlay fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="cashier-dialog bg-surface-1 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="cashier-dialog-header p-4 border-b border-line-strong bg-surface-2 dark:bg-surface-1 light:rounded-t-2xl flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-t1">
                  {t('dineIn:tableSessionHeader', { table: selectedHistorySession.table_number, session: selectedHistorySession.session_number })}
                </h3>
                <p className="text-sm text-t3">
                  {new Date(selectedHistorySession.opened_at).toLocaleString('el-GR')} - {new Date(selectedHistorySession.closed_at).toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <button
                onClick={() => setSelectedHistorySession(null)}
                className={`hover:bg-surface-2 rounded-lg ${isMobile ? 'p-3' : 'p-2'}`}
              >
                <X className="w-5 h-5 text-t3" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              {selectedHistorySession.orders && selectedHistorySession.orders.length > 0 ? (
                // FIX-FE(c): ίδιος κανόνας ονόματος μεγέθους με τους ανοιχτούς λογαριασμούς
                // (showSizeName — απόφαση 15, τέλη F1-W4-4). Μόνο εμφάνιση.
                <ClosedSessionOrders
                  orders={selectedHistorySession.orders}
                  products={products}
                />
              ) : (
                <div className="text-center py-8 text-t4">
                  {t('dineIn:noOrdersFound')}
                </div>
              )}
              {/* Κλειστό τραπέζι: λάθος πληρωμή (μετρητά / «Επί πιστώσει») ακυρώνεται κι από εδώ —
                  η συνεδρία ξανανοίγει με το υπόλοιπο. */}
              {store?.id && (
                <TablePaymentsList
                  storeId={store.id}
                  sessionId={selectedHistorySession.id}
                  canVoid={hasPermission('dine_in_void_payment')}
                  onVoided={(result) => {
                    showToast('success', result.message);
                    setSelectedHistorySession(null);
                    fetchSessionHistory();
                    fetchTables();
                  }}
                />
              )}
            </div>
            
            <div className="p-4 border-t border-line-strong bg-surface-2">
              <div className="flex items-center justify-between text-lg font-bold text-t1">
                <span>{t('common:total')}</span>
                <span>{formatMoney(parseFloat(selectedHistorySession.total_amount || '0'))}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Employee Table Assignments Modal ===== */}
      {showAssignmentsModal && (
        <div className="cashier-overlay fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="cashier-dialog bg-surface-1 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="cashier-dialog-header flex items-center justify-between px-6 py-4 border-b light:border-line bg-surface-2 dark:bg-surface-1 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-amber-600/20 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-700 dark:text-amber-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-t1">{t('dineIn:assignTables')}</h2>
                  <p className="text-xs text-t3">{t('dineIn:assignTablesHint')}</p>
                </div>
              </div>
              <button
                onClick={() => { setShowAssignmentsModal(false); setSelectedWaiterId(null); setAssignedTableIds([]); }}
                className="p-2 hover:bg-surface-2 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-t3" />
              </button>
            </div>

            <div className="flex flex-1 overflow-hidden">
              {/* Left: Waiter list */}
              <div className="w-48 shrink-0 border-r border-line overflow-y-auto">
                <p className="px-4 pt-4 pb-2 text-xs font-semibold text-t4 uppercase tracking-wider">{t('dineIn:waiters')}</p>
                {waiters.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-t4">{t('dineIn:noWaitersFound')}</p>
                ) : (
                  waiters.map(w => (
                    <button
                      key={w.id}
                      onClick={() => { setSelectedWaiterId(w.id); fetchWaiterAssignments(w.id); }}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                        selectedWaiterId === w.id
                          ? 'bg-amber-600/20 text-amber-800 dark:text-amber-300 border-r-2 border-amber-500'
                          : 'text-t2 hover:bg-surface-2'
                      }`}
                    >
                      {w.name}
                    </button>
                  ))
                )}
              </div>

              {/* Right: Table grid */}
              <div className="flex-1 overflow-y-auto p-4">
                {!selectedWaiterId ? (
                  <div className="h-full flex items-center justify-center text-t3 dark:text-t4 text-sm">
                    {t('dineIn:selectWaiterFromLeft')}
                  </div>
                ) : loadingAssignments ? (
                  <div className="h-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-t3" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {zones.map(zone => {
                      const zoneTables = tables.filter(t => t.zone_id === zone.id);
                      if (zoneTables.length === 0) return null;
                      return (
                        <div key={zone.id}>
                          <p className="text-xs font-semibold text-t3 uppercase tracking-wider mb-2">{zone.name}</p>
                          <div className="grid grid-cols-4 gap-2">
                            {zoneTables.map(table => {
                              const isAssigned = assignedTableIds.includes(table.id);
                              return (
                                <button
                                  key={table.id}
                                  onClick={() => {
                                    setAssignedTableIds(prev =>
                                      isAssigned ? prev.filter(id => id !== table.id) : [...prev, table.id]
                                    );
                                  }}
                                  className={`py-3 px-2 rounded-xl text-sm font-semibold border-2 transition-all ${
                                    isAssigned
                                      ? 'bg-amber-600/20 border-amber-500 text-amber-800 dark:text-amber-300'
                                      : 'bg-surface-2 border-line-strong text-t3 hover:border-line-strong'
                                  }`}
                                >
                                  {table.table_number}
                                  {isAssigned && <Check className="w-3 h-3 mx-auto mt-1" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-line flex items-center justify-between">
              <p className="text-sm text-t3">
                {assignedTableIds.length > 0
                  ? t('dineIn:tablesSelected', { count: assignedTableIds.length })
                  : t('dineIn:noTablesSelected')}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowAssignmentsModal(false); setSelectedWaiterId(null); setAssignedTableIds([]); }}
                  className={`px-4 bg-surface-2 hover:bg-surface-3 text-t2 rounded-xl text-sm transition-colors ${isMobile ? 'py-3' : 'py-2'}`}
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={saveAssignments}
                  disabled={!selectedWaiterId || savingAssignments}
                  className={`px-5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors ${isMobile ? 'py-3' : 'py-2'}`}
                >
                  {savingAssignments ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {t('common:save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <CancelOrderModal
        isOpen={showCancelModal}
        orderCode={selectedTable ? `Τραπέζι ${selectedTable.table_number}` : undefined}
        onClose={() => setShowCancelModal(false)}
        onConfirm={submitCancelSession}
        title={cancelModalFiscal?.title}
        notice={cancelModalFiscal?.notice}
        blockedMessage={cancelModalFiscal?.blocked ?? null}
        confirmLabel={cancelModalFiscal ? t('dineIn:cancelAfterReceipt.confirm') : undefined}
        extraPresetKeys={cancelModalFiscal ? ['wantedInvoice', 'chargedElsewhere'] : []}
      />

      {/* Numpad: session-level discount (in the session discount modal) */}
      <NumpadModal
        open={sessionDiscountNumpadOpen}
        initialValue={parseFloat(discountValue) || 0}
        suffix={discountType === 'percentage' ? '%' : '€'}
        allowDecimal={discountType === 'amount'}
        title={t('common:discount', { defaultValue: 'Έκπτωση' })}
        min={0}
        max={discountType === 'percentage' ? 100 : Number.POSITIVE_INFINITY}
        onClose={() => setSessionDiscountNumpadOpen(false)}
        onConfirm={(v) => { setDiscountValue(String(v)); setSessionDiscountNumpadOpen(false); }}
      />

    </div>
  );
};

export default DineInOperations;
